import React, { useState, useMemo, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import {
  Box,
  TextField,
  Button,
  Grid,
  Paper,
  Typography,
  IconButton,
  Container,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  FormControlLabel,
  Divider,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  Checkbox,
  Chip,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import AddIcon from '@mui/icons-material/Add';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DeleteIcon from '@mui/icons-material/Delete';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CropSquareIcon from '@mui/icons-material/CropSquare';

import {
  generateConnectionWithCaps,
  getShaibaKeyForFitting,
} from './connectionLogic';
import {
  generatePairedCircuitsFull,
  generateCircuits,
} from './circuitGenerationLogic';
import {
  generateDistributionCollectorDimensions,
} from './dimensionLinesLogic';
import {
  getCapUpdatesForConnectionFitting,
  shouldShowConnectionLengthField,
} from './endConnectionsLogic';
import {
  generateCap,
  generateReducer,
  getCapLength,
} from './capsLogic';
import {
  getAvailableDiameters,
  CIRCUIT_FITTING_CATEGORIES,
  CENTER_FITTING_CATEGORIES,
  getConcentricReducers,
  extractDnFromFlangeKey,
  getPipeDiameterForDn,
  isEndFittingCompatibleWithMainPipe,
  isDistributionEndFittingCompatibleWithSquareProfile as isEndFittingCompatibleWithSquareProfile,
  isDistributionCircuitFittingCompatibleWithSquareProfile,
  findEllipticalCap,
  visibleSpecialStubLength,
} from './collectorUtils';
import fittingsData from '../fittingsData';
import CenterFittingsUI from './CenterFittingsUI';
import AdditionalOutletsUI from './AdditionalOutletsUI';
import {
  getMinThickness,
  getActualCoating,
} from './pipeThicknessUtils';
import {
  generateCenterFittings,
  generateCenterFittingDimensions,
  generateCenterFittingBalloons,
  updateCenterFittingsForDiameter,
} from './centerFittingsLogic';

// Получаем доступные диаметры, категории фитингов и переходы
const availableDiameters = getAvailableDiameters();
const circuitFittingCategories = CIRCUIT_FITTING_CATEGORIES;
const centerFittingCategories = CENTER_FITTING_CATEGORIES;
const isCircuitFittingCompatibleWithSquareProfile = isDistributionCircuitFittingCompatibleWithSquareProfile;
const concentricReducers = getConcentricReducers();

/**
 * Доступные квадратные профили для распределительного коллектора
 */
const availableSquareProfiles = [
  { value: '40x40', label: '40×40' },
  { value: '50x50', label: '50×50' },
  { value: '60x60', label: '60×60' },
  { value: '80x80', label: '80×80' },
  { value: '100x100', label: '100×100' },
  { value: '150x150', label: '150×150' },
];

/**
 * Доступные общие размеры для компактного режима
 * Общий размер определяет размеры и листа подающего, и профиля обратного коллектора
 */
const availableCompactSizes = [
  { value: '60x60', label: '60×60' },
  { value: '80x80', label: '80×80' },
];

/**
 * Маппинг общего размера на лист подающего и профиль обратного коллектора
 * 
 * Общий размер 60×60 → лист 60×30 (подающий) + профиль 60×30 (обратный)
 * Общий размер 80×80 → лист 80×40 (подающий) + профиль 80×40 (обратный)
 */
const compactSizeToProfilesMap = {
  '60x60': {
    sheet: '60x30',    // лист для подающего коллектора
    profile: '60x30',  // профиль для обратного коллектора
  },
  '80x80': {
    sheet: '80x40',    // лист для подающего коллектора
    profile: '80x40',  // профиль для обратного коллектора
  },
};

/** Отступы первого и последнего контура (подача и обратка) в компактном режиме по «общему размеру». */
const COMPACT_MODE_FIRST_LAST_OFFSET_BY_SIZE = {
  '80x80': 53,
  '60x60': 30,
};

function getCompactModeFirstLastOffset(compactSize) {
  return COMPACT_MODE_FIRST_LAST_OFFSET_BY_SIZE[compactSize] ?? 53;
}

/**
 * Получаем фитинги по категории с проверкой совместимости
 */
const getFittingsByCategory = (category, mainPipeDiameter = null, compatibilityCheckFn = null) => {
  return Object.entries(fittingsData)
    .filter(([key, data]) => data.category === category)
    .map(([key, data]) => ({
      id: key,
      label: data.displayName,
      diameter: data.diameter,
      length: data.length,
      isCompatible: mainPipeDiameter && compatibilityCheckFn ? compatibilityCheckFn(key, mainPipeDiameter) : true,
    }))
    .sort((a, b) => a.diameter - b.diameter); // Сортируем по диаметру (от меньшего к большему)
};

/**
 * Проверка совместимости фитинга с диаметром основной трубы (гребенки)
 * Используется для контуров
 */
/**
 * Проверка совместимости фитинга с диаметром гребенки для КОНТУРОВ распределительного коллектора
 * @param {string} fittingId - ID фитинга
 * @param {number} mainPipeDiameter - Диаметр гребенки
 * @returns {boolean} - true если фитинг совместим
 */
const isCircuitFittingCompatible = (fittingId, mainPipeDiameter) => {
  const fittingData = fittingsData[fittingId];
  if (!fittingData) return true;

  const category = fittingData.category;

  // ========== Ограничения для гребенки 42.4мм ==========
  if (mainPipeDiameter === 42.4) {
    // Ниппели: запрещены больше 1/2
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2'];
      return allowedNippels.includes(fittingId);
    }

    // Муфты: запрещены больше 1/2
    if (category === 'Муфты') {
      const allowedMuftas = ['Mufta_1.4', 'Mufta_3.8', 'Mufta_1.2'];
      return allowedMuftas.includes(fittingId);
    }

    // Фланцы: запрещены все
    if (category === 'Фланцы') {
      return false;
    }

    // Накидные гайки: запрещены больше 1"
    if (category === 'Накидные гайки') {
      if (fittingId === 'Nak_Gaika_1_1.4' || fittingId === 'Nak_Gaika_1_1.2' || fittingId === 'Nak_Gaika_2') {
        return false;
      }
      return true;
    }

    // Шайбы с наружной резьбой: запрещены все"
    if (category === 'Шайбы с наружной резьбой') {
      return false;
    }
  }

  // ========== Ограничения для гребенки 48.3мм ==========
  if (mainPipeDiameter === 48.3) {
    // Ниппели: запрещены больше 3/4
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4'];
      return allowedNippels.includes(fittingId);
    }

    // Муфты: запрещены больше 1/2
    if (category === 'Муфты') {
      const allowedMuftas = ['Mufta_1.4', 'Mufta_3.8', 'Mufta_1.2'];
      return allowedMuftas.includes(fittingId);
    }

    // Фланцы: запрещены все
    if (category === 'Фланцы') {
      return false;
    }

    // Накидные гайки: запрещены больше 1"
    if (category === 'Накидные гайки') {
      if (fittingId === 'Nak_Gaika_1_1.4' || fittingId === 'Nak_Gaika_1_1.2' || fittingId === 'Nak_Gaika_2') {
        return false;
      }
      return true;
    }

    // Шайбы с наружной резьбой: запрещены больше 1"
    if (category === 'Шайбы с наружной резьбой') {
      if (fittingId === 'Shaib_Nar_Rezba_1_1.4' || fittingId === 'Shaib_Nar_Rezba_1_1.2' || fittingId === 'Shaib_Nar_Rezba_2') {
        return false;
      }
      return true;
    }
  }

  // ========== Ограничения для гребенки 60.3мм ==========
  if (mainPipeDiameter === 60.3) {
    // Ниппели: запрещены больше 1
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4', 'Nippel_1'];
      return allowedNippels.includes(fittingId);
    }

    // Муфты: запрещены больше 3/4
    if (category === 'Муфты') {
      const allowedMuftas = ['Mufta_1.4', 'Mufta_3.8', 'Mufta_1.2', 'Mufta_3.4'];
      return allowedMuftas.includes(fittingId);
    }

    // Фланцы: только Ду25 (труба 33,7 мм — как ниппель 1")
    if (category === 'Фланцы') {
      return fittingId.startsWith('Flanec_25_');
    }

    // Накидные гайки: запрещены больше 1 1/2"
    if (category === 'Накидные гайки') {
      if (fittingId === 'Nak_Gaika_2') {
        return false;
      }
      return true;
    }

    // Шайбы с наружной резьбой: запрещены больше 1 1/2"
    if (category === 'Шайбы с наружной резьбой') {
      if (fittingId === 'Shaib_Nar_Rezba_2') {
        return false;
      }
      return true;
    }
  }

  // ========== Ограничения для гребенки 76.1мм ==========
  if (mainPipeDiameter === 76.1) {
    // Ниппели: запрещены больше 1 1/4
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4', 'Nippel_1', 'Nippel_1_1.4'];
      return allowedNippels.includes(fittingId);
    }

    // Муфты: запрещены больше 1
    if (category === 'Муфты') {
      const allowedMuftas = ['Mufta_1.4', 'Mufta_3.8', 'Mufta_1.2', 'Mufta_3.4', 'Mufta_1'];
      return allowedMuftas.includes(fittingId);
    }

    // Фланцы: запрещены больше Ду32
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 88.9мм ==========
  if (mainPipeDiameter === 88.9) {
    // Ниппели: запрещены больше 1 1/2
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4', 'Nippel_1', 'Nippel_1_1.4', 'Nippel_1_1.2'];
      return allowedNippels.includes(fittingId);
    }

    // Муфты: запрещены больше 1 1/4
    if (category === 'Муфты') {
      const allowedMuftas = ['Mufta_1.4', 'Mufta_3.8', 'Mufta_1.2', 'Mufta_3.4', 'Mufta_1', 'Mufta_1_1.4'];
      return allowedMuftas.includes(fittingId);
    }

    // Фланцы: запрещены больше Ду40
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 101.6мм ==========
  if (mainPipeDiameter === 101.6) {
    // Ниппели: запрещены больше 1 1/2
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4', 'Nippel_1', 'Nippel_1_1.4', 'Nippel_1_1.2'];
      return allowedNippels.includes(fittingId);
    }

    // Фланцы: запрещены больше Ду40
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 114.3мм ==========
  if (mainPipeDiameter === 114.3) {
    // Ниппели: запрещены больше 2
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4', 'Nippel_1', 'Nippel_1_1.4', 'Nippel_1_1.2', 'Nippel_2'];
      return allowedNippels.includes(fittingId);
    }

    // Фланцы: запрещены больше Ду50
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 129мм ==========
  if (mainPipeDiameter === 129) {
    // Ниппели: запрещены больше 2
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4', 'Nippel_1', 'Nippel_1_1.4', 'Nippel_1_1.2', 'Nippel_2'];
      return allowedNippels.includes(fittingId);
    }

    // Фланцы: запрещены больше Ду50
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 139.7мм ==========
  if (mainPipeDiameter === 139.7) {
    // Ниппели: запрещены больше 2 1/2
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4', 'Nippel_1', 'Nippel_1_1.4', 'Nippel_1_1.2', 'Nippel_2', 'Nippel_2_1/2'];
      return allowedNippels.includes(fittingId);
    }

    // Фланцы: запрещены больше Ду65
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 159мм ==========
  if (mainPipeDiameter === 159) {
    // Фланцы: запрещены больше Ду80
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 168.3мм ==========
  if (mainPipeDiameter === 168.3) {
    // Фланцы: запрещены больше Ду80
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 219.1мм ==========
  if (mainPipeDiameter === 219.1) {
    // Фланцы: запрещены больше Ду100
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80') || fittingId.startsWith('Flanec_100')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 273мм ==========
  if (mainPipeDiameter === 273) {
    // Фланцы: запрещены больше Ду125
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80') || fittingId.startsWith('Flanec_100') || fittingId.startsWith('Flanec_125')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 323.9мм ==========
  if (mainPipeDiameter === 323.9) {
    // Фланцы: запрещены больше Ду150
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80') || fittingId.startsWith('Flanec_100') || fittingId.startsWith('Flanec_125') || fittingId.startsWith('Flanec_150')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 355.6мм ==========
  if (mainPipeDiameter === 355.6) {
    // Фланцы: запрещены больше Ду150
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80') || fittingId.startsWith('Flanec_100') || fittingId.startsWith('Flanec_125') || fittingId.startsWith('Flanec_150')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 406.4мм ==========
  if (mainPipeDiameter === 406.4) {
    // Фланцы: запрещены больше Ду200
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80') || fittingId.startsWith('Flanec_100') || fittingId.startsWith('Flanec_125') || fittingId.startsWith('Flanec_150') || fittingId.startsWith('Flanec_200')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 508мм ==========
  if (mainPipeDiameter === 508) {
    // Фланцы: запрещены больше Ду250
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80') || fittingId.startsWith('Flanec_100') || fittingId.startsWith('Flanec_125') || fittingId.startsWith('Flanec_150') || fittingId.startsWith('Flanec_200') || fittingId.startsWith('Flanec_250')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 609.6мм ==========
  if (mainPipeDiameter === 609.6) {
    // Фланцы: запрещены больше Ду300
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80') || fittingId.startsWith('Flanec_100') || fittingId.startsWith('Flanec_125') || fittingId.startsWith('Flanec_150') || fittingId.startsWith('Flanec_200') || fittingId.startsWith('Flanec_250') || fittingId.startsWith('Flanec_300')) {
        return true;
      }
      return false;
    }
  }

  return true;
};

/**
 * Проверка совместимости фитинга с диаметром гребенки для ЦЕНТРАЛЬНЫХ ФИТИНГОВ (термометры, лицевые подключения) 
 * @param {string} fittingId - ID фитинга
 * @param {number} mainPipeDiameter - Диаметр гребенки
 * @returns {boolean} - true если фитинг совместим
 */
const isCenterFittingCompatible = (fittingId, mainPipeDiameter) => {
  const fittingData = fittingsData[fittingId];
  if (!fittingData) return true;

  const category = fittingData.category;

  // ========== Ограничения для гребенки 42.4мм (центральные фитинги) ==========
  if (mainPipeDiameter === 42.4) {
    // Ниппели: запрещены больше 3/4
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4'];
      return allowedNippels.includes(fittingId);
    }

    // Муфты: запрещены больше 1/2
    if (category === 'Муфты') {
      const allowedMuftas = ['Mufta_1.4', 'Mufta_3.8', 'Mufta_1.2'];
      return allowedMuftas.includes(fittingId);
    }

    // Фланцы: запрещены все
    if (category === 'Фланцы') {
      return false;
    }

    // Накидные гайки: запрещены больше 1"
    if (category === 'Накидные гайки') {
      if (fittingId === 'Nak_Gaika_1_1.4' || fittingId === 'Nak_Gaika_1_1.2' || fittingId === 'Nak_Gaika_2') {
        return false;
      }
      return true;
    }

    // Шайбы с наружной резьбой: запрещены больше 1"
    if (category === 'Шайбы с наружной резьбой') {
      if (fittingId === 'Shaib_Nar_Rezba_1_1.4' || fittingId === 'Shaib_Nar_Rezba_1_1.2' || fittingId === 'Shaib_Nar_Rezba_2') {
        return false;
      }
      return true;
    }
  }

  // ========== Ограничения для гребенки 48.3мм (центральные фитинги) ==========
  if (mainPipeDiameter === 48.3) {
    // Ниппели: запрещены больше 3/4
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4'];
      return allowedNippels.includes(fittingId);
    }

    // Муфты: запрещены больше 3/4
    if (category === 'Муфты') {
      const allowedMuftas = ['Mufta_1.4', 'Mufta_3.8', 'Mufta_1.2', 'Mufta_3.4'];
      return allowedMuftas.includes(fittingId);
    }

    // Фланцы: запрещены все
    if (category === 'Фланцы') {
      return false;
    }

    // Накидные гайки: запрещены больше 1"
    if (category === 'Накидные гайки') {
      if (fittingId === 'Nak_Gaika_1_1.4' || fittingId === 'Nak_Gaika_1_1.2' || fittingId === 'Nak_Gaika_2') {
        return false;
      }
      return true;
    }

    // Шайбы с наружной резьбой: запрещены больше 1"
    if (category === 'Шайбы с наружной резьбой') {
      if (fittingId === 'Shaib_Nar_Rezba_1_1.4' || fittingId === 'Shaib_Nar_Rezba_1_1.2' || fittingId === 'Shaib_Nar_Rezba_2') {
        return false;
      }
      return true;
    }
  }

  // ========== Ограничения для гребенки 60.3мм (центральные фитинги) ==========
  if (mainPipeDiameter === 60.3) {
    // Ниппели: запрещены больше 1
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4', 'Nippel_1'];
      return allowedNippels.includes(fittingId);
    }

    // Муфты: запрещены больше 1
    if (category === 'Муфты') {
      const allowedMuftas = ['Mufta_1.4', 'Mufta_3.8', 'Mufta_1.2', 'Mufta_3.4', 'Mufta_1'];
      return allowedMuftas.includes(fittingId);
    }

    // Фланцы: только Ду25
    if (category === 'Фланцы') {
      return fittingId.startsWith('Flanec_25_');
    }

    // Накидные гайки: запрещены больше 1 1/2"
    if (category === 'Накидные гайки') {
      if (fittingId === 'Nak_Gaika_2') {
        return false;
      }
      return true;
    }

    // Шайбы с наружной резьбой: запрещены больше 1 1/2"
    if (category === 'Шайбы с наружной резьбой') {
      if (fittingId === 'Shaib_Nar_Rezba_2') {
        return false;
      }
      return true;
    }
  }

  // ========== Ограничения для гребенки 76.1мм (центральные фитинги) ==========
  if (mainPipeDiameter === 76.1) {
    // Ниппели: запрещены больше 1 1/2
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4', 'Nippel_1', 'Nippel_1_1.4', 'Nippel_1_1.2'];
      return allowedNippels.includes(fittingId);
    }

    // Муфты: запрещены больше 1 1/4
    if (category === 'Муфты') {
      const allowedMuftas = ['Mufta_1.4', 'Mufta_3.8', 'Mufta_1.2', 'Mufta_3.4', 'Mufta_1', 'Mufta_1_1.4'];
      return allowedMuftas.includes(fittingId);
    }

    // Фланцы: запрещены больше Ду40
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 88.9мм (центральные фитинги) ==========
  if (mainPipeDiameter === 88.9) {
    // Ниппели: запрещены больше 2
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4', 'Nippel_1', 'Nippel_1_1.4', 'Nippel_1_1.2', 'Nippel_2'];
      return allowedNippels.includes(fittingId);
    }

    // Муфты: запрещены больше 1 1/2
    if (category === 'Муфты') {
      const allowedMuftas = ['Mufta_1.4', 'Mufta_3.8', 'Mufta_1.2', 'Mufta_3.4', 'Mufta_1', 'Mufta_1_1.4', 'Mufta_1_1.2'];
      return allowedMuftas.includes(fittingId);
    }

    // Фланцы: запрещены больше Ду50
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 101.6мм (центральные фитинги) ==========
  if (mainPipeDiameter === 101.6) {
    // Ниппели: запрещены больше 2
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4', 'Nippel_1', 'Nippel_1_1.4', 'Nippel_1_1.2', 'Nippel_2'];
      return allowedNippels.includes(fittingId);
    }

    // Фланцы: запрещены больше Ду50
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 114.3мм (центральные фитинги) ==========
  if (mainPipeDiameter === 114.3) {
    // Ниппели: запрещены больше 2 1/2
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4', 'Nippel_1', 'Nippel_1_1.4', 'Nippel_1_1.2', 'Nippel_2', 'Nippel_2_1/2'];
      return allowedNippels.includes(fittingId);
    }

    // Фланцы: запрещены больше Ду65
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 129мм (центральные фитинги) ==========
  if (mainPipeDiameter === 129) {
    // Ниппели: запрещены больше 3
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4', 'Nippel_1', 'Nippel_1_1.4', 'Nippel_1_1.2', 'Nippel_2', 'Nippel_2_1/2', 'Nippel_3'];
      return allowedNippels.includes(fittingId);
    }

    // Фланцы: запрещены больше Ду80
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 139.7мм (центральные фитинги) ==========
  if (mainPipeDiameter === 139.7) {
    // Ниппели: запрещены больше 3
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4', 'Nippel_1', 'Nippel_1_1.4', 'Nippel_1_1.2', 'Nippel_2', 'Nippel_2_1/2', 'Nippel_3'];
      return allowedNippels.includes(fittingId);
    }

    // Фланцы: запрещены больше Ду80
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 159мм (центральные фитинги) ==========
  if (mainPipeDiameter === 159) {
    // Фланцы: запрещены больше Ду100
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80') || fittingId.startsWith('Flanec_100')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 168.3мм (центральные фитинги) ==========
  if (mainPipeDiameter === 168.3) {
    // Фланцы: запрещены больше Ду100
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80') || fittingId.startsWith('Flanec_100')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 219.1мм (центральные фитинги) ==========
  if (mainPipeDiameter === 219.1) {
    // Фланцы: запрещены больше Ду125
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80') || fittingId.startsWith('Flanec_100') || fittingId.startsWith('Flanec_125')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 273мм (центральные фитинги) ==========
  if (mainPipeDiameter === 273) {
    // Фланцы: запрещены больше Ду150
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80') || fittingId.startsWith('Flanec_100') || fittingId.startsWith('Flanec_125')|| fittingId.startsWith('Flanec_150')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 323.9мм (центральные фитинги)==========
  if (mainPipeDiameter === 323.9) {
    // Фланцы: запрещены больше Ду200
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80') || fittingId.startsWith('Flanec_100') || fittingId.startsWith('Flanec_125') || fittingId.startsWith('Flanec_150') || fittingId.startsWith('Flanec_200')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 355.6мм (центральные фитинги)==========
  if (mainPipeDiameter === 355.6) {
    // Фланцы: запрещены больше Ду200
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80') || fittingId.startsWith('Flanec_100') || fittingId.startsWith('Flanec_125') || fittingId.startsWith('Flanec_150') || fittingId.startsWith('Flanec_200')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 406.4мм (центральные фитинги)==========
  if (mainPipeDiameter === 406.4) {
    // Фланцы: запрещены больше Ду250
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80') || fittingId.startsWith('Flanec_100') || fittingId.startsWith('Flanec_125') || fittingId.startsWith('Flanec_150') || fittingId.startsWith('Flanec_200') || fittingId.startsWith('Flanec_250')) {
        return true;
      }
      return false;
    }
  }

  return true;
};

/**
 * Проверка совместимости фитинга с диаметром гребенки для КОРОТКИХ ДОПОЛНИТЕЛЬНЫХ ВЫХОДОВ распределительного коллектора
 * @param {string} fittingId - ID фитинга
 * @param {number} mainPipeDiameter - Диаметр гребенки
 * @returns {boolean} - true если фитинг совместим
 */
const isShortAdditionalOutletFittingCompatible = (fittingId, mainPipeDiameter) => {
  const fittingData = fittingsData[fittingId];
  if (!fittingData) return true;

  const category = fittingData.category;

  // ========== Ограничения для гребенки 42.4мм (Дополнительные выходы) ==========
  if (mainPipeDiameter === 42.4) {
    // Ниппели: запрещены больше 3/4
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4'];
      return allowedNippels.includes(fittingId);
    }

    // Муфты: запрещены больше 1/2
    if (category === 'Муфты') {
      const allowedMuftas = ['Mufta_1.4', 'Mufta_3.8', 'Mufta_1.2'];
      return allowedMuftas.includes(fittingId);
    }

    // Фланцы: запрещены все
    if (category === 'Фланцы') {
      return false;
    }

    // Накидные гайки: запрещены больше 1"
    if (category === 'Накидные гайки') {
      if (fittingId === 'Nak_Gaika_1_1.4' || fittingId === 'Nak_Gaika_1_1.2' || fittingId === 'Nak_Gaika_2') {
        return false;
      }
      return true;
    }

    // Шайбы с наружной резьбой: запрещены больше 1"
    if (category === 'Шайбы с наружной резьбой') {
      if (fittingId === 'Shaib_Nar_Rezba_1_1.4' || fittingId === 'Shaib_Nar_Rezba_1_1.2' || fittingId === 'Shaib_Nar_Rezba_2') {
        return false;
      }
      return true;
    }
  }

  // ========== Ограничения для гребенки 48.3мм (Дополнительные выходы) ==========
  if (mainPipeDiameter === 48.3) {
    // Ниппели: запрещены больше 3/4
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4'];
      return allowedNippels.includes(fittingId);
    }

    // Муфты: запрещены больше 3/4
    if (category === 'Муфты') {
      const allowedMuftas = ['Mufta_1.4', 'Mufta_3.8', 'Mufta_1.2', 'Mufta_3.4'];
      return allowedMuftas.includes(fittingId);
    }

    // Фланцы: запрещены все
    if (category === 'Фланцы') {
      return false;
    }

    // Накидные гайки: запрещены больше 1"
    if (category === 'Накидные гайки') {
      if (fittingId === 'Nak_Gaika_1_1.4' || fittingId === 'Nak_Gaika_1_1.2' || fittingId === 'Nak_Gaika_2') {
        return false;
      }
      return true;
    }

    // Шайбы с наружной резьбой: запрещены больше 1"
    if (category === 'Шайбы с наружной резьбой') {
      if (fittingId === 'Shaib_Nar_Rezba_1_1.4' || fittingId === 'Shaib_Nar_Rezba_1_1.2' || fittingId === 'Shaib_Nar_Rezba_2') {
        return false;
      }
      return true;
    }
  }

  // ========== Ограничения для гребенки 60.3мм (Дополнительные выходы) ==========
  if (mainPipeDiameter === 60.3) {
    // Ниппели: запрещены больше 1
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4', 'Nippel_1'];
      return allowedNippels.includes(fittingId);
    }

    // Муфты: запрещены больше 1
    if (category === 'Муфты') {
      const allowedMuftas = ['Mufta_1.4', 'Mufta_3.8', 'Mufta_1.2', 'Mufta_3.4', 'Mufta_1'];
      return allowedMuftas.includes(fittingId);
    }

    // Фланцы: только Ду25
    if (category === 'Фланцы') {
      return fittingId.startsWith('Flanec_25_');
    }

    // Накидные гайки: запрещены больше 1 1/2"
    if (category === 'Накидные гайки') {
      if (fittingId === 'Nak_Gaika_2') {
        return false;
      }
      return true;
    }

    // Шайбы с наружной резьбой: запрещены больше 1 1/2"
    if (category === 'Шайбы с наружной резьбой') {
      if (fittingId === 'Shaib_Nar_Rezba_2') {
        return false;
      }
      return true;
    }
  }

  // ========== Ограничения для гребенки 76.1мм (Дополнительные выходы) ==========
  if (mainPipeDiameter === 76.1) {
    // Ниппели: запрещены больше 1 1/2
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4', 'Nippel_1', 'Nippel_1_1.4', 'Nippel_1_1.2'];
      return allowedNippels.includes(fittingId);
    }

    // Муфты: запрещены больше 1 1/4
    if (category === 'Муфты') {
      const allowedMuftas = ['Mufta_1.4', 'Mufta_3.8', 'Mufta_1.2', 'Mufta_3.4', 'Mufta_1', 'Mufta_1_1.4'];
      return allowedMuftas.includes(fittingId);
    }

    // Фланцы: запрещены больше Ду40
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 88.9мм (Дополнительные выходы) ==========
  if (mainPipeDiameter === 88.9) {
    // Ниппели: запрещены больше 2
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4', 'Nippel_1', 'Nippel_1_1.4', 'Nippel_1_1.2', 'Nippel_2'];
      return allowedNippels.includes(fittingId);
    }

    // Муфты: запрещены больше 1 1/2
    if (category === 'Муфты') {
      const allowedMuftas = ['Mufta_1.4', 'Mufta_3.8', 'Mufta_1.2', 'Mufta_3.4', 'Mufta_1', 'Mufta_1_1.4', 'Mufta_1_1.2'];
      return allowedMuftas.includes(fittingId);
    }

    // Фланцы: запрещены больше Ду50
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 101.6мм (Дополнительные выходы) ==========
  if (mainPipeDiameter === 101.6) {
    // Ниппели: запрещены больше 2
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4', 'Nippel_1', 'Nippel_1_1.4', 'Nippel_1_1.2', 'Nippel_2'];
      return allowedNippels.includes(fittingId);
    }

    // Фланцы: запрещены больше Ду50
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 114.3мм (Дополнительные выходы) ==========
  if (mainPipeDiameter === 114.3) {
    // Ниппели: запрещены больше 2 1/2
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4', 'Nippel_1', 'Nippel_1_1.4', 'Nippel_1_1.2', 'Nippel_2', 'Nippel_2_1/2'];
      return allowedNippels.includes(fittingId);
    }

    // Фланцы: запрещены больше Ду65
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 129мм (Дополнительные выходы) ==========
  if (mainPipeDiameter === 129) {
    // Ниппели: запрещены больше 3
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4', 'Nippel_1', 'Nippel_1_1.4', 'Nippel_1_1.2', 'Nippel_2', 'Nippel_2_1/2', 'Nippel_3'];
      return allowedNippels.includes(fittingId);
    }

    // Фланцы: запрещены больше Ду80
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 139.7мм (Дополнительные выходы) ==========
  if (mainPipeDiameter === 139.7) {
    // Ниппели: запрещены больше 3
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4', 'Nippel_1', 'Nippel_1_1.4', 'Nippel_1_1.2', 'Nippel_2', 'Nippel_2_1/2', 'Nippel_3'];
      return allowedNippels.includes(fittingId);
    }

    // Фланцы: запрещены больше Ду80
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 159мм (Дополнительные выходы) ==========
  if (mainPipeDiameter === 159) {
    // Фланцы: запрещены больше Ду100
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80') || fittingId.startsWith('Flanec_100')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 168.3мм (Дополнительные выходы) ==========
  if (mainPipeDiameter === 168.3) {
    // Фланцы: запрещены больше Ду100
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80') || fittingId.startsWith('Flanec_100')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 219.1мм (Дополнительные выходы) ==========
  if (mainPipeDiameter === 219.1) {
    // Фланцы: запрещены больше Ду125
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80') || fittingId.startsWith('Flanec_100') || fittingId.startsWith('Flanec_125')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 273мм (Дополнительные выходы) ==========
  if (mainPipeDiameter === 273) {
    // Фланцы: запрещены больше Ду150
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80') || fittingId.startsWith('Flanec_100') || fittingId.startsWith('Flanec_125')|| fittingId.startsWith('Flanec_150')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 323.9мм (Дополнительные выходы)==========
  if (mainPipeDiameter === 323.9) {
    // Фланцы: запрещены больше Ду200
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80') || fittingId.startsWith('Flanec_100') || fittingId.startsWith('Flanec_125') || fittingId.startsWith('Flanec_150') || fittingId.startsWith('Flanec_200')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 355.6мм (Дополнительные выходы)==========
  if (mainPipeDiameter === 355.6) {
    // Фланцы: запрещены больше Ду200
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80') || fittingId.startsWith('Flanec_100') || fittingId.startsWith('Flanec_125') || fittingId.startsWith('Flanec_150') || fittingId.startsWith('Flanec_200')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 406.4мм (Дополнительные выходы)==========
  if (mainPipeDiameter === 406.4) {
    // Фланцы: запрещены больше Ду250
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80') || fittingId.startsWith('Flanec_100') || fittingId.startsWith('Flanec_125') || fittingId.startsWith('Flanec_150') || fittingId.startsWith('Flanec_200') || fittingId.startsWith('Flanec_250')) {
        return true;
      }
      return false;
    }
  }

  return true;
};

/**
 * Проверка совместимости фитинга с диаметром гребенки для СКВОЗНЫХ ДОПОЛНИТЕЛЬНЫХ ВЫХОДОВ распределительного коллектора
 * Используем те же ограничения, что и для контуров
 * @param {string} fittingId - ID фитинга
 * @param {number} mainPipeDiameter - Диаметр гребенки
 * @returns {boolean} - true если фитинг совместим
 */
const isThroughAdditionalOutletFittingCompatible = (fittingId, mainPipeDiameter) => {
  return isCircuitFittingCompatible(fittingId, mainPipeDiameter);
};

/**
 * Универсальная функция проверки совместимости для дополнительных выходов
 * Выбирает правильную функцию в зависимости от типа выхода
 * @param {string} fittingId - ID фитинга
 * @param {number} mainPipeDiameter - Диаметр гребенки
 * @param {string} outletType - Тип выхода: 'short' (короткий) или 'through' (сквозной)
 * @returns {boolean} - true если фитинг совместим
 */
const isAdditionalOutletFittingCompatible = (fittingId, mainPipeDiameter, outletType = 'short') => {
  if (outletType === 'through') {
    return isThroughAdditionalOutletFittingCompatible(fittingId, mainPipeDiameter);
  }
  return isShortAdditionalOutletFittingCompatible(fittingId, mainPipeDiameter);
};

/**
 * Фитинги термометров и лицевых подключений в компактном режиме.
 * Ниппели и муфты строже контуров; фланцы, гайки и шайбы заданы отдельно
 * и не наследуют торцевые ограничения квадратного профиля.
 */
const isCenterFittingCompatibleWithCompactSize = (fittingId, compactSize) => {
  const fittingData = fittingsData[fittingId];
  if (!fittingData) return true;

  const category = fittingData.category;
  const nakGaikaUpTo1 = ['Nak_Gaika_1.2', 'Nak_Gaika_3.4', 'Nak_Gaika_1'];
  const shaibaUpTo1 = ['Shaib_Nar_Rezba_1.2', 'Shaib_Nar_Rezba_3.4', 'Shaib_Nar_Rezba_1'];

  // ========== Ограничения для компактного размера 60x60 ==========
  if (compactSize === '60x60') {
    // Ниппели: максимум 1/2"
    if (category === 'Ниппели') {
      return ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2'].includes(fittingId);
    }
    // Муфты: максимум 3/8"
    if (category === 'Муфты') {
      return ['Mufta_1.4', 'Mufta_3.8'].includes(fittingId);
    }
    // Фланцы: все запрещены
    if (category === 'Фланцы') {
      return false;
    }
    // Накидные гайки: максимум 1"
    if (category === 'Накидные гайки') {
      return nakGaikaUpTo1.includes(fittingId);
    }
    // Шайбы с наружной резьбой: все запрещены
    if (category === 'Шайбы с наружной резьбой') {
      return false;
    }
    return false;
  }

  // ========== Ограничения для компактного размера 80x80 ==========
  if (compactSize === '80x80') {
    // Ниппели: максимум 3/4"
    if (category === 'Ниппели') {
      return ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4'].includes(fittingId);
    }
    // Муфты: максимум 1/2"
    if (category === 'Муфты') {
      return ['Mufta_1.4', 'Mufta_3.8', 'Mufta_1.2'].includes(fittingId);
    }
    // Фланцы: все запрещены
    if (category === 'Фланцы') {
      return false;
    }
    // Накидные гайки: максимум 1"
    if (category === 'Накидные гайки') {
      return nakGaikaUpTo1.includes(fittingId);
    }
    // Шайбы с наружной резьбой: максимум 1"
    if (category === 'Шайбы с наружной резьбой') {
      return shaibaUpTo1.includes(fittingId);
    }
    return false;
  }

  return true;
};

/**
 * Дополнительные выходы в компактном режиме.
 * 60×60 совпадает с ограничениями контуров этого размера.
 * 80×80: муфты до 1 1/2" и фланцы до Ду50, остальное — как у контуров.
 */
const isAdditionalOutletFittingCompatibleWithCompactSize = (fittingId, compactSize) => {
  if (compactSize === '80x80') {
    const fittingData = fittingsData[fittingId];
    if (!fittingData) return true;
    const category = fittingData.category;
    if (category === 'Муфты') {
      return ['Mufta_1.4', 'Mufta_3.8', 'Mufta_1.2', 'Mufta_3.4', 'Mufta_1', 'Mufta_1_1.4', 'Mufta_1_1.2'].includes(fittingId);
    }
    if (category === 'Фланцы') {
      return (
        fittingId.startsWith('Flanec_25_') ||
        fittingId.startsWith('Flanec_32') ||
        fittingId.startsWith('Flanec_40') ||
        fittingId.startsWith('Flanec_50')
      );
    }
  }
  return isCircuitFittingCompatibleWithSquareProfile(fittingId, compactSize);
};

/**
 * УСТАРЕВШАЯ: Для обратной совместимости
 * Используйте isCircuitFittingCompatibleWithSquareProfile для контуров
 * и isEndFittingCompatibleWithSquareProfile для торцевых подключений
 */
// eslint-disable-next-line no-unused-vars
const isFittingCompatibleWithSquareProfile = (fittingId, profile) => {
  return isCircuitFittingCompatibleWithSquareProfile(fittingId, profile);
};

/**
 * Параметры по умолчанию для распределительного коллектора
 */
const defaultParams = {
  // Форма коллекторов
  shape: 'round', // 'round' или 'square'
  isCompact: false, // Компактный режим (только для квадратных)
  compactSize: '80x80', // Общий размер для компактного режима ('60x60' или '80x80')
  /** Снимок до входа в компактный режим; хранится в params, чтобы пережить размонтирование (чертёж → назад). */
  preCompactUndoSnapshot: null,
  
  // Диаметры коллекторов (для круглых)
  supplyPipeDiameter: 76.1,
  returnPipeDiameter: 76.1,
  
  // Профили коллекторов (для квадратных)
  supplyPipeProfile: '80x80',
  returnPipeProfile: '80x80',
  
  pipeSpacing: 200,
  
  // Контуры
  circuitCount: 3,
  supplyFirstCircuitOffset: 80,
  supplyFirstCircuitOffsetMode: 'fromEdge', // 'fromEdge' или 'fromReturnCircuit'
  supplyFirstCircuitOffsetReferenceIndex: 0, // индекс контура обратки, от которого отступаем (0 = первый)
  supplyFirstCircuitOffsetSaved: null, // сохранённое значение до включения режима привязки
  supplyFirstCircuitOffsetLastUsed: 0, // последнее использованное смещение в режиме привязки
  supplyLastCircuitOffset: 80,
  returnFirstCircuitOffset: 80,
  returnFirstCircuitOffsetMode: 'fromEdge', // 'fromEdge' или 'fromSupplyCircuit'
  returnFirstCircuitOffsetReferenceIndex: 0, // индекс контура подачи, от которого отступаем (0 = первый)
  returnFirstCircuitOffsetSaved: null, // сохранённое значение до включения режима привязки
  returnFirstCircuitOffsetLastUsed: 0, // последнее использованное смещение в режиме привязки
  returnLastCircuitOffset: 80,
  supplyCircuitSpacing: 125, // межосевое контура подачи по умолчанию
  returnCircuitSpacing: 125, // межосевое контура обратки по умолчанию
  supplySpacingBetweenCircuits: 125, // расстояние между контурами подачи
  returnSpacingBetweenCircuits: 125, // расстояние между контурами обратки
  
  // Подключения подающего коллектора
  hasSupplyLeftConnection: false,
  hasSupplyRightConnection: false,
  supplyLeftConnectionType: 'fitting', // 'pipe' или 'fitting'
  supplyLeftConnectionFittingCategory: '',
  supplyLeftConnectionFitting: '',
  supplyLeftConnectionFittingLength: null, // для обычных фитингов
  supplyLeftConnectionLength: null, // для фланцев/гаек/шайб с переходами/заглушками
  supplyLeftConnectionPipeDiameter: 60.3,
  supplyLeftConnectionPipeLength: 100,
  
  supplyRightConnectionType: 'fitting',
  supplyRightConnectionFittingCategory: '',
  supplyRightConnectionFitting: '',
  supplyRightConnectionFittingLength: null,
  supplyRightConnectionLength: null,
  supplyRightConnectionPipeDiameter: 60.3,
  supplyRightConnectionPipeLength: 100,
  
  // Заглушки/переходы подающего коллектора
  hasSupplyLeftCap: true,
  supplyLeftCapType: 'elliptical', // 'elliptical' или 'straight'
  supplyLeftCapElementType: 'cap', // 'cap' или 'reducer'
  supplyLeftReducer: null,
  
  hasSupplyRightCap: true,
  supplyRightCapType: 'elliptical',
  supplyRightCapElementType: 'cap',
  supplyRightReducer: null,
  
  // Подключения обратного коллектора
  hasReturnLeftConnection: false,
  hasReturnRightConnection: false,
  returnLeftConnectionType: 'fitting',
  returnLeftConnectionFittingCategory: '',
  returnLeftConnectionFitting: '',
  returnLeftConnectionFittingLength: null,
  returnLeftConnectionLength: null,
  returnLeftConnectionPipeDiameter: 60.3,
  returnLeftConnectionPipeLength: 100,
  
  returnRightConnectionType: 'fitting',
  returnRightConnectionFittingCategory: '',
  returnRightConnectionFitting: '',
  returnRightConnectionFittingLength: null,
  returnRightConnectionLength: null,
  returnRightConnectionPipeDiameter: 60.3,
  returnRightConnectionPipeLength: 100,
  
  // Заглушки/переходы обратного коллектора
  hasReturnLeftCap: true,
  returnLeftCapType: 'elliptical',
  returnLeftCapElementType: 'cap',
  returnLeftReducer: null,
  
  hasReturnRightCap: true,
  returnRightCapType: 'elliptical',
  returnRightCapElementType: 'cap',
  returnRightReducer: null,
  
  // Центральные фитинги
  supplyCenterFittingCount: 0,
  returnCenterFittingCount: 0,
  
  // Дополнительные выходы (одиночные, как в этажном коллекторе)
  supplyAdditionalOutletCount: 0,
  returnAdditionalOutletCount: 0,
  supplyAdditionalFirstOutletOffset: 60,
  returnAdditionalFirstOutletOffset: 60,
  supplyAdditionalLastOutletOffset: 60,
  returnAdditionalLastOutletOffset: 60,
  supplyAdditionalOutletSpacing: 100,
  returnAdditionalOutletSpacing: 100,
  
  // Ручное управление длинами
  useManualLength: false, // Флаг ручного управления длинами
  manualSupplyLength: null, // Ручная длина подачи
  manualReturnLength: null, // Ручная длина обратки
};

/**
 * При входе в компактный режим: сброс данных из «характеристик» и лишнего —
 * торцевые подключения, центральные фитинги (термометры и т.п.), доп. выходы.
 * Счётчики + поля params; массивы сбрасываются отдельно через setState.
 */
const COMPACT_MODE_ENTRY_PARAM_RESET = {
  hasSupplyLeftConnection: false,
  hasSupplyRightConnection: false,
  hasReturnLeftConnection: false,
  hasReturnRightConnection: false,
  supplyLeftConnectionType: 'fitting',
  supplyLeftConnectionFittingCategory: '',
  supplyLeftConnectionFitting: '',
  supplyLeftConnectionFittingLength: null,
  supplyLeftConnectionLength: null,
  supplyLeftConnectionPipeDiameter: defaultParams.supplyLeftConnectionPipeDiameter,
  supplyLeftConnectionPipeLength: defaultParams.supplyLeftConnectionPipeLength,
  supplyRightConnectionType: 'fitting',
  supplyRightConnectionFittingCategory: '',
  supplyRightConnectionFitting: '',
  supplyRightConnectionFittingLength: null,
  supplyRightConnectionLength: null,
  supplyRightConnectionPipeDiameter: defaultParams.supplyRightConnectionPipeDiameter,
  supplyRightConnectionPipeLength: defaultParams.supplyRightConnectionPipeLength,
  returnLeftConnectionType: 'fitting',
  returnLeftConnectionFittingCategory: '',
  returnLeftConnectionFitting: '',
  returnLeftConnectionFittingLength: null,
  returnLeftConnectionLength: null,
  returnLeftConnectionPipeDiameter: defaultParams.returnLeftConnectionPipeDiameter,
  returnLeftConnectionPipeLength: defaultParams.returnLeftConnectionPipeLength,
  returnRightConnectionType: 'fitting',
  returnRightConnectionFittingCategory: '',
  returnRightConnectionFitting: '',
  returnRightConnectionFittingLength: null,
  returnRightConnectionLength: null,
  returnRightConnectionPipeDiameter: defaultParams.returnRightConnectionPipeDiameter,
  returnRightConnectionPipeLength: defaultParams.returnRightConnectionPipeLength,
  supplyCenterFittingCount: 0,
  returnCenterFittingCount: 0,
  supplyAdditionalOutletCount: 0,
  returnAdditionalOutletCount: 0,
};

/** Ключи params, которые сбрасываются при входе в компактный режим (для снимка «отката»). */
const COMPACT_MODE_SNAPSHOT_PARAM_KEYS = Object.keys(COMPACT_MODE_ENTRY_PARAM_RESET);

function deepCloneJson(obj) {
  return JSON.parse(JSON.stringify(obj));
}

/**
 * Определяет нужно ли использовать отдельный параметр длины для фланцев/гаек/шайб
 * @param {string} fittingId - ID фитинга
 * @param {boolean} hasCap - есть ли заглушка/переход
 * @param {number} mainPipeDiameter - диаметр основной трубы
 * @returns {boolean}
 */
const shouldUseConnectionLength = (fittingId, hasCap, mainPipeDiameter) => {
  if (!fittingId) return false;
  
  const isFlange = fittingId.startsWith('Flanec_');
  const isNakGaika = fittingId.startsWith('Nak_Gaika_');
  const isShaibaNarRezba = fittingId.startsWith('Shaib_Nar_Rezba_');
  
  // Для накидных гаек и шайб с наружной резьбой всегда используем connectionLength
  if (isNakGaika || isShaibaNarRezba) {
    return true;
  }
  
  // Для фланцев - только если есть переход/заглушка или диаметры не совпадают
  if (isFlange) {
    const dn = extractDnFromFlangeKey(fittingId);
    const flangePipeDiameter = getPipeDiameterForDn(dn);
    const needsReducerOrCap = flangePipeDiameter && 
                              flangePipeDiameter !== mainPipeDiameter && 
                              mainPipeDiameter > flangePipeDiameter;
    return needsReducerOrCap || hasCap;
  }
  
  return false;
};

/**
 * Редактор распределительного коллектора
 */
const DistributionCollectorEditor = ({
  onGenerate,
  onBack,
  onBackToDrawing,
  initialParams,
  calculationMode,
  onBackToCharacteristics,
}) => {
  // Инициализация параметров из сохранённых или по умолчанию
  const [params, setParams] = useState(() => {
    if (initialParams?.params) {
      const migratedParams = { ...defaultParams, ...initialParams.params };
      
      // Миграция старых параметров отступов
      if (migratedParams.firstCircuitOffset !== undefined) {
        // Если есть старые параметры, используем их для обоих коллекторов
        if (migratedParams.supplyFirstCircuitOffset === undefined) {
          migratedParams.supplyFirstCircuitOffset = migratedParams.firstCircuitOffset;
        }
        if (migratedParams.returnFirstCircuitOffset === undefined) {
          migratedParams.returnFirstCircuitOffset = migratedParams.firstCircuitOffset;
        }
        delete migratedParams.firstCircuitOffset;
      }
      
      if (migratedParams.lastCircuitOffset !== undefined) {
        if (migratedParams.supplyLastCircuitOffset === undefined) {
          migratedParams.supplyLastCircuitOffset = migratedParams.lastCircuitOffset;
        }
        if (migratedParams.returnLastCircuitOffset === undefined) {
          migratedParams.returnLastCircuitOffset = migratedParams.lastCircuitOffset;
        }
        delete migratedParams.lastCircuitOffset;
      }
      
      // Миграция старых ключей с неправильным регистром
      // Старые ключи: hassupplyLeftCap, supplyLeftcapElementType и т.д.
      // Новые ключи: hasSupplyLeftCap, supplyLeftCapElementType и т.д.
      const migrations = [
        { old: 'hassupplyLeftCap', new: 'hasSupplyLeftCap' },
        { old: 'supplyLeftcapElementType', new: 'supplyLeftCapElementType' },
        { old: 'supplyLeftcapType', new: 'supplyLeftCapType' },
        { old: 'supplyLeftreducer', new: 'supplyLeftReducer' },
        { old: 'hassupplyRightCap', new: 'hasSupplyRightCap' },
        { old: 'supplyRightcapElementType', new: 'supplyRightCapElementType' },
        { old: 'supplyRightcapType', new: 'supplyRightCapType' },
        { old: 'supplyRightreducer', new: 'supplyRightReducer' },
        { old: 'hasreturnLeftCap', new: 'hasReturnLeftCap' },
        { old: 'returnLeftcapElementType', new: 'returnLeftCapElementType' },
        { old: 'returnLeftcapType', new: 'returnLeftCapType' },
        { old: 'returnLeftreducer', new: 'returnLeftReducer' },
        { old: 'hasreturnRightCap', new: 'hasReturnRightCap' },
        { old: 'returnRightcapElementType', new: 'returnRightCapElementType' },
        { old: 'returnRightcapType', new: 'returnRightCapType' },
        { old: 'returnRightreducer', new: 'returnRightReducer' },
      ];
      
      migrations.forEach(({ old, new: newKey }) => {
        if (migratedParams[old] !== undefined) {
          migratedParams[newKey] = migratedParams[old];
          delete migratedParams[old];
        }
      });
      
      return migratedParams;
    }
    return defaultParams;
  });

  // Инициализация контуров подачи из сохранённых или по умолчанию
  const [supplyCircuits, setSupplyCircuits] = useState(() => {
    if (initialParams?.supplyCircuits) {
      return initialParams.supplyCircuits.map(circuit => ({
        ...circuit,
        circuitSpacing: circuit.circuitSpacing ?? 50,
        swapOrder: circuit.swapOrder ?? false,
      }));
    }
    // Миграция старого формата circuits
    if (initialParams?.circuits && initialParams.circuits.length > 0) {
      return initialParams.circuits.map(circuit => ({
        id: circuit.id,
        fitting: circuit.supplyFitting,
        fittingLength: circuit.supplyFittingLength,
        flangePipeLength: circuit.supplyFlangePipeLength,
        spacingToNext: circuit.spacingToNext,
        circuitSpacing: circuit.circuitSpacing ?? 50,
        swapOrder: circuit.swapOrder ?? false,
      }));
    }
    return Array.from({ length: defaultParams.circuitCount }, (_, i) => ({
      id: `supply-${i}`,
      fitting: 'Nippel_1',
      fittingLength: null,
      flangePipeLength: 100,
      spacingToNext: null,
      circuitSpacing: 125, // межосевое контура по умолчанию
      swapOrder: false,
    }));
  });
  
  // Инициализация контуров обратки из сохранённых или по умолчанию
  const [returnCircuits, setReturnCircuits] = useState(() => {
    if (initialParams?.returnCircuits) {
      return initialParams.returnCircuits.map(circuit => ({
        ...circuit,
        circuitSpacing: circuit.circuitSpacing ?? 50,
        swapOrder: circuit.swapOrder ?? false,
      }));
    }
    // Миграция старого формата circuits
    if (initialParams?.circuits && initialParams.circuits.length > 0) {
      return initialParams.circuits.map(circuit => ({
        id: circuit.id + '-return',
        fitting: circuit.returnFitting,
        fittingLength: circuit.returnFittingLength,
        flangePipeLength: circuit.returnFlangePipeLength,
        spacingToNext: circuit.spacingToNext,
        circuitSpacing: circuit.circuitSpacing ?? 50,
        swapOrder: circuit.swapOrder ?? false,
      }));
    }
    return Array.from({ length: defaultParams.circuitCount }, (_, i) => ({
      id: `return-${i}`,
      fitting: 'Nippel_1',
      fittingLength: null,
      flangePipeLength: 100,
      spacingToNext: null,
      circuitSpacing: 125, // межосевое контура по умолчанию
      swapOrder: false,
    }));
  });
  
  // Центральные фитинги подающего коллектора
  const [supplyCenterFittings, setSupplyCenterFittings] = useState(() => {
    if (initialParams?.supplyCenterFittings && initialParams.supplyCenterFittings.length > 0) {
      return initialParams.supplyCenterFittings;
    }
    return [];
  });
  
  // Центральные фитинги обратного коллектора
  const [returnCenterFittings, setReturnCenterFittings] = useState(() => {
    if (initialParams?.returnCenterFittings && initialParams.returnCenterFittings.length > 0) {
      return initialParams.returnCenterFittings;
    }
    return [];
  });
  
  // Дополнительные выходы подающего коллектора
  const [supplyAdditionalOutlets, setSupplyAdditionalOutlets] = useState(() => {
    if (initialParams?.supplyAdditionalOutlets && initialParams.supplyAdditionalOutlets.length > 0) {
      return initialParams.supplyAdditionalOutlets.map((outlet) => {
        // Добавляем значения по умолчанию для новых полей
        return {
          ...outlet,
          direction: outlet.direction || 'up',
          positionType: outlet.positionType || 'distance', // Старые данные используют distance
          distanceFromEdge: outlet.distanceFromEdge ?? 60,
          distanceDirection: outlet.distanceDirection || 'left',
          betweenIndex: outlet.betweenIndex ?? 0,
          outletType: outlet.outletType || 'short', // Тип выхода: 'short' (короткий) или 'through' (сквозной)
        };
      });
    }
    return [];
  });
  
  // Дополнительные выходы обратного коллектора
  const [returnAdditionalOutlets, setReturnAdditionalOutlets] = useState(() => {
    if (initialParams?.returnAdditionalOutlets && initialParams.returnAdditionalOutlets.length > 0) {
      return initialParams.returnAdditionalOutlets.map((outlet) => {
        // Добавляем значения по умолчанию для новых полей
        return {
          ...outlet,
          direction: outlet.direction || 'down',
          positionType: outlet.positionType || 'distance', // Старые данные используют distance
          distanceFromEdge: outlet.distanceFromEdge ?? 60,
          distanceDirection: outlet.distanceDirection || 'left',
          betweenIndex: outlet.betweenIndex ?? 0,
          outletType: outlet.outletType || 'short', // Тип выхода: 'short' (короткий) или 'through' (сквозной)
        };
      });
    }
    return [];
  });
  
  const [activeTab, setActiveTab] = useState(() => {
    // Восстанавливаем активную вкладку при возврате из чертежа
    if (initialParams?.activeTab !== undefined) {
      return initialParams.activeTab;
    }
    return 0;
  });
  const [incompatibilityDialog, setIncompatibilityDialog] = useState({
    open: false,
    message: '',
  });
  
  // Состояние для drag & drop контуров
  const [draggedSupplyCircuitIndex, setDraggedSupplyCircuitIndex] = useState(null);
  const [draggedReturnCircuitIndex, setDraggedReturnCircuitIndex] = useState(null);
  const [lengthsEqualized, setLengthsEqualized] = useState(false); // Флаг приравнивания длин
  const [equalizeDialog, setEqualizeDialog] = useState(false); // Диалог предложения приравнять длины
  const [diametersLinked, setDiametersLinked] = useState(true); // Флаг синхронизации диаметров (по умолчанию включен)
  const [leftConnectionsLinked, setLeftConnectionsLinked] = useState(true); // Флаг синхронизации левых торцевых подключений
  const [rightConnectionsLinked, setRightConnectionsLinked] = useState(true); // Флаг синхронизации правых торцевых подключений
  
  // Состояние для хранения предыдущих типов заглушек при переключении на квадратную форму
  const [savedCapTypes, setSavedCapTypes] = useState(null);

  /** Снимок params и массивов до входа в компактный режим — для восстановления при выходе. */
  const preCompactUndoRef = useRef(null);

  // Вычисляем эффективный отступ первого контура подачи (может зависеть от контура обратки)
  const effectiveSupplyFirstCircuitOffset = useMemo(() => {
    // Если режим "от контура обратки" включен
    if (params.supplyFirstCircuitOffsetMode === 'fromReturnCircuit') {
      // Проверяем наличие контуров обратки
      if (returnCircuits.length === 0) {
        // Если нет контуров обратки, используем обычное значение
        return params.supplyFirstCircuitOffset;
      }
      
      // Вычисляем позицию выбранного контура обратки
      const referenceIndex = params.supplyFirstCircuitOffsetReferenceIndex || 0;
      
      // Проверяем, что индекс в пределах доступных контуров
      if (referenceIndex >= returnCircuits.length) {
        // Если выбранный контур не существует, используем первый
        return params.returnFirstCircuitOffset + (params.supplyFirstCircuitOffset || 0);
      }
      
      // Вычисляем позицию выбранного контура обратки
      let referenceCircuitPosition = params.returnFirstCircuitOffset;
      
      // Добавляем расстояния до выбранного контура
      for (let i = 0; i < referenceIndex; i++) {
        const circuit = returnCircuits[i];
        const circuitSpacing = Number(circuit.circuitSpacing) || params.returnCircuitSpacing || 125;
        referenceCircuitPosition += circuitSpacing;
        
        // Добавляем расстояние до следующего контура
        if (i < referenceIndex) {
          const spacingToNext = Number(circuit.spacingToNext) || params.returnSpacingBetweenCircuits || 125;
          referenceCircuitPosition += spacingToNext;
        }
      }
      
      // Возвращаем: позиция выбранного контура обратки + смещение
      return referenceCircuitPosition + (params.supplyFirstCircuitOffset || 0);
    }
    
    // Режим "от торца" - используем обычное значение
    return params.supplyFirstCircuitOffset;
  }, [
    params.supplyFirstCircuitOffsetMode, 
    params.supplyFirstCircuitOffset, 
    params.supplyFirstCircuitOffsetReferenceIndex,
    params.returnFirstCircuitOffset,
    params.returnCircuitSpacing,
    params.returnSpacingBetweenCircuits,
    returnCircuits
  ]);

  // Вычисляем эффективный отступ первого контура обратки (может зависеть от контура подачи)
  const effectiveReturnFirstCircuitOffset = useMemo(() => {
    // Если режим "от контура подачи" включен
    if (params.returnFirstCircuitOffsetMode === 'fromSupplyCircuit') {
      // Проверяем наличие контуров подачи
      if (supplyCircuits.length === 0) {
        // Если нет контуров подачи, используем обычное значение
        return params.returnFirstCircuitOffset;
      }
      
      // Вычисляем позицию выбранного контура подачи
      const referenceIndex = params.returnFirstCircuitOffsetReferenceIndex || 0;
      
      // Проверяем, что индекс в пределах доступных контуров
      if (referenceIndex >= supplyCircuits.length) {
        // Если выбранный контур не существует, используем первый
        return effectiveSupplyFirstCircuitOffset + (params.returnFirstCircuitOffset || 0);
      }
      
      // Вычисляем позицию выбранного контура подачи
      let referenceCircuitPosition = effectiveSupplyFirstCircuitOffset;
      
      // Добавляем расстояния до выбранного контура
      for (let i = 0; i < referenceIndex; i++) {
        const circuit = supplyCircuits[i];
        const circuitSpacing = Number(circuit.circuitSpacing) || params.supplyCircuitSpacing || 125;
        referenceCircuitPosition += circuitSpacing;
        
        // Добавляем расстояние до следующего контура
        if (i < referenceIndex) {
          const spacingToNext = Number(circuit.spacingToNext) || params.supplySpacingBetweenCircuits || 125;
          referenceCircuitPosition += spacingToNext;
        }
      }
      
      // Возвращаем: позиция выбранного контура подачи + смещение
      return referenceCircuitPosition + (params.returnFirstCircuitOffset || 0);
    }
    
    // Режим "от торца" - используем обычное значение
    return params.returnFirstCircuitOffset;
  }, [
    params.returnFirstCircuitOffsetMode, 
    params.returnFirstCircuitOffset, 
    params.returnFirstCircuitOffsetReferenceIndex,
    effectiveSupplyFirstCircuitOffset,
    params.supplyCircuitSpacing,
    params.supplySpacingBetweenCircuits,
    supplyCircuits
  ]);

  // Расчёт длины коллекторов (БЕЗ прямых заглушек)
  // Отступы считаются от внешнего края заглушки, поэтому длина заглушки не добавляется
  const { supplyCalculatedLength, returnCalculatedLength } = useMemo(() => {
    // Если включен ручной режим, используем заданные значения
    if (params.useManualLength) {
      return {
        supplyCalculatedLength: params.manualSupplyLength || 150,
        returnCalculatedLength: params.manualReturnLength || 150,
      };
    }
    
    // Рассчитываем длину для коллектора подачи
    let supplyLength = effectiveSupplyFirstCircuitOffset + params.supplyLastCircuitOffset;
    
    // Добавляем межосевое внутри каждого контура подачи
    supplyCircuits.forEach((circuit, index) => {
      const circuitSpacing = Number(circuit.circuitSpacing) || params.supplyCircuitSpacing || 125;
      supplyLength += circuitSpacing; // межосевое внутри контура (между двумя патрубками)
      
      // Добавляем расстояние до следующего контура (кроме последнего)
      if (index < supplyCircuits.length - 1) {
        const spacingToNext = Number(circuit.spacingToNext) || params.supplySpacingBetweenCircuits || 125;
        supplyLength += spacingToNext;
      }
    });
    
    // Рассчитываем длину для коллектора обратки
    let returnLength = effectiveReturnFirstCircuitOffset + params.returnLastCircuitOffset;
    
    // Добавляем межосевое внутри каждого контура обратки
    returnCircuits.forEach((circuit, index) => {
      const circuitSpacing = Number(circuit.circuitSpacing) || params.returnCircuitSpacing || 125;
      returnLength += circuitSpacing; // межосевое внутри контура (между двумя патрубками)
      
      // Добавляем расстояние до следующего контура (кроме последнего)
      if (index < returnCircuits.length - 1) {
        const spacingToNext = Number(circuit.spacingToNext) || params.returnSpacingBetweenCircuits || 125;
        returnLength += spacingToNext;
      }
    });
    
    // Применяем минимальную длину
    supplyLength = Math.max(supplyLength, 150);
    returnLength = Math.max(returnLength, 150);
    
    return {
      supplyCalculatedLength: supplyLength,
      returnCalculatedLength: returnLength,
    };
  }, [
    params.useManualLength, 
    params.manualSupplyLength, 
    params.manualReturnLength,
    effectiveSupplyFirstCircuitOffset, 
    params.supplyLastCircuitOffset, 
    effectiveReturnFirstCircuitOffset, 
    params.returnLastCircuitOffset, 
    params.supplySpacingBetweenCircuits, 
    params.returnSpacingBetweenCircuits, 
    params.supplyCircuitSpacing, 
    params.returnCircuitSpacing, 
    supplyCircuits, 
    returnCircuits
  ]);

  // Вспомогательная функция для расчета длины подключения (только для UI)
  const getConnectionLengthForUI = useCallback((connectionFitting, connectionFittingLength, connectionLength, mainPipeDiameter, hasCap, capElementType, shape) => {
    // Если задана длина вручную, используем её
    if (connectionLength !== null && connectionLength !== undefined) {
      return connectionLength;
    }
    
    // Ниппели - либо длина из каталога, либо заданная вручную
    if (connectionFitting?.startsWith('Nippel_')) {
      if (connectionFittingLength !== null && connectionFittingLength !== undefined) {
        return connectionFittingLength;
      }
      const fittingData = fittingsData[connectionFitting];
      return fittingData?.length || 0;
    }
    
    // Муфты - либо длина из каталога, либо заданная вручную
    if (connectionFitting?.startsWith('Mufta_') || connectionFitting?.startsWith('Nippel_Mufta_')) {
      if (connectionFittingLength !== null && connectionFittingLength !== undefined) {
        return connectionFittingLength;
      }
      const fittingData = fittingsData[connectionFitting];
      return fittingData?.length || 0;
    }
    
    // Фланцы - зависит от соотношения Ду фланца и диаметра основной трубы
    if (connectionFitting?.startsWith('Flanec_')) {
      const flangeDn = extractDnFromFlangeKey(connectionFitting);
      const flangePipeDiameter = getPipeDiameterForDn(flangeDn);
      
      // Для квадратных профилей всегда используем дефолтную длину 100 мм
      // (нет логики "одинаковых диаметров")
      if (shape === 'square') {
        return 100;
      }
      
      // Для круглых труб: если Ду фланца совпадает с диаметром основной трубы - не добавляем длину
      if (flangePipeDiameter && flangePipeDiameter === mainPipeDiameter) {
        return 0;
      }
      
      // Если Ду фланца меньше диаметра основной трубы - 100 мм (дефолт)
      // Эта длина уже учитывает заглушку/переход (не нужно их прибавлять отдельно)
      if (flangePipeDiameter && flangePipeDiameter < mainPipeDiameter) {
        return 100;
      }
      
      // В остальных случаях
      return 100;
    }
    
    // Накидные гайки - дефолт зависит от формы трубы
    if (connectionFitting?.startsWith('Nak_Gaika_')) {
      return shape === 'square' ? 54 : 32; // Квадратные: 54мм, круглые: 32мм
    }
    
    // Шайбы с резьбой - дефолт зависит от формы трубы
    if (connectionFitting?.startsWith('Shaib_Nar_Rezba_')) {
      return shape === 'square' ? 54 : 32; // Квадратные: 54мм, круглые: 32мм
    }
    
    return 0;
  }, []);

  // Расчет общей длины подающего коллектора (для отображения в UI)
  const supplyTotalLength = useMemo(() => {
    let totalLength = supplyCalculatedLength;
    
    // Вспомогательная функция для проверки, является ли фланец с меньшим диаметром
    const isFlangeWithSmallerDiameter = (connectionFitting, mainDiameter) => {
      if (!connectionFitting?.startsWith('Flanec_')) return false;
      const flangeDn = extractDnFromFlangeKey(connectionFitting);
      const flangePipeDiameter = getPipeDiameterForDn(flangeDn);
      return flangePipeDiameter && flangePipeDiameter < mainDiameter;
    };
    
    // Добавляем левое подключение
    if (params.hasSupplyLeftConnection) {
      if (params.supplyLeftConnectionType === 'pipe') {
        totalLength += params.supplyLeftConnectionLength || 100;
      } else if (params.supplyLeftConnectionType === 'fitting') {
        totalLength += getConnectionLengthForUI(
          params.supplyLeftConnectionFitting,
          params.supplyLeftConnectionFittingLength,
          params.supplyLeftConnectionLength,
          params.supplyPipeDiameter,
          params.hasSupplyLeftCap,
          params.supplyLeftCapElementType,
          params.shape
        );
      }
    }
    
    // Добавляем правое подключение
    if (params.hasSupplyRightConnection) {
      if (params.supplyRightConnectionType === 'pipe') {
        totalLength += params.supplyRightConnectionLength || 100;
      } else if (params.supplyRightConnectionType === 'fitting') {
        totalLength += getConnectionLengthForUI(
          params.supplyRightConnectionFitting,
          params.supplyRightConnectionFittingLength,
          params.supplyRightConnectionLength,
          params.supplyPipeDiameter,
          params.hasSupplyRightCap,
          params.supplyRightCapElementType,
          params.shape
        );
      }
    }
    
    // Проверяем наличие фланцев с меньшим диаметром
    const hasLeftFlangeSmaller = params.hasSupplyLeftConnection && 
                                  params.supplyLeftConnectionType === 'fitting' &&
                                  isFlangeWithSmallerDiameter(params.supplyLeftConnectionFitting, params.supplyPipeDiameter);
    
    const hasRightFlangeSmaller = params.hasSupplyRightConnection && 
                                   params.supplyRightConnectionType === 'fitting' &&
                                   isFlangeWithSmallerDiameter(params.supplyRightConnectionFitting, params.supplyPipeDiameter);
    
    // Добавляем левую заглушку/переход (если выступают наружу и нет фланца с меньшим диаметром)
    if (params.hasSupplyLeftCap && !hasLeftFlangeSmaller) {
      if (params.supplyLeftCapElementType === 'reducer') {
        const reducerData = fittingsData[params.supplyLeftReducer];
        if (reducerData) {
          totalLength += reducerData.length;
        }
      } else if (params.supplyLeftCapType === 'elliptical') {
        const capKey = `Zaglushka_EL_${params.supplyPipeDiameter}`;
        const capData = fittingsData[capKey];
        if (capData) {
          totalLength += capData.length;
        }
      }
    }
    
    // Добавляем правую заглушку/переход
    if (params.hasSupplyRightCap && !hasRightFlangeSmaller) {
      if (params.supplyRightCapElementType === 'reducer') {
        const reducerData = fittingsData[params.supplyRightReducer];
        if (reducerData) {
          totalLength += reducerData.length;
        }
      } else if (params.supplyRightCapType === 'elliptical') {
        const capKey = `Zaglushka_EL_${params.supplyPipeDiameter}`;
        const capData = fittingsData[capKey];
        if (capData) {
          totalLength += capData.length;
        }
      }
    }
    
    return Math.round(totalLength);
  }, [
    supplyCalculatedLength,
    params.hasSupplyLeftConnection,
    params.supplyLeftConnectionType,
    params.supplyLeftConnectionLength,
    params.supplyLeftConnectionFitting,
    params.supplyLeftConnectionFittingLength,
    params.hasSupplyRightConnection,
    params.supplyRightConnectionType,
    params.supplyRightConnectionLength,
    params.supplyRightConnectionFitting,
    params.supplyRightConnectionFittingLength,
    params.hasSupplyLeftCap,
    params.supplyLeftCapElementType,
    params.supplyLeftCapType,
    params.supplyLeftReducer,
    params.supplyPipeDiameter,
    params.hasSupplyRightCap,
    params.supplyRightCapElementType,
    params.supplyRightCapType,
    params.supplyRightReducer,
    params.shape,
    getConnectionLengthForUI,
  ]);

  // Расчет общей длины обратного коллектора (для отображения в UI)
  const returnTotalLength = useMemo(() => {
    let totalLength = returnCalculatedLength;
    
    // Вспомогательная функция для проверки, является ли фланец с меньшим диаметром
    const isFlangeWithSmallerDiameter = (connectionFitting, mainDiameter) => {
      if (!connectionFitting?.startsWith('Flanec_')) return false;
      const flangeDn = extractDnFromFlangeKey(connectionFitting);
      const flangePipeDiameter = getPipeDiameterForDn(flangeDn);
      return flangePipeDiameter && flangePipeDiameter < mainDiameter;
    };
    
    // Добавляем левое подключение
    if (params.hasReturnLeftConnection) {
      if (params.returnLeftConnectionType === 'pipe') {
        totalLength += params.returnLeftConnectionLength || 100;
      } else if (params.returnLeftConnectionType === 'fitting') {
        totalLength += getConnectionLengthForUI(
          params.returnLeftConnectionFitting,
          params.returnLeftConnectionFittingLength,
          params.returnLeftConnectionLength,
          params.returnPipeDiameter,
          params.hasReturnLeftCap,
          params.returnLeftCapElementType,
          params.shape
        );
      }
    }
    
    // Добавляем правое подключение
    if (params.hasReturnRightConnection) {
      if (params.returnRightConnectionType === 'pipe') {
        totalLength += params.returnRightConnectionLength || 100;
      } else if (params.returnRightConnectionType === 'fitting') {
        totalLength += getConnectionLengthForUI(
          params.returnRightConnectionFitting,
          params.returnRightConnectionFittingLength,
          params.returnRightConnectionLength,
          params.returnPipeDiameter,
          params.hasReturnRightCap,
          params.returnRightCapElementType,
          params.shape
        );
      }
    }
    
    // Проверяем наличие фланцев с меньшим диаметром
    const hasLeftFlangeSmaller = params.hasReturnLeftConnection && 
                                  params.returnLeftConnectionType === 'fitting' &&
                                  isFlangeWithSmallerDiameter(params.returnLeftConnectionFitting, params.returnPipeDiameter);
    
    const hasRightFlangeSmaller = params.hasReturnRightConnection && 
                                   params.returnRightConnectionType === 'fitting' &&
                                   isFlangeWithSmallerDiameter(params.returnRightConnectionFitting, params.returnPipeDiameter);
    
    // Добавляем левую заглушку/переход (если выступают наружу и нет фланца с меньшим диаметром)
    if (params.hasReturnLeftCap && !hasLeftFlangeSmaller) {
      if (params.returnLeftCapElementType === 'reducer') {
        const reducerData = fittingsData[params.returnLeftReducer];
        if (reducerData) {
          totalLength += reducerData.length;
        }
      } else if (params.returnLeftCapType === 'elliptical') {
        const capKey = `Zaglushka_EL_${params.returnPipeDiameter}`;
        const capData = fittingsData[capKey];
        if (capData) {
          totalLength += capData.length;
        }
      }
    }
    
    // Добавляем правую заглушку/переход
    if (params.hasReturnRightCap && !hasRightFlangeSmaller) {
      if (params.returnRightCapElementType === 'reducer') {
        const reducerData = fittingsData[params.returnRightReducer];
        if (reducerData) {
          totalLength += reducerData.length;
        }
      } else if (params.returnRightCapType === 'elliptical') {
        const capKey = `Zaglushka_EL_${params.returnPipeDiameter}`;
        const capData = fittingsData[capKey];
        if (capData) {
          totalLength += capData.length;
        }
      }
    }
    
    return Math.round(totalLength);
  }, [
    returnCalculatedLength,
    params.hasReturnLeftConnection,
    params.returnLeftConnectionType,
    params.returnLeftConnectionLength,
    params.returnLeftConnectionFitting,
    params.returnLeftConnectionFittingLength,
    params.hasReturnRightConnection,
    params.returnRightConnectionType,
    params.returnRightConnectionLength,
    params.returnRightConnectionFitting,
    params.returnRightConnectionFittingLength,
    params.hasReturnLeftCap,
    params.returnLeftCapElementType,
    params.returnLeftCapType,
    params.returnLeftReducer,
    params.returnPipeDiameter,
    params.hasReturnRightCap,
    params.returnRightCapElementType,
    params.returnRightCapType,
    params.returnRightReducer,
    params.shape,
    getConnectionLengthForUI,
  ]);

  // Вычисляем отступ последнего контура в ручном режиме (обратный расчет)
  const { supplyLastCircuitOffsetCalculated, returnLastCircuitOffsetCalculated } = useMemo(() => {
    if (!params.useManualLength) {
      return {
        supplyLastCircuitOffsetCalculated: params.supplyLastCircuitOffset,
        returnLastCircuitOffsetCalculated: params.returnLastCircuitOffset,
      };
    }
    
    // Для подачи
    let supplyRemainder = params.manualSupplyLength || 150;
    supplyRemainder -= effectiveSupplyFirstCircuitOffset;
    
    supplyCircuits.forEach((circuit, index) => {
      const circuitSpacing = Number(circuit.circuitSpacing) || params.supplyCircuitSpacing || 125;
      supplyRemainder -= circuitSpacing;
      
      if (index < supplyCircuits.length - 1) {
        const spacingToNext = Number(circuit.spacingToNext) || params.supplySpacingBetweenCircuits || 125;
        supplyRemainder -= spacingToNext;
      }
    });
    
    // Для обратки
    let returnRemainder = params.manualReturnLength || 150;
    returnRemainder -= effectiveReturnFirstCircuitOffset;
    
    returnCircuits.forEach((circuit, index) => {
      const circuitSpacing = Number(circuit.circuitSpacing) || params.returnCircuitSpacing || 125;
      returnRemainder -= circuitSpacing;
      
      if (index < returnCircuits.length - 1) {
        const spacingToNext = Number(circuit.spacingToNext) || params.returnSpacingBetweenCircuits || 125;
        returnRemainder -= spacingToNext;
      }
    });
    
    return {
      supplyLastCircuitOffsetCalculated: Math.max(supplyRemainder, 0),
      returnLastCircuitOffsetCalculated: Math.max(returnRemainder, 0),
    };
  }, [
    params.useManualLength,
    params.manualSupplyLength,
    params.manualReturnLength,
    effectiveSupplyFirstCircuitOffset,
    effectiveReturnFirstCircuitOffset,
    params.supplyLastCircuitOffset,
    params.returnLastCircuitOffset,
    params.supplySpacingBetweenCircuits,
    params.returnSpacingBetweenCircuits,
    params.supplyCircuitSpacing,
    params.returnCircuitSpacing,
    supplyCircuits,
    returnCircuits
  ]);


  // Проверка, все ли контуры подачи имеют одинаковое межосевое
  const supplyCircuitSpacingUniform = useMemo(() => {
    if (supplyCircuits.length === 0) return true;
    const firstValue = Number(supplyCircuits[0].circuitSpacing) || params.supplyCircuitSpacing || 125;
    return supplyCircuits.every(circuit => {
      const value = Number(circuit.circuitSpacing) || params.supplyCircuitSpacing || 125;
      return value === firstValue;
    });
  }, [supplyCircuits, params.supplyCircuitSpacing]);

  // Проверка, все ли контуры подачи имеют одинаковое расстояние между ними
  const supplySpacingBetweenUniform = useMemo(() => {
    if (supplyCircuits.length <= 1) return true;
    const relevantCircuits = supplyCircuits.slice(0, -1); // Все кроме последнего
    const firstValue = Number(relevantCircuits[0].spacingToNext) || params.supplySpacingBetweenCircuits || 125;
    return relevantCircuits.every(circuit => {
      const value = Number(circuit.spacingToNext) || params.supplySpacingBetweenCircuits || 125;
      return value === firstValue;
    });
  }, [supplyCircuits, params.supplySpacingBetweenCircuits]);

  // Проверка, все ли контуры обратки имеют одинаковое межосевое
  const returnCircuitSpacingUniform = useMemo(() => {
    if (returnCircuits.length === 0) return true;
    const firstValue = Number(returnCircuits[0].circuitSpacing) || params.returnCircuitSpacing || 125;
    return returnCircuits.every(circuit => {
      const value = Number(circuit.circuitSpacing) || params.returnCircuitSpacing || 125;
      return value === firstValue;
    });
  }, [returnCircuits, params.returnCircuitSpacing]);

  // Проверка, все ли контуры обратки имеют одинаковое расстояние между ними
  const returnSpacingBetweenUniform = useMemo(() => {
    if (returnCircuits.length <= 1) return true;
    const relevantCircuits = returnCircuits.slice(0, -1); // Все кроме последнего
    const firstValue = Number(relevantCircuits[0].spacingToNext) || params.returnSpacingBetweenCircuits || 125;
    return relevantCircuits.every(circuit => {
      const value = Number(circuit.spacingToNext) || params.returnSpacingBetweenCircuits || 125;
      return value === firstValue;
    });
  }, [returnCircuits, params.returnSpacingBetweenCircuits]);

  // Автоматическое отключение режима привязки при удалении всех контуров
  useEffect(() => {
    // Если режим привязки подачи включен и контуров обратки нет
    if (params.supplyFirstCircuitOffsetMode === 'fromReturnCircuit' && returnCircuits.length === 0) {
      // Выключаем режим и восстанавливаем сохранённое значение
      setParams(prev => ({
        ...prev,
        supplyFirstCircuitOffsetMode: 'fromEdge',
        supplyFirstCircuitOffsetLastUsed: prev.supplyFirstCircuitOffset,
        supplyFirstCircuitOffset: prev.supplyFirstCircuitOffsetSaved !== null 
          ? prev.supplyFirstCircuitOffsetSaved 
          : prev.supplyFirstCircuitOffset,
        supplyFirstCircuitOffsetSaved: null
      }));
      setLengthsEqualized(false);
    }
    
    // Если режим привязки обратки включен и контуров подачи нет
    if (params.returnFirstCircuitOffsetMode === 'fromSupplyCircuit' && supplyCircuits.length === 0) {
      // Выключаем режим и восстанавливаем сохранённое значение
      setParams(prev => ({
        ...prev,
        returnFirstCircuitOffsetMode: 'fromEdge',
        returnFirstCircuitOffsetLastUsed: prev.returnFirstCircuitOffset,
        returnFirstCircuitOffset: prev.returnFirstCircuitOffsetSaved !== null 
          ? prev.returnFirstCircuitOffsetSaved 
          : prev.returnFirstCircuitOffset,
        returnFirstCircuitOffsetSaved: null
      }));
      setLengthsEqualized(false);
    }
  }, [
    returnCircuits.length, 
    supplyCircuits.length,
    params.supplyFirstCircuitOffsetMode, 
    params.returnFirstCircuitOffsetMode,
    params.supplyFirstCircuitOffsetSaved, 
    params.supplyFirstCircuitOffset,
    params.returnFirstCircuitOffsetSaved,
    params.returnFirstCircuitOffset
  ]);

  // Обновление параметров
  const updateParam = (keyOrObj, value) => {
    setParams(prev => {
      let updates = {};
      
      if (typeof keyOrObj === 'object') {
        updates = { ...keyOrObj };
        
        // Синхронизация торцевых подключений для объектов
        if (leftConnectionsLinked || rightConnectionsLinked) {
          const syncUpdates = {};
          
          Object.keys(updates).forEach(key => {
            // Левые подключения
            if (leftConnectionsLinked) {
              // Проверяем ключи для подачи (Supply) - все ключи связанные с левым подключением подачи
              if (key.startsWith('supplyLeftConnection') || 
                  key.startsWith('hasSupplyLeftCap') || 
                  key.startsWith('supplyLeftCap') ||
                  key.startsWith('supplyLeftReducer')) {
                const returnKey = key.replace(/supplyLeft/g, 'returnLeft')
                                     .replace(/SupplyLeft/g, 'ReturnLeft');
                syncUpdates[returnKey] = updates[key];
              } 
              // Проверяем ключи для обратки (Return) - все ключи связанные с левым подключением обратки
              else if (key.startsWith('returnLeftConnection') || 
                       key.startsWith('hasReturnLeftCap') || 
                       key.startsWith('returnLeftCap') ||
                       key.startsWith('returnLeftReducer')) {
                const supplyKey = key.replace(/returnLeft/g, 'supplyLeft')
                                     .replace(/ReturnLeft/g, 'SupplyLeft');
                syncUpdates[supplyKey] = updates[key];
              }
              if (key === 'hasSupplyLeftConnection') {
                syncUpdates.hasReturnLeftConnection = updates[key];
              } else if (key === 'hasReturnLeftConnection') {
                syncUpdates.hasSupplyLeftConnection = updates[key];
              }
            }
            
            // Правые подключения
            if (rightConnectionsLinked) {
              // Проверяем ключи для подачи (Supply) - все ключи связанные с правым подключением подачи
              if (key.startsWith('supplyRightConnection') || 
                  key.startsWith('hasSupplyRightCap') || 
                  key.startsWith('supplyRightCap') ||
                  key.startsWith('supplyRightReducer')) {
                const returnKey = key.replace(/supplyRight/g, 'returnRight')
                                     .replace(/SupplyRight/g, 'ReturnRight');
                syncUpdates[returnKey] = updates[key];
              } 
              // Проверяем ключи для обратки (Return) - все ключи связанные с правым подключением обратки
              else if (key.startsWith('returnRightConnection') || 
                       key.startsWith('hasReturnRightCap') || 
                       key.startsWith('returnRightCap') ||
                       key.startsWith('returnRightReducer')) {
                const supplyKey = key.replace(/returnRight/g, 'supplyRight')
                                     .replace(/ReturnRight/g, 'SupplyRight');
                syncUpdates[supplyKey] = updates[key];
              }
              if (key === 'hasSupplyRightConnection') {
                syncUpdates.hasReturnRightConnection = updates[key];
              } else if (key === 'hasReturnRightConnection') {
                syncUpdates.hasSupplyRightConnection = updates[key];
              }
            }
          });
          
          updates = { ...updates, ...syncUpdates };
        }
      } else {
      // Не конвертируем boolean в number
      if (typeof value === 'boolean') {
          updates[keyOrObj] = value;
        } else {
      const numValue = Number(value);
      const finalValue = !isNaN(numValue) && value !== '' ? numValue : value;
          updates[keyOrObj] = finalValue;
        }
        
        // Синхронизация торцевых подключений
        // Левые подключения
        if (leftConnectionsLinked) {
          if (keyOrObj.startsWith('supplyLeftConnection') || 
              keyOrObj.startsWith('hasSupplyLeftCap') || 
              keyOrObj.startsWith('supplyLeftCap') ||
              keyOrObj.startsWith('supplyLeftReducer')) {
            const returnKey = keyOrObj.replace(/supplyLeft/g, 'returnLeft')
                                      .replace(/SupplyLeft/g, 'ReturnLeft');
            updates[returnKey] = updates[keyOrObj];
          } else if (keyOrObj.startsWith('returnLeftConnection') || 
                     keyOrObj.startsWith('hasReturnLeftCap') || 
                     keyOrObj.startsWith('returnLeftCap') ||
                     keyOrObj.startsWith('returnLeftReducer')) {
            const supplyKey = keyOrObj.replace(/returnLeft/g, 'supplyLeft')
                                      .replace(/ReturnLeft/g, 'SupplyLeft');
            updates[supplyKey] = updates[keyOrObj];
          }
          if (keyOrObj === 'hasSupplyLeftConnection') {
            updates.hasReturnLeftConnection = updates[keyOrObj];
          } else if (keyOrObj === 'hasReturnLeftConnection') {
            updates.hasSupplyLeftConnection = updates[keyOrObj];
          }
        }
        
        // Правые подключения
        if (rightConnectionsLinked) {
          if (keyOrObj.startsWith('supplyRightConnection') || 
              keyOrObj.startsWith('hasSupplyRightCap') || 
              keyOrObj.startsWith('supplyRightCap') ||
              keyOrObj.startsWith('supplyRightReducer')) {
            const returnKey = keyOrObj.replace(/supplyRight/g, 'returnRight')
                                      .replace(/SupplyRight/g, 'ReturnRight');
            updates[returnKey] = updates[keyOrObj];
          } else if (keyOrObj.startsWith('returnRightConnection') || 
                     keyOrObj.startsWith('hasReturnRightCap') || 
                     keyOrObj.startsWith('returnRightCap') ||
                     keyOrObj.startsWith('returnRightReducer')) {
            const supplyKey = keyOrObj.replace(/returnRight/g, 'supplyRight')
                                      .replace(/ReturnRight/g, 'SupplyRight');
            updates[supplyKey] = updates[keyOrObj];
          }
          if (keyOrObj === 'hasSupplyRightConnection') {
            updates.hasReturnRightConnection = updates[keyOrObj];
          } else if (keyOrObj === 'hasReturnRightConnection') {
            updates.hasSupplyRightConnection = updates[keyOrObj];
          }
        }
      }
      
      return { ...prev, ...updates };
    });
  };

  // В компактном режиме счётчики в params и массивы фитингов/доп. выходов должны совпадать.
  // После updateParam батч иногда оставлял старые массивы (термометры/выходы всё ещё на схеме).
  useLayoutEffect(() => {
    if (!params.isCompact) return;
    const clamp = (count, setFn) => {
      setFn((prev) => {
        if (count === 0 && prev.length > 0) return [];
        if (count > 0 && prev.length > count) return prev.slice(0, count);
        return prev;
      });
    };
    clamp(params.supplyCenterFittingCount, setSupplyCenterFittings);
    clamp(params.returnCenterFittingCount, setReturnCenterFittings);
    clamp(params.supplyAdditionalOutletCount, setSupplyAdditionalOutlets);
    clamp(params.returnAdditionalOutletCount, setReturnAdditionalOutlets);
  }, [
    params.isCompact,
    params.supplyCenterFittingCount,
    params.returnCenterFittingCount,
    params.supplyAdditionalOutletCount,
    params.returnAdditionalOutletCount,
  ]);
  
  // Убираем обёртку updateParamWithSync, она больше не нужна

  // Функция приравнивания длин коллекторов
  const equalizeLengths = () => {
    const maxLength = Math.max(supplyCalculatedLength, returnCalculatedLength);
    
    if (params.useManualLength) {
      // В ручном режиме просто устанавливаем обе длины равными максимальной
      updateParam('manualSupplyLength', Number(maxLength.toFixed(2)));
      updateParam('manualReturnLength', Number(maxLength.toFixed(2)));
    } else {
      // В автоматическом режиме корректируем отступы
      if (supplyCalculatedLength < maxLength) {
        // Подача короче - увеличиваем её lastCircuitOffset
        const difference = maxLength - supplyCalculatedLength;
        updateParam('supplyLastCircuitOffset', params.supplyLastCircuitOffset + difference);
      } else if (returnCalculatedLength < maxLength) {
        // Обратка короче - увеличиваем её lastCircuitOffset
        const difference = maxLength - returnCalculatedLength;
        updateParam('returnLastCircuitOffset', params.returnLastCircuitOffset + difference);
      }
    }
    
    setLengthsEqualized(true);
  };
  
  // Обработчики изменения длины коллекторов
  // Обновление контура подачи
  const updateSupplyCircuit = (index, keyOrObj, value) => {
    setLengthsEqualized(false); // Сбрасываем флаг при изменении
    setSupplyCircuits(prev => {
      const newCircuits = [...prev];
      if (typeof keyOrObj === 'object') {
        // Если передан объект, объединяем его с текущим контуром
        newCircuits[index] = { ...newCircuits[index], ...keyOrObj };
      } else {
        // Если передан ключ и значение
        newCircuits[index] = { ...newCircuits[index], [keyOrObj]: value };
      }
      return newCircuits;
    });
  };

  // Обновление контура обратки
  const updateReturnCircuit = (index, keyOrObj, value) => {
    setLengthsEqualized(false); // Сбрасываем флаг при изменении
    setReturnCircuits(prev => {
      const newCircuits = [...prev];
      if (typeof keyOrObj === 'object') {
        // Если передан объект, объединяем его с текущим контуром
        newCircuits[index] = { ...newCircuits[index], ...keyOrObj };
      } else {
        // Если передан ключ и значение
        newCircuits[index] = { ...newCircuits[index], [keyOrObj]: value };
      }
      return newCircuits;
    });
  };

  // Подбирает фитинг для нового контура: если последний контур имеет совместимый фитинг — берём его, иначе максимально совместимый
  const getDefaultFittingForCircuit = (existingCircuits, diameter) => {
    const lastFitting = existingCircuits.length > 0
      ? (existingCircuits[existingCircuits.length - 1].fittingId || existingCircuits[existingCircuits.length - 1].fitting)
      : null;
    if (lastFitting && isCircuitFittingCompatible(lastFitting, diameter)) return lastFitting;
    const fittings = getFittingsByCategory('Ниппели', diameter, isCircuitFittingCompatible);
    const compatible = fittings.filter(f => f.isCompatible);
    if (compatible.length > 0) return compatible[compatible.length - 1].id;
    return 'Nippel_1';
  };

  // Добавление контура подачи
  const addSupplyCircuit = () => {
    setLengthsEqualized(false);
    setSupplyCircuits(prev => {
      const defaultFitting = getDefaultFittingForCircuit(prev, params.supplyPipeDiameter);
      return [
        ...prev,
        {
          id: `supply-${Date.now()}`,
          fitting: defaultFitting,
          fittingLength: null,
          flangePipeLength: 50,
          spacingToNext: null,
          circuitSpacing: params.supplyCircuitSpacing || 50,
          swapOrder: false,
        },
      ];
    });
  };

  // Добавление контура обратки
  const addReturnCircuit = () => {
    setLengthsEqualized(false);
    setReturnCircuits(prev => {
      const defaultFitting = getDefaultFittingForCircuit(prev, params.returnPipeDiameter);
      return [
        ...prev,
        {
          id: `return-${Date.now()}`,
          fitting: defaultFitting,
          fittingLength: null,
          flangePipeLength: 50,
          spacingToNext: null,
          circuitSpacing: params.returnCircuitSpacing || 50,
          swapOrder: false,
        },
      ];
    });
  };

  // Удаление контура подачи
  const deleteSupplyCircuit = (index) => {
      setLengthsEqualized(false); // Сбрасываем флаг при удалении контура
      setSupplyCircuits(prev => prev.filter((_, i) => i !== index));
  };

  // Удаление контура обратки
  const deleteReturnCircuit = (index) => {
      setLengthsEqualized(false); // Сбрасываем флаг при удалении контура
      setReturnCircuits(prev => prev.filter((_, i) => i !== index));
  };

  // Перемещение контура подачи
  const moveSupplyCircuit = (fromIndex, toIndex) => {
    setLengthsEqualized(false);
    setSupplyCircuits(prev => {
      const newCircuits = [...prev];
      const [movedItem] = newCircuits.splice(fromIndex, 1);
      newCircuits.splice(toIndex, 0, movedItem);
      return newCircuits;
    });
  };

  // Перемещение контура обратки
  const moveReturnCircuit = (fromIndex, toIndex) => {
    setLengthsEqualized(false);
    setReturnCircuits(prev => {
      const newCircuits = [...prev];
      const [movedItem] = newCircuits.splice(fromIndex, 1);
      newCircuits.splice(toIndex, 0, movedItem);
      return newCircuits;
    });
  };

  // === Функции для центральных фитингов подающего коллектора ===
  
  const updateSupplyCenterFittingCount = (delta) => {
    const newCount = Math.max(0, params.supplyCenterFittingCount + delta);
    setParams((prev) => ({ ...prev, supplyCenterFittingCount: newCount }));
    
    if (delta > 0) {
      setSupplyCenterFittings((prev) => [
        ...prev,
        ...Array(delta).fill(null).map((_, i) => ({
          id: prev.length + i + 1,
          fittingCategory: 'Муфты',
          fittingId: 'Mufta_1.2', // 1/2"
          positionType: 'center', // 'distance', 'center', 'between'
          side: 'both', // 'both', 'front', 'back'
          distanceFromStart: 50,
          distanceDirection: 'left', // 'left' (слева) или 'right' (справа)
          betweenCircuits: '1-2',
          referenceRow: 'supply', // 'supply' или 'return' - для распределительного коллектора
        })),
      ]);
    } else {
      setSupplyCenterFittings((prev) => prev.slice(0, newCount));
    }
  };
  
  const updateSupplyCenterFitting = (index, keyOrObj, value) => {
    setSupplyCenterFittings((prev) =>
      prev.map((f, i) => {
        if (i !== index) return f;
        if (typeof keyOrObj === 'object') {
          return { ...f, ...keyOrObj };
        }
        return { ...f, [keyOrObj]: typeof value === 'string' ? value : Number(value) };
      })
    );
  };
  
  const deleteSupplyCenterFitting = (index) => {
    setSupplyCenterFittings((prev) => {
      const newFittings = prev.filter((_, i) => i !== index);
      return newFittings.map((f, i) => ({ ...f, id: i + 1 }));
    });
    setParams((prev) => ({ ...prev, supplyCenterFittingCount: Math.max(0, prev.supplyCenterFittingCount - 1) }));
  };

  // === Функции для центральных фитингов обратного коллектора ===
  
  const updateReturnCenterFittingCount = (delta) => {
    const newCount = Math.max(0, params.returnCenterFittingCount + delta);
    setParams((prev) => ({ ...prev, returnCenterFittingCount: newCount }));
    
    if (delta > 0) {
      setReturnCenterFittings((prev) => [
        ...prev,
        ...Array(delta).fill(null).map((_, i) => ({
          id: prev.length + i + 1,
          fittingCategory: 'Муфты',
          fittingId: 'Mufta_1.2', // 1/2"
          positionType: 'center', // 'distance', 'center', 'between'
          side: 'both', // 'both', 'front', 'back'
          distanceFromStart: 50,
          distanceDirection: 'left', // 'left' (слева) или 'right' (справа)
          betweenCircuits: '1-2',
          referenceRow: 'return', // 'supply' или 'return' - для распределительного коллектора
        })),
      ]);
    } else {
      setReturnCenterFittings((prev) => prev.slice(0, newCount));
    }
  };
  
  const updateReturnCenterFitting = (index, keyOrObj, value) => {
    setReturnCenterFittings((prev) =>
      prev.map((f, i) => {
        if (i !== index) return f;
        if (typeof keyOrObj === 'object') {
          return { ...f, ...keyOrObj };
        }
        return { ...f, [keyOrObj]: typeof value === 'string' ? value : Number(value) };
      })
    );
  };
  
  const deleteReturnCenterFitting = (index) => {
    setReturnCenterFittings((prev) => {
      const newFittings = prev.filter((_, i) => i !== index);
      return newFittings.map((f, i) => ({ ...f, id: i + 1 }));
    });
    setParams((prev) => ({ ...prev, returnCenterFittingCount: Math.max(0, prev.returnCenterFittingCount - 1) }));
  };

  // === Функции для дополнительных выходов подающего коллектора ===
  
  const updateSupplyAdditionalOutletCount = (delta) => {
    const newCount = Math.max(0, params.supplyAdditionalOutletCount + delta);
    setParams((prev) => ({ ...prev, supplyAdditionalOutletCount: newCount }));
    
    if (delta > 0) {
      setSupplyAdditionalOutlets((prev) => [
        ...prev,
        ...Array(delta).fill(null).map((_, i) => ({
          id: prev.length + i + 1,
          type: 'fitting',
          diameter: 26.9,
          length: 80,
          fittingCategory: 'Ниппели',
          fittingId: 'Nippel_3.4',
          fittingLength: null,
          flangePipeLength: null,
          direction: 'up', // направление по умолчанию вверх для подающего коллектора
          positionType: 'center', // по умолчанию в центре
          distanceFromEdge: 60,
          distanceDirection: 'left',
          betweenIndex: 0,
          referenceRow: 'supply', // 'supply' или 'return' - для распределительного коллектора
          outletType: 'short', // тип выхода: 'short' (короткий) или 'through' (сквозной)
        })),
      ]);
    } else {
      setSupplyAdditionalOutlets((prev) => prev.slice(0, newCount));
    }
  };
  
  const updateSupplyAdditionalOutlet = (index, key, value) => {
    setSupplyAdditionalOutlets(prev => {
      const newOutlets = [...prev];
      newOutlets[index] = { ...newOutlets[index], [key]: value };
      return newOutlets;
    });
  };
  
  const deleteSupplyAdditionalOutlet = (index) => {
    setSupplyAdditionalOutlets((prev) => {
      const newOutlets = prev.filter((_, i) => i !== index);
      return newOutlets.map((f, i) => ({ ...f, id: i + 1 }));
    });
    setParams((prev) => ({ ...prev, supplyAdditionalOutletCount: Math.max(0, prev.supplyAdditionalOutletCount - 1) }));
  };

  // === Функции для дополнительных выходов обратного коллектора ===
  
  const updateReturnAdditionalOutletCount = (delta) => {
    const newCount = Math.max(0, params.returnAdditionalOutletCount + delta);
    setParams((prev) => ({ ...prev, returnAdditionalOutletCount: newCount }));
    
    if (delta > 0) {
      setReturnAdditionalOutlets((prev) => [
        ...prev,
        ...Array(delta).fill(null).map((_, i) => ({
          id: prev.length + i + 1,
          type: 'fitting',
          diameter: 26.9,
          length: 80,
          fittingCategory: 'Ниппели',
          fittingId: 'Nippel_3.4',
          fittingLength: null,
          flangePipeLength: null,
          direction: 'down', // направление по умолчанию вниз для обратного коллектора
          positionType: 'center', // по умолчанию в центре
          distanceFromEdge: 60,
          distanceDirection: 'left',
          betweenIndex: 0,
          referenceRow: 'return', // 'supply' или 'return' - для распределительного коллектора
          outletType: 'short', // тип выхода: 'short' (короткий) или 'through' (сквозной)
        })),
      ]);
    } else {
      setReturnAdditionalOutlets((prev) => prev.slice(0, newCount));
    }
  };
  
  const updateReturnAdditionalOutlet = (index, key, value) => {
    setReturnAdditionalOutlets(prev => {
      const newOutlets = [...prev];
      newOutlets[index] = { ...newOutlets[index], [key]: value };
      return newOutlets;
    });
  };
  
  const deleteReturnAdditionalOutlet = (index) => {
    setReturnAdditionalOutlets((prev) => {
      const newOutlets = prev.filter((_, i) => i !== index);
      return newOutlets.map((f, i) => ({ ...f, id: i + 1 }));
    });
    setParams((prev) => ({ ...prev, returnAdditionalOutletCount: Math.max(0, prev.returnAdditionalOutletCount - 1) }));
  };

  // Генерация коллектора
  const handleGenerate = () => {
    // Проверяем, нужно ли предложить приравнять длины
    if (!lengthsEqualized && supplyCalculatedLength !== returnCalculatedLength) {
      setEqualizeDialog(true);
      return;
    }
    
    // Если включен ручной режим, используем вычисленные значения отступов
    const effectiveParams = params.useManualLength ? {
      ...params,
      supplyLastCircuitOffset: supplyLastCircuitOffsetCalculated,
      returnLastCircuitOffset: returnLastCircuitOffsetCalculated,
    } : params;
    
    // Генерируем
    const drawing = generateDistributionCollector(
      effectiveParams,
      supplyCircuits,
      returnCircuits,
      supplyCenterFittings,
      returnCenterFittings,
      supplyCalculatedLength,
      returnCalculatedLength,
      supplyAdditionalOutlets,
      returnAdditionalOutlets,
      Math.max(supplyTotalLength, returnTotalLength), // Передаем общую длину системы
      effectiveSupplyFirstCircuitOffset, // Передаём эффективный отступ подачи
      effectiveReturnFirstCircuitOffset // Передаём эффективный отступ обратки
    );
    // Передаём параметры для возможности вернуться к редактору
    onGenerate(drawing, { 
      params, 
      supplyCircuits, 
      returnCircuits, 
      supplyCenterFittings, 
      returnCenterFittings,
      supplyAdditionalOutlets,
      returnAdditionalOutlets,
      activeTab // Сохраняем активную вкладку
    });
  };
  
  // Генерация с приравниванием длин
  const handleGenerateWithEqualize = () => {
    setEqualizeDialog(false);
    equalizeLengths();
    // Генерируем после небольшой задержки, чтобы состояние успело обновиться
    setTimeout(() => {
      // Если включен ручной режим, используем вычисленные значения отступов
      const effectiveParams = params.useManualLength ? {
        ...params,
        supplyLastCircuitOffset: supplyLastCircuitOffsetCalculated,
        returnLastCircuitOffset: returnLastCircuitOffsetCalculated,
      } : params;
      
      const drawing = generateDistributionCollector(
        effectiveParams,
        supplyCircuits,
        returnCircuits,
        supplyCenterFittings,
        returnCenterFittings,
        Math.max(supplyCalculatedLength, returnCalculatedLength),
        Math.max(supplyCalculatedLength, returnCalculatedLength),
        supplyAdditionalOutlets,
        returnAdditionalOutlets,
        Math.max(supplyTotalLength, returnTotalLength), // Передаем общую длину системы
        effectiveSupplyFirstCircuitOffset, // Передаём эффективный отступ подачи
        effectiveReturnFirstCircuitOffset // Передаём эффективный отступ обратки
      );
      onGenerate(drawing, { 
        params, 
        supplyCircuits, 
        returnCircuits, 
        supplyCenterFittings, 
        returnCenterFittings,
        supplyAdditionalOutlets,
        returnAdditionalOutlets,
        activeTab // Сохраняем активную вкладку
      });
    }, 0);
  };
  
  // Генерация без приравнивания
  const handleGenerateWithoutEqualize = () => {
    setEqualizeDialog(false);
    setLengthsEqualized(true); // Устанавливаем флаг, чтобы не показывать диалог снова
    
    // НЕ вызываем equalizeLengths() - просто генерируем как есть
    // Если включен ручной режим, используем вычисленные значения отступов
    const effectiveParams = params.useManualLength ? {
      ...params,
      supplyLastCircuitOffset: supplyLastCircuitOffsetCalculated,
      returnLastCircuitOffset: returnLastCircuitOffsetCalculated,
    } : params;
    
    const drawing = generateDistributionCollector(
      effectiveParams,
      supplyCircuits,
      returnCircuits,
      supplyCenterFittings,
      returnCenterFittings,
      supplyCalculatedLength,
      returnCalculatedLength,
      supplyAdditionalOutlets,
      returnAdditionalOutlets,
      Math.max(supplyTotalLength, returnTotalLength), // Передаем общую длину системы
      effectiveSupplyFirstCircuitOffset, // Передаём эффективный отступ подачи
      effectiveReturnFirstCircuitOffset // Передаём эффективный отступ обратки
    );
    onGenerate(drawing, { 
      params, 
      supplyCircuits, 
      returnCircuits, 
      supplyCenterFittings, 
      returnCenterFittings,
      supplyAdditionalOutlets,
      returnAdditionalOutlets,
      activeTab // Сохраняем активную вкладку
    });
  };

  // Получение фитингов для контуров с проверкой совместимости
  const getSupplyFittings = (category) => {
    if (params.shape === 'square') {
      // Для квадратных профилей используем специальную проверку
      // В компактном режиме используем compactSize для проверки совместимости
      const profileToCheck = params.isCompact ? params.compactSize : params.supplyPipeProfile;
      return getFittingsByCategory(category, null, null).map(f => ({
        ...f,
        isCompatible: isCircuitFittingCompatibleWithSquareProfile(f.id, profileToCheck)
      }));
    }
    return getFittingsByCategory(category, params.supplyPipeDiameter, isCircuitFittingCompatible);
  };

  const getReturnFittings = (category) => {
    if (params.shape === 'square') {
      // Для квадратных профилей используем специальную проверку
      // В компактном режиме используем compactSize для проверки совместимости
      const profileToCheck = params.isCompact ? params.compactSize : params.returnPipeProfile;
      return getFittingsByCategory(category, null, null).map(f => ({
        ...f,
        isCompatible: isCircuitFittingCompatibleWithSquareProfile(f.id, profileToCheck)
      }));
    }
    return getFittingsByCategory(category, params.returnPipeDiameter, isCircuitFittingCompatible);
  };

  // Получение фитингов для концевых подключений
  // (будет использоваться в UI для выбора фитингов подключений)
  // const getSupplyEndFittings = (category) => {
  //   return getFittingsByCategory(category, params.supplyPipeDiameter, isEndFittingCompatibleWithMainPipe);
  // };
  // const getReturnEndFittings = (category) => {
  //   return getFittingsByCategory(category, params.returnPipeDiameter, isEndFittingCompatibleWithMainPipe);
  // };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)',
        py: 3,
        overflow: 'visible',
      }}
    >
      <Container maxWidth="xl" sx={{ overflow: 'visible' }}>
        {/* Заголовок */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 1, mr: 2 }}>
          <IconButton
            onClick={onBack}
            sx={{
              bgcolor: 'white',
              boxShadow: 1,
              '&:hover': { bgcolor: 'grey.100' },
            }}
          >
            <HomeIcon />
          </IconButton>
          {onBackToCharacteristics && (
            <IconButton
              onClick={onBackToCharacteristics}
              sx={{
                bgcolor: 'white',
                boxShadow: 1,
                '&:hover': { bgcolor: 'grey.100' },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
          )}
          {onBackToDrawing && (
            <IconButton
              onClick={onBackToDrawing}
              sx={{
                bgcolor: 'white',
                boxShadow: 1,
                '&:hover': { bgcolor: 'grey.100' },
              }}
            >
              <ArrowForwardIcon />
            </IconButton>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h4">Распределительный коллектор</Typography>
          {params._calculatedFrom === 'characteristics' && (
            <Chip
              label="Рассчитано из характеристик"
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
          <ToggleButtonGroup
            value={params.shape}
            exclusive
            onChange={(e, newShape) => {
              if (newShape !== null) {
                const updates = { shape: newShape };
                
                // При переключении на круглый сбрасываем компактный режим и снимок для отката
                if (newShape === 'round') {
                  updates.isCompact = false;
                  preCompactUndoRef.current = null;
                  updates.preCompactUndoSnapshot = null;
                }
                
                // При переключении на квадратный устанавливаем профили
                if (newShape === 'square') {
                  // Сохраняем текущие типы заглушек перед переключением на квадратную форму
                  setSavedCapTypes({
                    supplyLeftCapType: params.supplyLeftCapType,
                    supplyRightCapType: params.supplyRightCapType,
                    returnLeftCapType: params.returnLeftCapType,
                    returnRightCapType: params.returnRightCapType,
                  });
                  
                  // Гарантируем установку профилей (используем существующие или дефолтные)
                  updates.supplyPipeProfile = params.supplyPipeProfile || '80x80';
                  updates.returnPipeProfile = params.returnPipeProfile || '80x80';
                  // Для квадратных только прямые заглушки
                  updates.supplyLeftCapType = 'straight';
                  updates.supplyRightCapType = 'straight';
                  updates.returnLeftCapType = 'straight';
                  updates.returnRightCapType = 'straight';
                  updates.supplyLeftCapElementType = 'cap';
                  updates.supplyRightCapElementType = 'cap';
                  updates.returnLeftCapElementType = 'cap';
                  updates.returnRightCapElementType = 'cap';
                  updates.hasSupplyLeftCap = true;
                  updates.hasSupplyRightCap = true;
                  updates.hasReturnLeftCap = true;
                  updates.hasReturnRightCap = true;
                  
                  // Восстанавливаем состояние компактного режима, если оно было сохранено
                  if (params.wasCompactBeforeRound) {
                    updates.isCompact = true;
                    Object.assign(updates, COMPACT_MODE_ENTRY_PARAM_RESET);
                    // Применяем настройки компактного режима
                    const compactSize = params.compactSize || '80x80';
                    const mapping = compactSizeToProfilesMap[compactSize];
                    if (mapping) {
                      const sheetProfile = mapping.sheet;
                      const spacingValue = parseInt(sheetProfile.split('x')[1]);
                      updates.compactSize = compactSize;
                      updates.supplyPipeProfile = mapping.sheet;
                      updates.returnPipeProfile = mapping.profile;
                      updates.pipeSpacing = spacingValue;
                    }
                    const compactEdge = getCompactModeFirstLastOffset(compactSize);
                    updates.supplyFirstCircuitOffset = compactEdge;
                    updates.supplyLastCircuitOffset = compactEdge;
                    updates.returnFirstCircuitOffset = compactEdge;
                    updates.returnLastCircuitOffset = compactEdge;
                    updates.wasCompactBeforeRound = false; // Сбрасываем флаг
                  }
                  
                  // Сбрасываем fittingLength для всех контуров, чтобы применились дефолтные значения для квадратных
                  setSupplyCircuits(prev => prev.map(circuit => ({
                    ...circuit,
                    fittingLength: null,
                  })));
                  setReturnCircuits(prev => prev.map(circuit => ({
                    ...circuit,
                    fittingLength: null,
                  })));
                } else if (newShape === 'round') {
                  // При переходе на круглую форму
                  // Восстанавливаем сохраненные типы заглушек, если они были сохранены
                  if (savedCapTypes) {
                    updates.supplyLeftCapType = savedCapTypes.supplyLeftCapType;
                    updates.supplyRightCapType = savedCapTypes.supplyRightCapType;
                    updates.returnLeftCapType = savedCapTypes.returnLeftCapType;
                    updates.returnRightCapType = savedCapTypes.returnRightCapType;
                    
                    // Очищаем сохраненные значения
                    setSavedCapTypes(null);
                  }
                  
                  // Если был включен компактный режим, сохраняем это состояние и отключаем его
                  if (params.isCompact) {
                    updates.wasCompactBeforeRound = true; // Сохраняем флаг, что компактный был включен
                    updates.isCompact = false;
                    updates.pipeSpacing = params.previousPipeSpacing || 200;
                  }
                }
                
                updateParam(updates);
              }
            }}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                '&.Mui-selected': {
                  bgcolor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                  },
                },
              },
            }}
          >
            <ToggleButton value="round" aria-label="круглая форма">
              <Tooltip title="Круглая форма">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <RadioButtonUncheckedIcon fontSize="small" />
                  <Typography variant="body2">Круглый</Typography>
                </Box>
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="square" aria-label="квадратная форма">
              <Tooltip title="Квадратная форма">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CropSquareIcon fontSize="small" />
                  <Typography variant="body2">Квадратный</Typography>
                </Box>
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
          {params.shape === 'square' && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={params.isCompact || false}
                  onChange={(e) => {
                    const isCompact = e.target.checked;
                    const updates = { isCompact };
                    
                    if (isCompact) {
                      const snap = {
                        paramSlice: Object.fromEntries(
                          COMPACT_MODE_SNAPSHOT_PARAM_KEYS.map((k) => [k, params[k]])
                        ),
                        supplyPipeProfile: params.supplyPipeProfile,
                        returnPipeProfile: params.returnPipeProfile,
                        compactSize: params.compactSize ?? '80x80',
                        pipeSpacing: params.pipeSpacing,
                        previousPipeSpacing: params.previousPipeSpacing,
                        supplyFirstCircuitOffset: params.supplyFirstCircuitOffset,
                        supplyLastCircuitOffset: params.supplyLastCircuitOffset,
                        returnFirstCircuitOffset: params.returnFirstCircuitOffset,
                        returnLastCircuitOffset: params.returnLastCircuitOffset,
                        supplyCenterFittings: deepCloneJson(supplyCenterFittings),
                        returnCenterFittings: deepCloneJson(returnCenterFittings),
                        supplyAdditionalOutlets: deepCloneJson(supplyAdditionalOutlets),
                        returnAdditionalOutlets: deepCloneJson(returnAdditionalOutlets),
                      };
                      preCompactUndoRef.current = snap;
                      // В params — чтобы снимок сохранялся в autoConstructorParams при генерации чертежа
                      updates.preCompactUndoSnapshot = deepCloneJson(snap);
                      // Сохраняем текущее межосевое в params перед входом в компактный режим
                      updates.previousPipeSpacing = params.pipeSpacing;
                      // Не тащить в компактный режим торцевые подключения / термометры / доп. выходы с расчёта характеристик
                      Object.assign(updates, COMPACT_MODE_ENTRY_PARAM_RESET);
                      
                      // При включении компактного режима устанавливаем размеры из compactSize
                      const compactSize = params.compactSize || '80x80';
                      const mapping = compactSizeToProfilesMap[compactSize];
                      
                      if (mapping) {
                        // Межосевое = высота листа (второй размер)
                        // 60x30 -> 30, 80x40 -> 40
                        const sheetProfile = mapping.sheet; // '60x30' или '80x40'
                        const spacingValue = parseInt(sheetProfile.split('x')[1]); // Берем второй размер (высоту)
                        
                        updates.compactSize = compactSize;
                        updates.supplyPipeProfile = mapping.sheet;
                        updates.returnPipeProfile = mapping.profile;
                        updates.pipeSpacing = spacingValue; // Устанавливаем межосевое = высоте листа
                      }
                      const compactEdge = getCompactModeFirstLastOffset(compactSize);
                      updates.supplyFirstCircuitOffset = compactEdge;
                      updates.supplyLastCircuitOffset = compactEdge;
                      updates.returnFirstCircuitOffset = compactEdge;
                      updates.returnLastCircuitOffset = compactEdge;
                    } else {
                      const snap = preCompactUndoRef.current || params.preCompactUndoSnapshot;
                      if (snap) {
                        Object.assign(updates, snap.paramSlice);
                        updates.supplyPipeProfile = snap.supplyPipeProfile;
                        updates.returnPipeProfile = snap.returnPipeProfile;
                        updates.compactSize = snap.compactSize;
                        updates.pipeSpacing = snap.pipeSpacing;
                        updates.previousPipeSpacing = snap.previousPipeSpacing;
                        if ('supplyFirstCircuitOffset' in snap) {
                          updates.supplyFirstCircuitOffset = snap.supplyFirstCircuitOffset;
                          updates.supplyLastCircuitOffset = snap.supplyLastCircuitOffset;
                          updates.returnFirstCircuitOffset = snap.returnFirstCircuitOffset;
                          updates.returnLastCircuitOffset = snap.returnLastCircuitOffset;
                        }
                        setSupplyCenterFittings(snap.supplyCenterFittings);
                        setReturnCenterFittings(snap.returnCenterFittings);
                        setSupplyAdditionalOutlets(snap.supplyAdditionalOutlets);
                        setReturnAdditionalOutlets(snap.returnAdditionalOutlets);
                        preCompactUndoRef.current = null;
                        updates.preCompactUndoSnapshot = null;
                      } else {
                        // При отключении без снимка (например, загрузка уже в компактном) — прежняя логика
                        const availableProfileValues = availableSquareProfiles.map(p => p.value);
                        if (!availableProfileValues.includes(params.supplyPipeProfile)) {
                          updates.supplyPipeProfile = '80x80';
                        }
                        if (!availableProfileValues.includes(params.returnPipeProfile)) {
                          updates.returnPipeProfile = '80x80';
                        }
                        updates.pipeSpacing = params.previousPipeSpacing || 200;
                        updates.preCompactUndoSnapshot = null;
                      }
                    }
                    
                    updateParam(updates);
                  }}
                  size="small"
                />
              }
              label={
                <Typography variant="body2">Компактный</Typography>
              }
              sx={{ ml: 1 }}
            />
          )}
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="contained"
          color="primary"
          startIcon={<PlayArrowIcon />}
          onClick={handleGenerate}
          size="large"
        >
          Сгенерировать
        </Button>
      </Box>

      {/* Схема-превью (между заголовком и вкладками) */}
      <Paper 
        sx={{ 
          p: 1.5, 
          mb: 2, 
          bgcolor: 'grey.50',
          position: 'sticky',
          top: 0,
          zIndex: 100,
          boxShadow: '0 0.125rem 0.5rem rgba(0,0,0,0.1)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 75,
            bgcolor: 'white',
            borderRadius: 1,
            border: '0.0625rem solid',
            borderColor: 'grey.200',
          }}
          key={`schematic-${supplyCircuits.map(c => c.swapOrder ? '1' : '0').join('')}-${returnCircuits.map(c => c.swapOrder ? '1' : '0').join('')}-${supplyCenterFittings.map(f => `${f.positionType}-${f.betweenIndex}-${f.distanceFromStart}-${f.distanceDirection}`).join('-')}-${returnCenterFittings.map(f => `${f.positionType}-${f.betweenIndex}-${f.distanceFromStart}-${f.distanceDirection}`).join('-')}`}
        >
          {(() => {
            const svgWidth = 600;
            const svgHeight = 75;
            const padding = 50;
            const verticalSpacing = 22;
            
            // Используем большую длину для масштаба и отображения
            const maxLength = Math.max(supplyCalculatedLength, returnCalculatedLength, 200);
            const scale = Math.min((svgWidth - padding * 2) / maxLength, 0.8);
            
            const supplyY = svgHeight / 2 - verticalSpacing / 2;
            const returnY = svgHeight / 2 + verticalSpacing / 2;
            
            // Обе гребенки отображаем одинаковой длины (по максимальной)
            const supplyStartX = padding;
            const supplyEndX = supplyStartX + maxLength * scale;
            const returnStartX = padding;
            const returnEndX = returnStartX + maxLength * scale;
            
            // Позиции контуров подачи (с межосевым расстоянием)
            let supplyCurrentX = effectiveSupplyFirstCircuitOffset;
            const supplyPositions = supplyCircuits.map((circuit, i) => {
              const circuitSpacing = Number(circuit.circuitSpacing) || params.supplyCircuitSpacing || 125;
              const pos = { start: supplyStartX + supplyCurrentX * scale, spacing: circuitSpacing * scale };
              if (i < supplyCircuits.length - 1) {
                const spacing = Number(circuit.spacingToNext) || params.supplySpacingBetweenCircuits || 125;
                supplyCurrentX += circuitSpacing + spacing;
              }
              return pos;
            });
            
            // Позиции контуров обратки (с межосевым расстоянием)
            let returnCurrentX = effectiveReturnFirstCircuitOffset;
            const returnPositions = returnCircuits.map((circuit, i) => {
              const circuitSpacing = Number(circuit.circuitSpacing) || params.returnCircuitSpacing || 125;
              const pos = { start: returnStartX + returnCurrentX * scale, spacing: circuitSpacing * scale };
              if (i < returnCircuits.length - 1) {
                const spacing = Number(circuit.spacingToNext) || params.returnSpacingBetweenCircuits || 125;
                returnCurrentX += circuitSpacing + spacing;
              }
              return pos;
            });
            
            // Позиции центральных фитингов подачи
            const supplyCenterFittingPositions = supplyCenterFittings.map(fitting => {
              let x = supplyStartX;
              
              // Определяем какие позиции использовать (для подачи: supply=подача, return=обратка)
              const referenceRow = fitting.referenceRow || 'supply';
              const positions = referenceRow === 'return' ? returnPositions : supplyPositions;
              const circuits = referenceRow === 'return' ? returnCircuits : supplyCircuits;
              
              if (fitting.positionType === 'center') {
                x = (supplyStartX + supplyEndX) / 2;
              } else if (fitting.positionType === 'betweenCircuits' && circuits.length > 1) {
                const idx = fitting.betweenIndex || 0;
                if (idx < positions.length - 1) {
                  const pos1End = positions[idx].start + positions[idx].spacing;
                  const pos2Start = positions[idx + 1].start;
                  x = (pos1End + pos2Start) / 2;
                }
              } else if (fitting.positionType === 'betweenFittings' && circuits.length > 0) {
                const idx = fitting.betweenIndex || 0;
                // Собираем все патрубки
                const allFittings = [];
                positions.forEach(pos => {
                  allFittings.push(pos.start);
                  allFittings.push(pos.start + pos.spacing);
                });
                if (idx < allFittings.length - 1) {
                  x = (allFittings[idx] + allFittings[idx + 1]) / 2;
                }
              } else if (fitting.positionType === 'distance') {
                const dir = fitting.distanceDirection || 'left';
                if (dir === 'left') {
                  x = supplyStartX + (fitting.distanceFromStart || 50) * scale;
                } else {
                  x = supplyEndX - (fitting.distanceFromStart || 50) * scale;
                }
              }
              return { x, fitting };
            });
            
            // Позиции центральных фитингов обратки
            const returnCenterFittingPositions = returnCenterFittings.map(fitting => {
              let x = returnStartX;
              
              // Определяем какие позиции использовать (для обратки: return=обратка, supply=подача)
              const referenceRow = fitting.referenceRow || 'return';
              const positions = referenceRow === 'supply' ? supplyPositions : returnPositions;
              const circuits = referenceRow === 'supply' ? supplyCircuits : returnCircuits;
              
              if (fitting.positionType === 'center') {
                x = (returnStartX + returnEndX) / 2;
              } else if (fitting.positionType === 'betweenCircuits' && circuits.length > 1) {
                const idx = fitting.betweenIndex || 0;
                if (idx < positions.length - 1) {
                  const pos1End = positions[idx].start + positions[idx].spacing;
                  const pos2Start = positions[idx + 1].start;
                  x = (pos1End + pos2Start) / 2;
                }
              } else if (fitting.positionType === 'betweenFittings' && circuits.length > 0) {
                const idx = fitting.betweenIndex || 0;
                // Собираем все патрубки
                const allFittings = [];
                positions.forEach(pos => {
                  allFittings.push(pos.start);
                  allFittings.push(pos.start + pos.spacing);
                });
                if (idx < allFittings.length - 1) {
                  x = (allFittings[idx] + allFittings[idx + 1]) / 2;
                }
              } else if (fitting.positionType === 'distance') {
                const dir = fitting.distanceDirection || 'left';
                if (dir === 'left') {
                  x = returnStartX + (fitting.distanceFromStart || 50) * scale;
                } else {
                  x = returnEndX - (fitting.distanceFromStart || 50) * scale;
                }
              }
              return { x, fitting };
            });
            
            // Позиции дополнительных выходов подачи
            const supplyAdditionalPositions = supplyAdditionalOutlets.map(outlet => {
              let x = supplyStartX;
              
              // Определяем какие позиции использовать (для подачи: supply=подача, return=обратка)
              const referenceRow = outlet.referenceRow || 'supply';
              const positions = referenceRow === 'return' ? returnPositions : supplyPositions;
              const circuits = referenceRow === 'return' ? returnCircuits : supplyCircuits;
              
              if (outlet.positionType === 'center') {
                x = (supplyStartX + supplyEndX) / 2;
              } else if (outlet.positionType === 'betweenCircuits' && circuits.length > 1) {
                const idx = outlet.betweenIndex || 0;
                if (idx < positions.length - 1) {
                  const pos1End = positions[idx].start + positions[idx].spacing;
                  const pos2Start = positions[idx + 1].start;
                  x = (pos1End + pos2Start) / 2;
                }
              } else if (outlet.positionType === 'betweenFittings' && circuits.length > 0) {
                const idx = outlet.betweenIndex || 0;
                // Собираем все патрубки
                const allFittings = [];
                positions.forEach(pos => {
                  allFittings.push(pos.start);
                  allFittings.push(pos.start + pos.spacing);
                });
                if (idx < allFittings.length - 1) {
                  x = (allFittings[idx] + allFittings[idx + 1]) / 2;
                }
              } else if (outlet.positionType === 'distance') {
                if (outlet.distanceDirection === 'left') {
                  x = supplyStartX + (outlet.distanceFromEdge || 60) * scale;
                } else {
                  x = supplyEndX - (outlet.distanceFromEdge || 60) * scale;
                }
              }
              return { x, outlet };
            });
            
            // Позиции дополнительных выходов обратки
            const returnAdditionalPositions = returnAdditionalOutlets.map(outlet => {
              let x = returnStartX;
              
              // Определяем какие позиции использовать (для обратки: return=обратка, supply=подача)
              const referenceRow = outlet.referenceRow || 'return';
              const positions = referenceRow === 'supply' ? supplyPositions : returnPositions;
              const circuits = referenceRow === 'supply' ? supplyCircuits : returnCircuits;
              
              if (outlet.positionType === 'center') {
                x = (returnStartX + returnEndX) / 2;
              } else if (outlet.positionType === 'betweenCircuits' && circuits.length > 1) {
                const idx = outlet.betweenIndex || 0;
                if (idx < positions.length - 1) {
                  const pos1End = positions[idx].start + positions[idx].spacing;
                  const pos2Start = positions[idx + 1].start;
                  x = (pos1End + pos2Start) / 2;
                }
              } else if (outlet.positionType === 'betweenFittings' && circuits.length > 0) {
                const idx = outlet.betweenIndex || 0;
                // Собираем все патрубки
                const allFittings = [];
                positions.forEach(pos => {
                  allFittings.push(pos.start);
                  allFittings.push(pos.start + pos.spacing);
                });
                if (idx < allFittings.length - 1) {
                  x = (allFittings[idx] + allFittings[idx + 1]) / 2;
                }
              } else if (outlet.positionType === 'distance') {
                if (outlet.distanceDirection === 'left') {
                  x = returnStartX + (outlet.distanceFromEdge || 60) * scale;
                } else {
                  x = returnEndX - (outlet.distanceFromEdge || 60) * scale;
                }
              }
              return { x, outlet };
            });
            
            return (
              <svg viewBox={`0 0 ${svgWidth} ${svgHeight}`} style={{ width: '100%', height: '100%' }}>
                {/* Подающий коллектор (верхний) - красный */}
                <line
                  x1={supplyStartX}
                  y1={supplyY}
                  x2={supplyEndX}
                  y2={supplyY}
                  stroke="#d32f2f"
                  strokeWidth={2}
                  strokeLinecap="round"
                />
                <text x={supplyStartX - 40} y={supplyY + 2} fontSize={7} fill="#d32f2f" fontWeight="600">
                  П
                </text>
                
                {/* Обратный коллектор (нижний) - синий */}
                <line
                  x1={returnStartX}
                  y1={returnY}
                  x2={returnEndX}
                  y2={returnY}
                  stroke="#212121"
                  strokeWidth={2}
                  strokeLinecap="round"
                />
                <text x={returnStartX - 40} y={returnY + 2} fontSize={7} fill="#212121" fontWeight="600">
                  О
                </text>
                
                {/* Заглушки подачи */}
                {params.hasSupplyLeftCap && (
                  <rect x={supplyStartX - 4} y={supplyY - 1} width={3} height={2} fill="#bdbdbd" rx={0.5} />
                )}
                {params.hasSupplyRightCap && (
                  <rect x={supplyEndX + 1} y={supplyY - 1} width={3} height={2} fill="#bdbdbd" rx={0.5} />
                )}
                
                {/* Заглушки обратки */}
                {params.hasReturnLeftCap && (
                  <rect x={returnStartX - 4} y={returnY - 1} width={3} height={2} fill="#bdbdbd" rx={0.5} />
                )}
                {params.hasReturnRightCap && (
                  <rect x={returnEndX + 1} y={returnY - 1} width={3} height={2} fill="#bdbdbd" rx={0.5} />
                )}
                
                {/* Торцевые подключения подачи */}
                {params.hasSupplyLeftConnection && (
                  <line
                    x1={supplyStartX - 12}
                    y1={supplyY}
                    x2={supplyStartX}
                    y2={supplyY}
                    stroke="#d32f2f"
                    strokeWidth={1.5}
                  />
                )}
                {params.hasSupplyRightConnection && (
                  <line
                    x1={supplyEndX}
                    y1={supplyY}
                    x2={supplyEndX + 12}
                    y2={supplyY}
                    stroke="#d32f2f"
                    strokeWidth={1.5}
                  />
                )}
                
                {/* Торцевые подключения обратки */}
                {params.hasReturnLeftConnection && (
                  <line
                    x1={returnStartX - 12}
                    y1={returnY}
                    x2={returnStartX}
                    y2={returnY}
                    stroke="#212121"
                    strokeWidth={1.5}
                  />
                )}
                {params.hasReturnRightConnection && (
                  <line
                    x1={returnEndX}
                    y1={returnY}
                    x2={returnEndX + 12}
                    y2={returnY}
                    stroke="#212121"
                    strokeWidth={1.5}
                  />
                )}
                
                {/* Контуры подачи (отрисовываем все, независимо от обратки) */}
                {supplyPositions.map((supplyPos, i) => {
                  const supplyCircuit = supplyCircuits[i];
                  const isFlange = supplyCircuit.fitting?.startsWith('Flanec_');
                  const isNakGaika = supplyCircuit.fitting?.startsWith('Nak_Gaika_');
                  const isShaibaNarRezba = supplyCircuit.fitting?.startsWith('Shaib_Nar_Rezba_');
                  const supplySwap = supplyCircuit.swapOrder || false;
                  
                  // Для подачи: по умолчанию первый КОРОТКИЙ, второй ДЛИННЫЙ
                  const supplyShortX = supplySwap ? (supplyPos.start + supplyPos.spacing) : supplyPos.start;
                  const supplyLongX = supplySwap ? supplyPos.start : (supplyPos.start + supplyPos.spacing);
                  
                  return (
                    <g key={`supply-circuit-${i}`}>
                      {/* Короткий патрубок */}
                      <line
                        x1={supplyShortX}
                        y1={supplyY}
                        x2={supplyShortX}
                        y2={supplyY - 5}
                        stroke="#d32f2f"
                        strokeWidth={1}
                      />
                      <circle cx={supplyShortX} cy={supplyY} r={0.8} fill="#d32f2f" />
                      
                      {/* Длинный патрубок */}
                      <line
                        x1={supplyLongX}
                        y1={supplyY + 22}
                        x2={supplyLongX}
                        y2={supplyY - 5}
                        stroke="#d32f2f"
                        strokeWidth={1.2}
                      />
                      <circle cx={supplyLongX} cy={supplyY} r={0.8} fill="#d32f2f" />
                      
                      {/* Фланцы */}
                      {isFlange && (
                        <>
                          <rect x={supplyShortX - 2} y={supplyY - 7} width={4} height={1.5} fill="#424242" rx={0.3} />
                          <rect x={supplyLongX - 2} y={supplyY - 7} width={4} height={1.5} fill="#424242" rx={0.3} />
                        </>
                      )}
                      
                      {/* Накидные гайки */}
                      {isNakGaika && (
                        <>
                          <circle cx={supplyShortX} cy={supplyY - 6} r={1.2} fill="none" stroke="#424242" strokeWidth={0.8} />
                          <circle cx={supplyLongX} cy={supplyY - 6} r={1.2} fill="none" stroke="#424242" strokeWidth={0.8} />
                        </>
                      )}
                      
                      {/* Шайбы наружной резьбы */}
                      {isShaibaNarRezba && (
                        <>
                          <circle cx={supplyShortX} cy={supplyY - 6} r={2} fill="#757575" />
                          <circle cx={supplyLongX} cy={supplyY - 6} r={2} fill="#757575" />
                        </>
                      )}
                      
                      {/* Номер контура над патрубками */}
                      <text
                        x={(supplyShortX + supplyLongX) / 2}
                        y={supplyY - 13}
                        textAnchor="middle"
                        fontSize={5}
                        fill="#d32f2f"
                        fontWeight="600"
                      >
                        {i + 1}
                      </text>
                    </g>
                  );
                })}
                
                {/* Контуры обратки (отрисовываем все, независимо от подачи) */}
                {returnPositions.map((returnPos, i) => {
                  const returnCircuit = returnCircuits[i];
                  const isFlange = returnCircuit.fitting?.startsWith('Flanec_');
                  const isNakGaika = returnCircuit.fitting?.startsWith('Nak_Gaika_');
                  const isShaibaNarRezba = returnCircuit.fitting?.startsWith('Shaib_Nar_Rezba_');
                  const returnSwap = returnCircuit.swapOrder || false;
                  
                  // Для обратки (нижний): по умолчанию первый ДЛИННЫЙ, второй КОРОТКИЙ
                  // При swap: первый КОРОТКИЙ, второй ДЛИННЫЙ
                  const returnLongX = returnSwap ? (returnPos.start + returnPos.spacing) : returnPos.start;
                  const returnShortX = returnSwap ? returnPos.start : (returnPos.start + returnPos.spacing);
                  
                  return (
                    <g key={`return-circuit-${i}`}>
                      {/* Длинный патрубок */}
                      <line
                        x1={returnLongX}
                        y1={returnY - 22}
                        x2={returnLongX}
                        y2={returnY + 5}
                        stroke="#212121"
                        strokeWidth={1.2}
                      />
                      <circle cx={returnLongX} cy={returnY} r={0.8} fill="#212121" />
                      
                      {/* Короткий патрубок */}
                      <line
                        x1={returnShortX}
                        y1={returnY}
                        x2={returnShortX}
                        y2={returnY + 5}
                        stroke="#212121"
                        strokeWidth={1}
                      />
                      <circle cx={returnShortX} cy={returnY} r={0.8} fill="#212121" />
                      
                      {/* Фланцы */}
                      {isFlange && (
                        <>
                          <rect x={returnLongX - 2} y={returnY + 5.5} width={4} height={1.5} fill="#424242" rx={0.3} />
                          <rect x={returnShortX - 2} y={returnY + 5.5} width={4} height={1.5} fill="#424242" rx={0.3} />
                        </>
                      )}
                      
                      {/* Накидные гайки */}
                      {isNakGaika && (
                        <>
                          <circle cx={returnLongX} cy={returnY + 6.5} r={1.2} fill="none" stroke="#424242" strokeWidth={0.8} />
                          <circle cx={returnShortX} cy={returnY + 6.5} r={1.2} fill="none" stroke="#424242" strokeWidth={0.8} />
                        </>
                      )}
                      
                      {/* Шайбы наружной резьбы */}
                      {isShaibaNarRezba && (
                        <>
                          <circle cx={returnLongX} cy={returnY + 6.5} r={2} fill="#757575" />
                          <circle cx={returnShortX} cy={returnY + 6.5} r={2} fill="#757575" />
                        </>
                      )}
                      
                      {/* Номер контура под патрубками */}
                      <text
                        x={(returnLongX + returnShortX) / 2}
                        y={returnY + 15}
                        textAnchor="middle"
                        fontSize={5}
                        fill="#212121"
                        fontWeight="600"
                      >
                        {i + 1}
                      </text>
                    </g>
                  );
                })}
                
                {/* Дополнительные выходы подачи */}
                {supplyAdditionalPositions.map((pos, i) => {
                  const direction = pos.outlet.direction || 'up';
                  const outletType = pos.outlet.outletType || 'short';
                  const isThrough = outletType === 'through';
                  
                  if (isThrough) {
                    // Для сквозных выходов рисуем две линии
                    const shortLength = 10;
                    
                    if (direction === 'up') {
                      // Короткая часть вверх
                      const yShortEnd = supplyY - shortLength;
                      // Длинная часть вниз до обратки
                      const yLongEnd = returnY;
                      
                      return (
                        <g key={`supply-add-${i}`}>
                          {/* Короткая часть */}
                          <line
                            x1={pos.x}
                            y1={supplyY}
                            x2={pos.x}
                            y2={yShortEnd}
                            stroke="#757575"
                            strokeWidth={1}
                          />
                          <circle cx={pos.x} cy={yShortEnd - 1.5} r={1.5} fill="#757575" />
                          
                          {/* Длинная часть до обратки */}
                          <line
                            x1={pos.x}
                            y1={supplyY}
                            x2={pos.x}
                            y2={yLongEnd}
                            stroke="#757575"
                            strokeWidth={1}
                          />
                        </g>
                      );
                    } else {
                      // Короткая часть вниз
                      const yShortEnd = supplyY + shortLength;
                      
                      return (
                        <g key={`supply-add-${i}`}>
                          <line
                            x1={pos.x}
                            y1={supplyY}
                            x2={pos.x}
                            y2={yShortEnd}
                            stroke="#757575"
                            strokeWidth={1}
                          />
                          <circle cx={pos.x} cy={yShortEnd + 1.5} r={1.5} fill="#757575" />
                        </g>
                      );
                    }
                  } else {
                    // Обычный короткий выход
                    const yEnd = direction === 'up' ? supplyY - 10 : supplyY + 10;
                    return (
                      <g key={`supply-add-${i}`}>
                        <line
                          x1={pos.x}
                          y1={supplyY}
                          x2={pos.x}
                          y2={yEnd}
                          stroke="#757575"
                          strokeWidth={1}
                        />
                        <circle cx={pos.x} cy={yEnd + (direction === 'up' ? -1.5 : 1.5)} r={1.5} fill="#757575" />
                      </g>
                    );
                  }
                })}
                
                {/* Дополнительные выходы обратки */}
                {returnAdditionalPositions.map((pos, i) => {
                  const direction = pos.outlet.direction || 'down';
                  const outletType = pos.outlet.outletType || 'short';
                  const isThrough = outletType === 'through';
                  
                  if (isThrough) {
                    const shortLength = 10;
                    
                    if (direction === 'down') {
                      const yShortEnd = returnY + shortLength;
                      const yLongEnd = supplyY;
                      
                      return (
                        <g key={`return-add-${i}`}>
                          <line x1={pos.x} y1={returnY} x2={pos.x} y2={yShortEnd} stroke="#424242" strokeWidth={1.5} />
                          <line x1={pos.x} y1={returnY} x2={pos.x} y2={yShortEnd} stroke="#e0e0e0" strokeWidth={0.8} />
                          <circle cx={pos.x} cy={yShortEnd + 1.5} r={1.5} fill="#e0e0e0" stroke="#424242" strokeWidth={0.5} />
                          <line x1={pos.x} y1={returnY} x2={pos.x} y2={yLongEnd} stroke="#424242" strokeWidth={1.5} />
                          <line x1={pos.x} y1={returnY} x2={pos.x} y2={yLongEnd} stroke="#e0e0e0" strokeWidth={0.8} />
                        </g>
                      );
                    } else {
                      const yShortEnd = returnY - shortLength;
                      
                      return (
                        <g key={`return-add-${i}`}>
                          <line x1={pos.x} y1={returnY} x2={pos.x} y2={yShortEnd} stroke="#424242" strokeWidth={1.5} />
                          <line x1={pos.x} y1={returnY} x2={pos.x} y2={yShortEnd} stroke="#e0e0e0" strokeWidth={0.8} />
                          <circle cx={pos.x} cy={yShortEnd - 1.5} r={1.5} fill="#e0e0e0" stroke="#424242" strokeWidth={0.5} />
                        </g>
                      );
                    }
                  } else {
                    const yEnd = direction === 'down' ? returnY + 10 : returnY - 10;
                    return (
                      <g key={`return-add-${i}`}>
                        <line x1={pos.x} y1={returnY} x2={pos.x} y2={yEnd} stroke="#424242" strokeWidth={1.5} />
                        <line x1={pos.x} y1={returnY} x2={pos.x} y2={yEnd} stroke="#e0e0e0" strokeWidth={0.8} />
                        <circle cx={pos.x} cy={yEnd + (direction === 'down' ? 1.5 : -1.5)} r={1.5} fill="#e0e0e0" stroke="#424242" strokeWidth={0.5} />
                      </g>
                    );
                  }
                })}
                
                {/* Центральные фитинги подачи (термометры и т.д.) — рендерятся поверх доп. выходов */}
                {supplyCenterFittingPositions.map((pos, i) => (
                  <g key={`supply-center-${i}`}>
                    <circle cx={pos.x} cy={supplyY} r={3} fill="#757575" />
                  </g>
                ))}
                
                {/* Центральные фитинги обратки — рендерятся поверх доп. выходов */}
                {returnCenterFittingPositions.map((pos, i) => (
                  <g key={`return-center-${i}`}>
                    <circle cx={pos.x} cy={returnY} r={3} fill="#e0e0e0" stroke="#424242" strokeWidth={0.5} />
                  </g>
                ))}
                
                {/* Информация */}
                <text x={svgWidth - 10} y={10} textAnchor="end" fontSize={6} fill="#666">
                  Общая длина - П: {supplyTotalLength} мм | О: {returnTotalLength} мм
                </text>
              </svg>
            );
          })()}
        </Box>
      </Paper>

      {/* Вкладки */}
      <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 3 }}>
        <Tab label="Основные параметры" />
        <Tab label="Контуры" />
        <Tab label="Подключения" />
        <Tab label="Заглушки" />
      </Tabs>

      {/* Вкладка 1: Основные параметры */}
      {activeTab === 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Основные параметры</Typography>
          <Grid container spacing={3} alignItems="center">
            {/* В компактном режиме показываем один селект "Общий размер" */}
            {params.isCompact ? (
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Общий размер</InputLabel>
                  <Select
                    value={params.compactSize || '80x80'}
                    label="Общий размер"
                    onChange={(e) => {
                      const newCompactSize = e.target.value;
                      const mapping = compactSizeToProfilesMap[newCompactSize];
                      
                      if (mapping) {
                        // Межосевое = высота листа (второй размер)
                        // 60x30 -> 30, 80x40 -> 40
                        const sheetProfile = mapping.sheet; // '60x30' или '80x40'
                        const spacingValue = parseInt(sheetProfile.split('x')[1]); // Берем второй размер (высоту)
                        
                        // Обновляем контуры подающего коллектора (проверяем совместимость по compactSize)
                        const updatedSupplyCircuits = supplyCircuits.map((circuit) => {
                          const fittingId = circuit.fittingId || circuit.fitting;
                          if (fittingId && circuit.type !== 'pipe' && !isCircuitFittingCompatibleWithSquareProfile(fittingId, newCompactSize)) {
                            const fittingsInCategory = getFittingsByCategory(circuit.fittingCategory || 'Ниппели', null, null);
                            const compatibleFittings = fittingsInCategory.filter(f => isCircuitFittingCompatibleWithSquareProfile(f.id, newCompactSize));
                            
                            if (compatibleFittings.length > 0) {
                              const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                              return {
                                ...circuit,
                                fitting: maxCompatibleFitting.id,
                                fittingId: maxCompatibleFitting.id,
                                diameter: fittingsData[maxCompatibleFitting.id]?.diameter || circuit.diameter,
                                fittingLength: null,
                              };
                            }
                          }
                          return { ...circuit, fittingLength: null };
                        });
                        setSupplyCircuits(updatedSupplyCircuits);
                        
                        // Обновляем дополнительные выходы подающего коллектора
                        const updatedSupplyAdditionalOutlets = supplyAdditionalOutlets.map((outlet) => {
                          if (outlet.type === 'fitting' && outlet.fittingId && !isAdditionalOutletFittingCompatibleWithCompactSize(outlet.fittingId, newCompactSize)) {
                            const fittingsInCategory = getFittingsByCategory(outlet.fittingCategory || 'Ниппели', null, null);
                            const compatibleFittings = fittingsInCategory.filter(f => isAdditionalOutletFittingCompatibleWithCompactSize(f.id, newCompactSize));
                            
                            if (compatibleFittings.length > 0) {
                              const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                              return { ...outlet, fittingId: maxCompatibleFitting.id, fittingLength: null };
                            }
                          }
                          return outlet;
                        });
                        setSupplyAdditionalOutlets(updatedSupplyAdditionalOutlets);
                        
                        // Обновляем контуры обратного коллектора (проверяем совместимость по compactSize)
                        const updatedReturnCircuits = returnCircuits.map((circuit) => {
                          const fittingId = circuit.fittingId || circuit.fitting;
                          if (fittingId && circuit.type !== 'pipe' && !isCircuitFittingCompatibleWithSquareProfile(fittingId, newCompactSize)) {
                            const fittingsInCategory = getFittingsByCategory(circuit.fittingCategory || 'Ниппели', null, null);
                            const compatibleFittings = fittingsInCategory.filter(f => isCircuitFittingCompatibleWithSquareProfile(f.id, newCompactSize));
                            
                            if (compatibleFittings.length > 0) {
                              const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                              return {
                                ...circuit,
                                fitting: maxCompatibleFitting.id,
                                fittingId: maxCompatibleFitting.id,
                                diameter: fittingsData[maxCompatibleFitting.id]?.diameter || circuit.diameter,
                                fittingLength: null,
                              };
                            }
                          }
                          return { ...circuit, fittingLength: null };
                        });
                        setReturnCircuits(updatedReturnCircuits);
                        
                        // Обновляем дополнительные выходы обратного коллектора
                        const updatedReturnAdditionalOutlets = returnAdditionalOutlets.map((outlet) => {
                          if (outlet.type === 'fitting' && outlet.fittingId && !isAdditionalOutletFittingCompatibleWithCompactSize(outlet.fittingId, newCompactSize)) {
                            const fittingsInCategory = getFittingsByCategory(outlet.fittingCategory || 'Ниппели', null, null);
                            const compatibleFittings = fittingsInCategory.filter(f => isAdditionalOutletFittingCompatibleWithCompactSize(f.id, newCompactSize));
                            
                            if (compatibleFittings.length > 0) {
                              const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                              return { ...outlet, fittingId: maxCompatibleFitting.id, fittingLength: null };
                            }
                          }
                          return outlet;
                        });
                        setReturnAdditionalOutlets(updatedReturnAdditionalOutlets);

                        // Обновляем центральные фитинги подающего коллектора
                        const updatedSupplyCenterFittings = supplyCenterFittings.map((fitting) => {
                          if (fitting.fittingId && !isCenterFittingCompatibleWithCompactSize(fitting.fittingId, newCompactSize)) {
                            const fittingsInCategory = getFittingsByCategory(fitting.fittingCategory || 'Ниппели', null, null);
                            const compatibleFittings = fittingsInCategory.filter(f => isCenterFittingCompatibleWithCompactSize(f.id, newCompactSize));
                            if (compatibleFittings.length > 0) {
                              const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                              return { ...fitting, fittingId: maxCompatibleFitting.id, fittingLength: null };
                            }
                          }
                          return fitting;
                        });
                        setSupplyCenterFittings(updatedSupplyCenterFittings);

                        // Обновляем центральные фитинги обратного коллектора
                        const updatedReturnCenterFittings = returnCenterFittings.map((fitting) => {
                          if (fitting.fittingId && !isCenterFittingCompatibleWithCompactSize(fitting.fittingId, newCompactSize)) {
                            const fittingsInCategory = getFittingsByCategory(fitting.fittingCategory || 'Ниппели', null, null);
                            const compatibleFittings = fittingsInCategory.filter(f => isCenterFittingCompatibleWithCompactSize(f.id, newCompactSize));
                            if (compatibleFittings.length > 0) {
                              const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                              return { ...fitting, fittingId: maxCompatibleFitting.id, fittingLength: null };
                            }
                          }
                          return fitting;
                        });
                        setReturnCenterFittings(updatedReturnCenterFittings);
                        
                        const compactEdge = getCompactModeFirstLastOffset(newCompactSize);
                        updateParam({
                          compactSize: newCompactSize,
                          supplyPipeProfile: mapping.sheet,
                          returnPipeProfile: mapping.profile,
                          pipeSpacing: spacingValue, // Устанавливаем межосевое = высоте листа
                          supplyFirstCircuitOffset: compactEdge,
                          supplyLastCircuitOffset: compactEdge,
                          returnFirstCircuitOffset: compactEdge,
                          returnLastCircuitOffset: compactEdge,
                        });
                      }
                    }}
                  >
                    {availableCompactSizes.map(s => (
                      <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            ) : (
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>
                    {params.shape === 'round' 
                      ? 'Диаметр подающего коллектора' 
                      : 'Профиль подающего коллектора'
                    }
                  </InputLabel>
                  <Select
                    value={params.shape === 'round' ? params.supplyPipeDiameter : (params.supplyPipeProfile || '80x80')}
                    label={params.shape === 'round' 
                      ? 'Диаметр подающего коллектора' 
                      : 'Профиль подающего коллектора'
                    }
                    onChange={(e) => {
                      const newValue = e.target.value;
                    
                    if (params.shape === 'round') {
                      const newDiameter = newValue;
                    
                      // Обновляем центральные фитинги подающего коллектора
                      const updatedSupplyCenterFittings = updateCenterFittingsForDiameter(
                        supplyCenterFittings,
                        newDiameter,
                        isCenterFittingCompatible,
                        (category) => getFittingsByCategory(category, newDiameter, isCenterFittingCompatible)
                      );
                      if (JSON.stringify(updatedSupplyCenterFittings) !== JSON.stringify(supplyCenterFittings)) {
                        setSupplyCenterFittings(updatedSupplyCenterFittings);
                      }
                    
                      // Обновляем контуры подающего коллектора
                      const updatedSupplyCircuits = supplyCircuits.map((circuit) => {
                        const fittingId = circuit.fittingId || circuit.fitting; // Поддержка обоих полей
                        // Если есть fitting или fittingId и type не равен 'pipe', то это фитинг
                        if (fittingId && circuit.type !== 'pipe' && !isCircuitFittingCompatible(fittingId, newDiameter)) {
                          // Фитинг несовместим - пытаемся найти совместимый в той же категории
                          const fittingsInCategory = getFittingsByCategory(circuit.fittingCategory || 'Ниппели', newDiameter, isCircuitFittingCompatible);
                          const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                          
                          if (compatibleFittings.length > 0) {
                            // Нашли совместимые фитинги - берем ПОСЛЕДНИЙ (максимальный)
                            const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                            return {
                              ...circuit,
                              fitting: maxCompatibleFitting.id,
                              fittingId: maxCompatibleFitting.id,
                              diameter: fittingsData[maxCompatibleFitting.id]?.diameter || circuit.diameter,
                              fittingLength: null,
                            };
                          } else {
                            // Нет совместимых фитингов в категории - сбрасываем на трубу
                            return {
                              ...circuit,
                              type: 'pipe',
                              fitting: null,
                              fittingId: null,
                              fittingCategory: null,
                              fittingLength: null,
                              diameter: newDiameter,
                              length: 100,
                            };
                          }
                        }
                        return circuit;
                      });
                      if (JSON.stringify(updatedSupplyCircuits) !== JSON.stringify(supplyCircuits)) {
                        setSupplyCircuits(updatedSupplyCircuits);
                      }
                    
                      // Обновляем дополнительные выходы подающего коллектора
                      const updatedSupplyAdditionalOutlets = supplyAdditionalOutlets.map((outlet) => {
                        if (outlet.type === 'fitting' && outlet.fittingId && !isAdditionalOutletFittingCompatible(outlet.fittingId, newDiameter)) {
                          const fittingsInCategory = getFittingsByCategory(outlet.fittingCategory || 'Ниппели', newDiameter, isAdditionalOutletFittingCompatible);
                          const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                          
                          if (compatibleFittings.length > 0) {
                            const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                            return {
                              ...outlet,
                              fittingId: maxCompatibleFitting.id,
                              diameter: fittingsData[maxCompatibleFitting.id]?.diameter || outlet.diameter,
                              fittingLength: null,
                            };
                          } else {
                            return {
                              ...outlet,
                              type: 'pipe',
                              fittingId: null,
                              fittingCategory: null,
                              fittingLength: null,
                              diameter: newDiameter,
                              length: 100,
                            };
                          }
                        }
                        return outlet;
                      });
                      if (JSON.stringify(updatedSupplyAdditionalOutlets) !== JSON.stringify(supplyAdditionalOutlets)) {
                        setSupplyAdditionalOutlets(updatedSupplyAdditionalOutlets);
                      }
                    
                      // Обновляем торцевые подключения подающего коллектора
                      const supplyEndConnectionUpdates = {};
                    
                      // Левое торцевое подключение подающего
                      if (params.hasSupplyLeftConnection && params.supplyLeftConnectionType === 'fitting' && params.supplyLeftConnectionFitting) {
                        // Проверяем совместимость фитинга с новым диаметром
                        if (!isEndFittingCompatibleWithMainPipe(params.supplyLeftConnectionFitting, newDiameter)) {
                          // Фитинг несовместим - пытаемся найти совместимый в той же категории
                          const currentCategory = params.supplyLeftConnectionFittingCategory || 'Ниппели';
                          const fittingsInCategory = getFittingsByCategory(currentCategory, newDiameter, isEndFittingCompatibleWithMainPipe);
                          const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                          
                          if (compatibleFittings.length > 0) {
                            // Нашли совместимые фитинги - берем ПОСЛЕДНИЙ (максимальный)
                            const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                            supplyEndConnectionUpdates.supplyLeftConnectionFitting = maxCompatibleFitting.id;
                            supplyEndConnectionUpdates.supplyLeftConnectionFittingLength = null;
                          }
                        }
                        
                        const capUpdates = getCapUpdatesForConnectionFitting(
                          supplyEndConnectionUpdates.supplyLeftConnectionFitting || params.supplyLeftConnectionFitting,
                          newDiameter,
                          'left',
                          'supply',
                          params.shape
                        );
                        Object.assign(supplyEndConnectionUpdates, capUpdates);
                      }
                      // Если заглушка установлена, но нет фитинга - обновляем тип заглушки на эллиптическую для больших диаметров
                      else if (params.hasSupplyLeftCap && params.supplyLeftCapElementType === 'cap' && params.shape !== 'square') {
                        const ellipticalCap = findEllipticalCap(newDiameter);
                        if (ellipticalCap && params.supplyLeftCapType !== 'elliptical') {
                          supplyEndConnectionUpdates.supplyLeftCapType = 'elliptical';
                        }
                      }
                    
                      // Правое торцевое подключение подающего
                      if (params.hasSupplyRightConnection && params.supplyRightConnectionType === 'fitting' && params.supplyRightConnectionFitting) {
                        // Проверяем совместимость фитинга с новым диаметром
                        if (!isEndFittingCompatibleWithMainPipe(params.supplyRightConnectionFitting, newDiameter)) {
                          // Фитинг несовместим - пытаемся найти совместимый в той же категории
                          const currentCategory = params.supplyRightConnectionFittingCategory || 'Ниппели';
                          const fittingsInCategory = getFittingsByCategory(currentCategory, newDiameter, isEndFittingCompatibleWithMainPipe);
                          const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                          
                          if (compatibleFittings.length > 0) {
                            // Нашли совместимые фитинги - берем ПОСЛЕДНИЙ (максимальный)
                            const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                            supplyEndConnectionUpdates.supplyRightConnectionFitting = maxCompatibleFitting.id;
                            supplyEndConnectionUpdates.supplyRightConnectionFittingLength = null;
                          }
                        }
                        
                        const capUpdates = getCapUpdatesForConnectionFitting(
                          supplyEndConnectionUpdates.supplyRightConnectionFitting || params.supplyRightConnectionFitting,
                          newDiameter,
                          'right',
                          'supply',
                          params.shape
                        );
                        Object.assign(supplyEndConnectionUpdates, capUpdates);
                      }
                      // Если заглушка установлена, но нет фитинга - обновляем тип заглушки на эллиптическую для больших диаметров
                      else if (params.hasSupplyRightCap && params.supplyRightCapElementType === 'cap' && params.shape !== 'square') {
                        const ellipticalCap = findEllipticalCap(newDiameter);
                        if (ellipticalCap && params.supplyRightCapType !== 'elliptical') {
                          supplyEndConnectionUpdates.supplyRightCapType = 'elliptical';
                        }
                      }
                    
                      if (Object.keys(supplyEndConnectionUpdates).length > 0) {
                        updateParam(supplyEndConnectionUpdates);
                      }
                    
                      if (diametersLinked) {
                        // Если синхронизация включена, меняем оба диаметра
                        updateParam({ supplyPipeDiameter: newDiameter, returnPipeDiameter: newDiameter });
                      
                        // Обновляем центральные фитинги обратного коллектора
                        const updatedReturnCenterFittings = updateCenterFittingsForDiameter(
                          returnCenterFittings,
                          newDiameter,
                          isCenterFittingCompatible,
                          (category) => getFittingsByCategory(category, newDiameter, isCenterFittingCompatible)
                        );
                        if (JSON.stringify(updatedReturnCenterFittings) !== JSON.stringify(returnCenterFittings)) {
                          setReturnCenterFittings(updatedReturnCenterFittings);
                        }
                      
                        // Обновляем контуры обратного коллектора
                        const updatedReturnCircuits = returnCircuits.map((circuit) => {
                          const fittingId = circuit.fittingId || circuit.fitting; // Поддержка обоих полей
                          // Если есть fitting или fittingId и type не равен 'pipe', то это фитинг
                          if (fittingId && circuit.type !== 'pipe' && !isCircuitFittingCompatible(fittingId, newDiameter)) {
                            const fittingsInCategory = getFittingsByCategory(circuit.fittingCategory || 'Ниппели', newDiameter, isCircuitFittingCompatible);
                            const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                            
                            if (compatibleFittings.length > 0) {
                              const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                              return {
                                ...circuit,
                                fitting: maxCompatibleFitting.id,
                                fittingId: maxCompatibleFitting.id,
                                diameter: fittingsData[maxCompatibleFitting.id]?.diameter || circuit.diameter,
                                fittingLength: null,
                              };
                            } else {
                              return {
                                ...circuit,
                                type: 'pipe',
                                fitting: null,
                                fittingId: null,
                                fittingCategory: null,
                                fittingLength: null,
                                diameter: newDiameter,
                                length: 100,
                              };
                            }
                          }
                          return circuit;
                        });
                        if (JSON.stringify(updatedReturnCircuits) !== JSON.stringify(returnCircuits)) {
                          setReturnCircuits(updatedReturnCircuits);
                        }
                      
                        // Обновляем дополнительные выходы обратного коллектора
                        const updatedReturnAdditionalOutlets = returnAdditionalOutlets.map((outlet) => {
                          if (outlet.type === 'fitting' && outlet.fittingId && !isAdditionalOutletFittingCompatible(outlet.fittingId, newDiameter)) {
                            const fittingsInCategory = getFittingsByCategory(outlet.fittingCategory || 'Ниппели', newDiameter, isAdditionalOutletFittingCompatible);
                            const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                            
                            if (compatibleFittings.length > 0) {
                              const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                              return {
                                ...outlet,
                                fittingId: maxCompatibleFitting.id,
                                diameter: fittingsData[maxCompatibleFitting.id]?.diameter || outlet.diameter,
                                fittingLength: null,
                              };
                            } else {
                              return {
                                ...outlet,
                                type: 'pipe',
                                fittingId: null,
                                fittingCategory: null,
                                fittingLength: null,
                                diameter: newDiameter,
                                length: 100,
                              };
                            }
                          }
                          return outlet;
                        });
                        if (JSON.stringify(updatedReturnAdditionalOutlets) !== JSON.stringify(returnAdditionalOutlets)) {
                          setReturnAdditionalOutlets(updatedReturnAdditionalOutlets);
                        }
                      
                        // Обновляем торцевые подключения обратного коллектора
                        const returnEndConnectionUpdates = {};
                      
                        // Левое торцевое подключение обратного
                        if (params.hasReturnLeftConnection && params.returnLeftConnectionType === 'fitting' && params.returnLeftConnectionFitting) {
                          // Проверяем совместимость фитинга с новым диаметром
                          if (!isEndFittingCompatibleWithMainPipe(params.returnLeftConnectionFitting, newDiameter)) {
                            // Фитинг несовместим - пытаемся найти совместимый в той же категории
                            const currentCategory = params.returnLeftConnectionFittingCategory || 'Ниппели';
                            const fittingsInCategory = getFittingsByCategory(currentCategory, newDiameter, isEndFittingCompatibleWithMainPipe);
                            const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                            
                            if (compatibleFittings.length > 0) {
                              // Нашли совместимые фитинги - берем ПОСЛЕДНИЙ (максимальный)
                              const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                              returnEndConnectionUpdates.returnLeftConnectionFitting = maxCompatibleFitting.id;
                              returnEndConnectionUpdates.returnLeftConnectionFittingLength = null;
                            }
                          }
                          
                          const capUpdates = getCapUpdatesForConnectionFitting(
                            returnEndConnectionUpdates.returnLeftConnectionFitting || params.returnLeftConnectionFitting,
                            newDiameter,
                            'left',
                            'return',
                            params.shape
                          );
                          Object.assign(returnEndConnectionUpdates, capUpdates);
                        }
                      
                        // Правое торцевое подключение обратного
                        if (params.hasReturnRightConnection && params.returnRightConnectionType === 'fitting' && params.returnRightConnectionFitting) {
                          // Проверяем совместимость фитинга с новым диаметром
                          if (!isEndFittingCompatibleWithMainPipe(params.returnRightConnectionFitting, newDiameter)) {
                            // Фитинг несовместим - пытаемся найти совместимый в той же категории
                            const currentCategory = params.returnRightConnectionFittingCategory || 'Ниппели';
                            const fittingsInCategory = getFittingsByCategory(currentCategory, newDiameter, isEndFittingCompatibleWithMainPipe);
                            const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                            
                            if (compatibleFittings.length > 0) {
                              // Нашли совместимые фитинги - берем ПОСЛЕДНИЙ (максимальный)
                              const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                              returnEndConnectionUpdates.returnRightConnectionFitting = maxCompatibleFitting.id;
                              returnEndConnectionUpdates.returnRightConnectionFittingLength = null;
                            }
                          }
                          
                          const capUpdates = getCapUpdatesForConnectionFitting(
                            returnEndConnectionUpdates.returnRightConnectionFitting || params.returnRightConnectionFitting,
                            newDiameter,
                            'right',
                            'return',
                            params.shape
                          );
                          Object.assign(returnEndConnectionUpdates, capUpdates);
                        }
                      
                        if (Object.keys(returnEndConnectionUpdates).length > 0) {
                          updateParam(returnEndConnectionUpdates);
                        }
                      } else {
                        updateParam('supplyPipeDiameter', newDiameter);
                      }
                    } else {
                      // Квадратный профиль - обновляем профиль и автопереключаем несовместимые фитинги
                      const newProfile = newValue;
                      
                      // Обновляем контуры подающего коллектора
                      const updatedSupplyCircuits = supplyCircuits.map((circuit) => {
                        const fittingId = circuit.fittingId || circuit.fitting;
                        if (fittingId && circuit.type !== 'pipe' && !isCircuitFittingCompatibleWithSquareProfile(fittingId, newProfile)) {
                          const fittingsInCategory = getFittingsByCategory(circuit.fittingCategory || 'Ниппели', null, null);
                          const compatibleFittings = fittingsInCategory.filter(f => isCircuitFittingCompatibleWithSquareProfile(f.id, newProfile));
                          
                          if (compatibleFittings.length > 0) {
                            const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                            return {
                              ...circuit,
                              fitting: maxCompatibleFitting.id,
                              fittingId: maxCompatibleFitting.id,
                              diameter: fittingsData[maxCompatibleFitting.id]?.diameter || circuit.diameter,
                              fittingLength: null,
                            };
                          }
                        }
                        // Сбрасываем fittingLength для всех контуров, чтобы применились дефолты для квадратных
                        return {
                          ...circuit,
                          fittingLength: null,
                        };
                      });
                      setSupplyCircuits(updatedSupplyCircuits);
                      
                      // Обновляем дополнительные выходы подающего коллектора
                      const updatedSupplyAdditionalOutlets = supplyAdditionalOutlets.map((outlet) => {
                        if (outlet.type === 'fitting' && outlet.fittingId && !isCircuitFittingCompatibleWithSquareProfile(outlet.fittingId, newProfile)) {
                          const fittingsInCategory = getFittingsByCategory(outlet.fittingCategory || 'Ниппели', null, null);
                          const compatibleFittings = fittingsInCategory.filter(f => isCircuitFittingCompatibleWithSquareProfile(f.id, newProfile));
                          
                          if (compatibleFittings.length > 0) {
                            const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                            return {
                              ...outlet,
                              fittingId: maxCompatibleFitting.id,
                              diameter: fittingsData[maxCompatibleFitting.id]?.diameter || outlet.diameter,
                              fittingLength: null,
                            };
                          }
                        }
                        return outlet;
                      });
                      if (JSON.stringify(updatedSupplyAdditionalOutlets) !== JSON.stringify(supplyAdditionalOutlets)) {
                        setSupplyAdditionalOutlets(updatedSupplyAdditionalOutlets);
                      }
                      
                      if (diametersLinked) {
                        updateParam({ supplyPipeProfile: newProfile, returnPipeProfile: newProfile });
                        
                        // Обновляем контуры обратного коллектора
                        const updatedReturnCircuits = returnCircuits.map((circuit) => {
                          const fittingId = circuit.fittingId || circuit.fitting;
                          if (fittingId && circuit.type !== 'pipe' && !isCircuitFittingCompatibleWithSquareProfile(fittingId, newProfile)) {
                            const fittingsInCategory = getFittingsByCategory(circuit.fittingCategory || 'Ниппели', null, null);
                            const compatibleFittings = fittingsInCategory.filter(f => isCircuitFittingCompatibleWithSquareProfile(f.id, newProfile));
                            
                            if (compatibleFittings.length > 0) {
                              const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                              return {
                                ...circuit,
                                fitting: maxCompatibleFitting.id,
                                fittingId: maxCompatibleFitting.id,
                                diameter: fittingsData[maxCompatibleFitting.id]?.diameter || circuit.diameter,
                                fittingLength: null,
                              };
                            }
                          }
                          // Сбрасываем fittingLength для всех контуров, чтобы применились дефолты для квадратных
                          return {
                            ...circuit,
                            fittingLength: null,
                          };
                        });
                        setReturnCircuits(updatedReturnCircuits);
                        
                        // Обновляем дополнительные выходы обратного коллектора
                        const updatedReturnAdditionalOutlets = returnAdditionalOutlets.map((outlet) => {
                          if (outlet.type === 'fitting' && outlet.fittingId && !isCircuitFittingCompatibleWithSquareProfile(outlet.fittingId, newProfile)) {
                            const fittingsInCategory = getFittingsByCategory(outlet.fittingCategory || 'Ниппели', null, null);
                            const compatibleFittings = fittingsInCategory.filter(f => isCircuitFittingCompatibleWithSquareProfile(f.id, newProfile));
                            
                            if (compatibleFittings.length > 0) {
                              const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                              return {
                                ...outlet,
                                fittingId: maxCompatibleFitting.id,
                                diameter: fittingsData[maxCompatibleFitting.id]?.diameter || outlet.diameter,
                                fittingLength: null,
                              };
                            }
                          }
                          return outlet;
                        });
                        if (JSON.stringify(updatedReturnAdditionalOutlets) !== JSON.stringify(returnAdditionalOutlets)) {
                          setReturnAdditionalOutlets(updatedReturnAdditionalOutlets);
                        }
                      } else {
                        updateParam('supplyPipeProfile', newProfile);
                      }
                    }
                  }}
                  >
                    {params.shape === 'round' 
                      ? availableDiameters.map(d => (
                          <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>
                        ))
                      : availableSquareProfiles.map(p => (
                          <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                        ))
                    }
                  </Select>
                </FormControl>
              </Grid>
            )}
            
            {/* Кнопка синхронизации диаметров - скрыта в компактном режиме */}
            {!params.isCompact && (
              <Grid item xs={12} md={1} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Button
                  variant={diametersLinked ? 'contained' : 'outlined'}
                  onClick={() => {
                    const newLinkedState = !diametersLinked;
                    setDiametersLinked(newLinkedState);
                    // Если включаем синхронизацию, приравниваем диаметры (используем диаметр подающего)
                    if (newLinkedState && params.supplyPipeDiameter !== params.returnPipeDiameter) {
                      updateParam('returnPipeDiameter', params.supplyPipeDiameter);
                    }
                  }}
                  sx={{
                    minWidth: '3rem', // 48px
                    width: '3rem',
                    height: '3rem',
                    borderRadius: '0.25rem', // 4px
                    fontSize: '1.25rem', // 20px
                    fontWeight: 'bold',
                    padding: 0,
                  }}
                  title={diametersLinked ? 'Диаметры синхронизированы' : 'Диаметры независимы'}
                >
                  =
                </Button>
              </Grid>
            )}
            
            {/* Селект обратного коллектора - скрыт в компактном режиме */}
            {!params.isCompact && (
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>
                    {params.shape === 'round' 
                      ? 'Диаметр обратного коллектора' 
                      : 'Профиль обратного коллектора'
                    }
                  </InputLabel>
                  <Select
                    value={params.shape === 'round' ? params.returnPipeDiameter : (params.returnPipeProfile || '80x80')}
                    label={params.shape === 'round' 
                      ? 'Диаметр обратного коллектора' 
                      : 'Профиль обратного коллектора'
                    }
                    onChange={(e) => {
                      const newValue = e.target.value;
                    
                    if (params.shape === 'round') {
                      const newDiameter = newValue;
                    
                      // Обновляем центральные фитинги обратного коллектора
                      const updatedReturnCenterFittings = updateCenterFittingsForDiameter(
                        returnCenterFittings,
                        newDiameter,
                        isCenterFittingCompatible,
                        (category) => getFittingsByCategory(category, newDiameter, isCenterFittingCompatible)
                      );
                      if (JSON.stringify(updatedReturnCenterFittings) !== JSON.stringify(returnCenterFittings)) {
                        setReturnCenterFittings(updatedReturnCenterFittings);
                      }
                    
                      // Обновляем контуры обратного коллектора
                      const updatedReturnCircuits = returnCircuits.map((circuit) => {
                        const fittingId = circuit.fittingId || circuit.fitting; // Поддержка обоих полей
                        // Если есть fitting или fittingId и type не равен 'pipe', то это фитинг
                        if (fittingId && circuit.type !== 'pipe' && !isCircuitFittingCompatible(fittingId, newDiameter)) {
                          const fittingsInCategory = getFittingsByCategory(circuit.fittingCategory || 'Ниппели', newDiameter, isCircuitFittingCompatible);
                          const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                          
                          if (compatibleFittings.length > 0) {
                            const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                            return {
                              ...circuit,
                              fitting: maxCompatibleFitting.id,
                              fittingId: maxCompatibleFitting.id,
                              diameter: fittingsData[maxCompatibleFitting.id]?.diameter || circuit.diameter,
                              fittingLength: null,
                            };
                          } else {
                            return {
                              ...circuit,
                              type: 'pipe',
                              fitting: null,
                              fittingId: null,
                              fittingCategory: null,
                              fittingLength: null,
                              diameter: newDiameter,
                              length: 100,
                            };
                          }
                        }
                        return circuit;
                      });
                      if (JSON.stringify(updatedReturnCircuits) !== JSON.stringify(returnCircuits)) {
                        setReturnCircuits(updatedReturnCircuits);
                      }
                    
                      // Обновляем дополнительные выходы обратного коллектора
                      const updatedReturnAdditionalOutlets = returnAdditionalOutlets.map((outlet) => {
                        if (outlet.type === 'fitting' && outlet.fittingId && !isAdditionalOutletFittingCompatible(outlet.fittingId, newDiameter)) {
                          const fittingsInCategory = getFittingsByCategory(outlet.fittingCategory || 'Ниппели', newDiameter, isAdditionalOutletFittingCompatible);
                          const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                          
                          if (compatibleFittings.length > 0) {
                            const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                            return {
                              ...outlet,
                              fittingId: maxCompatibleFitting.id,
                              diameter: fittingsData[maxCompatibleFitting.id]?.diameter || outlet.diameter,
                              fittingLength: null,
                            };
                          } else {
                            return {
                              ...outlet,
                              type: 'pipe',
                              fittingId: null,
                              fittingCategory: null,
                              fittingLength: null,
                              diameter: newDiameter,
                              length: 100,
                            };
                          }
                        }
                        return outlet;
                      });
                      if (JSON.stringify(updatedReturnAdditionalOutlets) !== JSON.stringify(returnAdditionalOutlets)) {
                        setReturnAdditionalOutlets(updatedReturnAdditionalOutlets);
                      }
                    
                      // Обновляем торцевые подключения обратного коллектора
                      const returnEndConnectionUpdates = {};
                    
                      // Левое торцевое подключение обратного
                      if (params.hasReturnLeftConnection && params.returnLeftConnectionType === 'fitting' && params.returnLeftConnectionFitting) {
                        // Проверяем совместимость фитинга с новым диаметром
                        if (!isEndFittingCompatibleWithMainPipe(params.returnLeftConnectionFitting, newDiameter)) {
                          // Фитинг несовместим - пытаемся найти совместимый в той же категории
                          const currentCategory = params.returnLeftConnectionFittingCategory || 'Ниппели';
                          const fittingsInCategory = getFittingsByCategory(currentCategory, newDiameter, isEndFittingCompatibleWithMainPipe);
                          const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                          
                          if (compatibleFittings.length > 0) {
                            // Нашли совместимые фитинги - берем ПОСЛЕДНИЙ (максимальный)
                            const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                            returnEndConnectionUpdates.returnLeftConnectionFitting = maxCompatibleFitting.id;
                            returnEndConnectionUpdates.returnLeftConnectionFittingLength = null;
                          }
                        }
                        
                        const capUpdates = getCapUpdatesForConnectionFitting(
                          returnEndConnectionUpdates.returnLeftConnectionFitting || params.returnLeftConnectionFitting,
                          newDiameter,
                          'left',
                          'return',
                          params.shape
                        );
                        Object.assign(returnEndConnectionUpdates, capUpdates);
                      }
                      // Если заглушка установлена, но нет фитинга - обновляем тип заглушки на эллиптическую для больших диаметров
                      else if (params.hasReturnLeftCap && params.returnLeftCapElementType === 'cap' && params.shape !== 'square') {
                        const ellipticalCap = findEllipticalCap(newDiameter);
                        if (ellipticalCap && params.returnLeftCapType !== 'elliptical') {
                          returnEndConnectionUpdates.returnLeftCapType = 'elliptical';
                        }
                      }
                    
                      // Правое торцевое подключение обратного
                      if (params.hasReturnRightConnection && params.returnRightConnectionType === 'fitting' && params.returnRightConnectionFitting) {
                        // Проверяем совместимость фитинга с новым диаметром
                        if (!isEndFittingCompatibleWithMainPipe(params.returnRightConnectionFitting, newDiameter)) {
                          // Фитинг несовместим - пытаемся найти совместимый в той же категории
                          const currentCategory = params.returnRightConnectionFittingCategory || 'Ниппели';
                          const fittingsInCategory = getFittingsByCategory(currentCategory, newDiameter, isEndFittingCompatibleWithMainPipe);
                          const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                          
                          if (compatibleFittings.length > 0) {
                            // Нашли совместимые фитинги - берем ПОСЛЕДНИЙ (максимальный)
                            const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                            returnEndConnectionUpdates.returnRightConnectionFitting = maxCompatibleFitting.id;
                            returnEndConnectionUpdates.returnRightConnectionFittingLength = null;
                          }
                        }
                        
                        const capUpdates = getCapUpdatesForConnectionFitting(
                          returnEndConnectionUpdates.returnRightConnectionFitting || params.returnRightConnectionFitting,
                          newDiameter,
                          'right',
                          'return',
                          params.shape
                        );
                        Object.assign(returnEndConnectionUpdates, capUpdates);
                      }
                      // Если заглушка установлена, но нет фитинга - обновляем тип заглушки на эллиптическую для больших диаметров
                      else if (params.hasReturnRightCap && params.returnRightCapElementType === 'cap' && params.shape !== 'square') {
                        const ellipticalCap = findEllipticalCap(newDiameter);
                        if (ellipticalCap && params.returnRightCapType !== 'elliptical') {
                          returnEndConnectionUpdates.returnRightCapType = 'elliptical';
                        }
                      }
                    
                      if (Object.keys(returnEndConnectionUpdates).length > 0) {
                        updateParam(returnEndConnectionUpdates);
                      }
                    
                      if (diametersLinked) {
                        // Если синхронизация включена, меняем оба диаметра
                        updateParam({ supplyPipeDiameter: newDiameter, returnPipeDiameter: newDiameter });
                      
                        // Обновляем центральные фитинги подающего коллектора
                        const updatedSupplyCenterFittings = updateCenterFittingsForDiameter(
                          supplyCenterFittings,
                          newDiameter,
                          isCenterFittingCompatible,
                          (category) => getFittingsByCategory(category, newDiameter, isCenterFittingCompatible)
                        );
                        if (JSON.stringify(updatedSupplyCenterFittings) !== JSON.stringify(supplyCenterFittings)) {
                          setSupplyCenterFittings(updatedSupplyCenterFittings);
                        }
                      
                        // Обновляем контуры подающего коллектора
                        const updatedSupplyCircuits = supplyCircuits.map((circuit) => {
                          const fittingId = circuit.fittingId || circuit.fitting; // Поддержка обоих полей
                          // Если есть fitting или fittingId и type не равен 'pipe', то это фитинг
                          if (fittingId && circuit.type !== 'pipe' && !isCircuitFittingCompatible(fittingId, newDiameter)) {
                            const fittingsInCategory = getFittingsByCategory(circuit.fittingCategory || 'Ниппели', newDiameter, isCircuitFittingCompatible);
                            const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                            
                            if (compatibleFittings.length > 0) {
                              const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                              return {
                                ...circuit,
                                fitting: maxCompatibleFitting.id,
                                fittingId: maxCompatibleFitting.id,
                                diameter: fittingsData[maxCompatibleFitting.id]?.diameter || circuit.diameter,
                                fittingLength: null,
                              };
                            } else {
                              return {
                                ...circuit,
                                type: 'pipe',
                                fitting: null,
                                fittingId: null,
                                fittingCategory: null,
                                fittingLength: null,
                                diameter: newDiameter,
                                length: 100,
                              };
                            }
                          }
                          return circuit;
                        });
                        if (JSON.stringify(updatedSupplyCircuits) !== JSON.stringify(supplyCircuits)) {
                          setSupplyCircuits(updatedSupplyCircuits);
                        }
                      
                        // Обновляем дополнительные выходы подающего коллектора
                        const updatedSupplyAdditionalOutlets = supplyAdditionalOutlets.map((outlet) => {
                          if (outlet.type === 'fitting' && outlet.fittingId && !isAdditionalOutletFittingCompatible(outlet.fittingId, newDiameter)) {
                            const fittingsInCategory = getFittingsByCategory(outlet.fittingCategory || 'Ниппели', newDiameter, isAdditionalOutletFittingCompatible);
                            const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                            
                            if (compatibleFittings.length > 0) {
                              const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                              return {
                                ...outlet,
                                fittingId: maxCompatibleFitting.id,
                                diameter: fittingsData[maxCompatibleFitting.id]?.diameter || outlet.diameter,
                                fittingLength: null,
                              };
                            } else {
                              return {
                                ...outlet,
                                type: 'pipe',
                                fittingId: null,
                                fittingCategory: null,
                                fittingLength: null,
                                diameter: newDiameter,
                                length: 100,
                              };
                            }
                          }
                          return outlet;
                        });
                        if (JSON.stringify(updatedSupplyAdditionalOutlets) !== JSON.stringify(supplyAdditionalOutlets)) {
                          setSupplyAdditionalOutlets(updatedSupplyAdditionalOutlets);
                        }
                      
                        // Обновляем торцевые подключения подающего коллектора
                        const supplyEndConnectionUpdates = {};
                      
                        // Левое торцевое подключение подающего
                        if (params.hasSupplyLeftConnection && params.supplyLeftConnectionType === 'fitting' && params.supplyLeftConnectionFitting) {
                          // Проверяем совместимость фитинга с новым диаметром
                          if (!isEndFittingCompatibleWithMainPipe(params.supplyLeftConnectionFitting, newDiameter)) {
                            // Фитинг несовместим - пытаемся найти совместимый в той же категории
                            const currentCategory = params.supplyLeftConnectionFittingCategory || 'Ниппели';
                            const fittingsInCategory = getFittingsByCategory(currentCategory, newDiameter, isEndFittingCompatibleWithMainPipe);
                            const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                            
                            if (compatibleFittings.length > 0) {
                              // Нашли совместимые фитинги - берем ПОСЛЕДНИЙ (максимальный)
                              const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                              supplyEndConnectionUpdates.supplyLeftConnectionFitting = maxCompatibleFitting.id;
                              supplyEndConnectionUpdates.supplyLeftConnectionFittingLength = null;
                            }
                          }
                          
                          const capUpdates = getCapUpdatesForConnectionFitting(
                            supplyEndConnectionUpdates.supplyLeftConnectionFitting || params.supplyLeftConnectionFitting,
                            newDiameter,
                            'left',
                            'supply'
                          );
                          Object.assign(supplyEndConnectionUpdates, capUpdates);
                        }
                      
                        // Правое торцевое подключение подающего
                        if (params.hasSupplyRightConnection && params.supplyRightConnectionType === 'fitting' && params.supplyRightConnectionFitting) {
                          // Проверяем совместимость фитинга с новым диаметром
                          if (!isEndFittingCompatibleWithMainPipe(params.supplyRightConnectionFitting, newDiameter)) {
                            // Фитинг несовместим - пытаемся найти совместимый в той же категории
                            const currentCategory = params.supplyRightConnectionFittingCategory || 'Ниппели';
                            const fittingsInCategory = getFittingsByCategory(currentCategory, newDiameter, isEndFittingCompatibleWithMainPipe);
                            const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                            
                            if (compatibleFittings.length > 0) {
                              // Нашли совместимые фитинги - берем ПОСЛЕДНИЙ (максимальный)
                              const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                              supplyEndConnectionUpdates.supplyRightConnectionFitting = maxCompatibleFitting.id;
                              supplyEndConnectionUpdates.supplyRightConnectionFittingLength = null;
                            }
                          }
                          
                          const capUpdates = getCapUpdatesForConnectionFitting(
                            supplyEndConnectionUpdates.supplyRightConnectionFitting || params.supplyRightConnectionFitting,
                            newDiameter,
                            'right',
                            'supply'
                          );
                          Object.assign(supplyEndConnectionUpdates, capUpdates);
                        }
                      
                        if (Object.keys(supplyEndConnectionUpdates).length > 0) {
                          updateParam(supplyEndConnectionUpdates);
                        }
                      } else {
                        updateParam('returnPipeDiameter', newDiameter);
                      }
                    } else {
                      // Квадратный профиль - обновляем профиль и автопереключаем несовместимые фитинги
                      const newProfile = newValue;
                      
                      // Обновляем контуры обратного коллектора
                      const updatedReturnCircuits = returnCircuits.map((circuit) => {
                        const fittingId = circuit.fittingId || circuit.fitting;
                        if (fittingId && circuit.type !== 'pipe' && !isCircuitFittingCompatibleWithSquareProfile(fittingId, newProfile)) {
                          const fittingsInCategory = getFittingsByCategory(circuit.fittingCategory || 'Ниппели', null, null);
                          const compatibleFittings = fittingsInCategory.filter(f => isCircuitFittingCompatibleWithSquareProfile(f.id, newProfile));
                          
                          if (compatibleFittings.length > 0) {
                            const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                            return {
                              ...circuit,
                              fitting: maxCompatibleFitting.id,
                              fittingId: maxCompatibleFitting.id,
                              diameter: fittingsData[maxCompatibleFitting.id]?.diameter || circuit.diameter,
                              fittingLength: null,
                            };
                          }
                        }
                        // Сбрасываем fittingLength для всех контуров, чтобы применились дефолты для квадратных
                        return {
                          ...circuit,
                          fittingLength: null,
                        };
                      });
                      setReturnCircuits(updatedReturnCircuits);
                      
                      // Обновляем дополнительные выходы обратного коллектора
                      const updatedReturnAdditionalOutlets = returnAdditionalOutlets.map((outlet) => {
                        if (outlet.type === 'fitting' && outlet.fittingId && !isCircuitFittingCompatibleWithSquareProfile(outlet.fittingId, newProfile)) {
                          const fittingsInCategory = getFittingsByCategory(outlet.fittingCategory || 'Ниппели', null, null);
                          const compatibleFittings = fittingsInCategory.filter(f => isCircuitFittingCompatibleWithSquareProfile(f.id, newProfile));
                          
                          if (compatibleFittings.length > 0) {
                            const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                            return {
                              ...outlet,
                              fittingId: maxCompatibleFitting.id,
                              diameter: fittingsData[maxCompatibleFitting.id]?.diameter || outlet.diameter,
                              fittingLength: null,
                            };
                          }
                        }
                        return outlet;
                      });
                      if (JSON.stringify(updatedReturnAdditionalOutlets) !== JSON.stringify(returnAdditionalOutlets)) {
                        setReturnAdditionalOutlets(updatedReturnAdditionalOutlets);
                      }
                      
                      if (diametersLinked) {
                        updateParam({ supplyPipeProfile: newProfile, returnPipeProfile: newProfile });
                        
                        // Обновляем контуры подающего коллектора
                        const updatedSupplyCircuits = supplyCircuits.map((circuit) => {
                          const fittingId = circuit.fittingId || circuit.fitting;
                          if (fittingId && circuit.type !== 'pipe' && !isCircuitFittingCompatibleWithSquareProfile(fittingId, newProfile)) {
                            const fittingsInCategory = getFittingsByCategory(circuit.fittingCategory || 'Ниппели', null, null);
                            const compatibleFittings = fittingsInCategory.filter(f => isCircuitFittingCompatibleWithSquareProfile(f.id, newProfile));
                            
                            if (compatibleFittings.length > 0) {
                              const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                              return {
                                ...circuit,
                                fitting: maxCompatibleFitting.id,
                                fittingId: maxCompatibleFitting.id,
                                diameter: fittingsData[maxCompatibleFitting.id]?.diameter || circuit.diameter,
                                fittingLength: null,
                              };
                            }
                          }
                          // Сбрасываем fittingLength для всех контуров, чтобы применились дефолты для квадратных
                          return {
                            ...circuit,
                            fittingLength: null,
                          };
                        });
                        setSupplyCircuits(updatedSupplyCircuits);
                        
                        // Обновляем дополнительные выходы подающего коллектора
                        const updatedSupplyAdditionalOutlets = supplyAdditionalOutlets.map((outlet) => {
                          if (outlet.type === 'fitting' && outlet.fittingId && !isCircuitFittingCompatibleWithSquareProfile(outlet.fittingId, newProfile)) {
                            const fittingsInCategory = getFittingsByCategory(outlet.fittingCategory || 'Ниппели', null, null);
                            const compatibleFittings = fittingsInCategory.filter(f => isCircuitFittingCompatibleWithSquareProfile(f.id, newProfile));
                            
                            if (compatibleFittings.length > 0) {
                              const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                              return {
                                ...outlet,
                                fittingId: maxCompatibleFitting.id,
                                diameter: fittingsData[maxCompatibleFitting.id]?.diameter || outlet.diameter,
                                fittingLength: null,
                              };
                            }
                          }
                          return outlet;
                        });
                        if (JSON.stringify(updatedSupplyAdditionalOutlets) !== JSON.stringify(supplyAdditionalOutlets)) {
                          setSupplyAdditionalOutlets(updatedSupplyAdditionalOutlets);
                        }
                      } else {
                        updateParam('returnPipeProfile', newProfile);
                      }
                    }
                  }}
                  >
                    {params.shape === 'round' 
                      ? availableDiameters.map(d => (
                          <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>
                        ))
                      : availableSquareProfiles.map(p => (
                          <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                        ))
                    }
                  </Select>
                </FormControl>
              </Grid>
            )}
            
            {/* Поле "Расстояние между коллекторами" - скрыто в компактном режиме */}
            {!params.isCompact && (
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Расстояние между коллекторами (мм)"
                  type="number"
                  value={params.pipeSpacing}
                  onChange={(e) => updateParam('pipeSpacing', e.target.value)}
                />
              </Grid>
            )}
            
            {/* Разрыв строки */}
            <Grid item xs={12} />
            
            <Grid item xs={12} md={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={params.useManualLength}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      updateParam('useManualLength', checked);
                      // При включении ручного режима, инициализируем значения текущими расчетными
                      if (checked) {
                        updateParam('manualSupplyLength', Number(supplyCalculatedLength.toFixed(2)));
                        updateParam('manualReturnLength', Number(returnCalculatedLength.toFixed(2)));
                      }
                    }}
                  />
                }
                label="Ручная длина"
              />
            </Grid>
            
            <Grid item xs={12} md={1.5}>
              <TextField
                fullWidth
                label="Длина подающего (мм)"
                type="number"
                value={params.useManualLength ? (params.manualSupplyLength || '') : Number(supplyCalculatedLength.toFixed(2))}
                onChange={(e) => updateParam('manualSupplyLength', Number(e.target.value))}
                InputProps={{ readOnly: !params.useManualLength }}
                helperText={params.useManualLength ? "Ручной ввод" : "Автоматически"}
              />
            </Grid>
            
            <Grid item xs={12} md={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Button
                variant="outlined"
                onClick={equalizeLengths}
                disabled={supplyCalculatedLength === returnCalculatedLength}
                sx={{ 
                  minWidth: '2.5rem', // 40px
                  height: '2.5rem',
                  fontSize: '1.2rem',
                  fontWeight: 'bold',
                  bgcolor: lengthsEqualized ? 'success.light' : 'inherit',
                  '&:hover': {
                    bgcolor: lengthsEqualized ? 'success.main' : 'inherit',
                  }
                }}
                title={lengthsEqualized ? 'Длины приравнены' : 'Приравнять длины коллекторов'}
              >
                =
              </Button>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Длина обратного (мм)"
                type="number"
                value={params.useManualLength ? (params.manualReturnLength || '') : Number(returnCalculatedLength.toFixed(2))}
                onChange={(e) => updateParam('manualReturnLength', Number(e.target.value))}
                InputProps={{ readOnly: !params.useManualLength }}
                helperText={params.useManualLength ? "Ручной ввод" : "Автоматически"}
              />
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Вкладка 2: Контуры */}
      {activeTab === 1 && (
        <Paper sx={{ p: 3 }}>
          {/* Две колонки: Подача слева, Обратка справа */}
          <Grid container spacing={3}>
            {/* ЛЕВАЯ КОЛОНКА - КОЛЛЕКТОР ПОДАЧИ */}
            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: 1.5, height: '100%', bgcolor: '#fff5f5' }}>
                <Typography variant="subtitle1" color="error" sx={{ mb: 1.5, fontWeight: 600 }}>
                  Коллектор подачи ({supplyCircuits.length})
                </Typography>
                
                {/* Отступы */}
                <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 'bold', fontSize: '0.8rem' }}>
                  Отступы
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.75, mb: 1.5 }}>
                  <TextField
                    label="Первого контура"
                    type="number"
                    size="small"
                    value={params.supplyFirstCircuitOffsetMode === 'fromReturnCircuit' ? effectiveSupplyFirstCircuitOffset : params.supplyFirstCircuitOffset}
                    onChange={(e) => {
                      setLengthsEqualized(false);
                      updateParam('supplyFirstCircuitOffset', e.target.value);
                    }}
                    disabled={params.supplyFirstCircuitOffsetMode === 'fromReturnCircuit'}
                    fullWidth
                    InputProps={{ 
                      endAdornment: <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>мм</Typography>,
                      sx: { fontSize: '0.8rem' }
                    }}
                    InputLabelProps={{ sx: { fontSize: '0.8rem' } }}
                  />
                  <TextField
                    label="Последнего контура"
                    type="number"
                    size="small"
                    value={params.useManualLength ? Math.round(supplyLastCircuitOffsetCalculated) : params.supplyLastCircuitOffset}
                    onChange={(e) => {
                      setLengthsEqualized(false);
                      updateParam('supplyLastCircuitOffset', e.target.value);
                    }}
                    fullWidth
                    InputProps={{ 
                      readOnly: params.useManualLength,
                      endAdornment: <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>мм</Typography>,
                      sx: { fontSize: '0.8rem' }
                    }}
                    InputLabelProps={{ sx: { fontSize: '0.8rem' } }}
                    helperText={params.useManualLength ? "Авто (ручная длина)" : undefined}
                    FormHelperTextProps={{ sx: { fontSize: '0.65rem', mt: 0.25 } }}
                  />
                </Box>
                
                {/* Переключатель привязки к контуру обратки */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={params.supplyFirstCircuitOffsetMode === 'fromReturnCircuit'}
                        onChange={(e) => {
                          const newMode = e.target.checked ? 'fromReturnCircuit' : 'fromEdge';
                          
                          if (e.target.checked) {
                            // Включаем режим: выключаем режим обратки если включен (взаимоисключение)
                            setParams(prev => ({
                              ...prev,
                              // Выключаем режим обратки если включен
                              ...(prev.returnFirstCircuitOffsetMode === 'fromSupplyCircuit' ? {
                                returnFirstCircuitOffsetMode: 'fromEdge',
                                returnFirstCircuitOffsetLastUsed: prev.returnFirstCircuitOffset,
                                returnFirstCircuitOffset: prev.returnFirstCircuitOffsetSaved !== null 
                                  ? prev.returnFirstCircuitOffsetSaved 
                                  : prev.returnFirstCircuitOffset,
                                returnFirstCircuitOffsetSaved: null
                              } : {}),
                              // Включаем режим подачи
                              supplyFirstCircuitOffsetMode: newMode,
                              supplyFirstCircuitOffsetSaved: prev.supplyFirstCircuitOffset,
                              supplyFirstCircuitOffset: prev.supplyFirstCircuitOffsetLastUsed || 0,
                            }));
                            setLengthsEqualized(false);
                          } else {
                            // Выключаем режим: сохраняем текущее смещение для следующего раза
                            // и восстанавливаем абсолютный отступ
                            setParams(prev => ({
                              ...prev,
                              supplyFirstCircuitOffsetMode: newMode,
                              supplyFirstCircuitOffsetLastUsed: prev.supplyFirstCircuitOffset, // Сохраняем смещение
                              supplyFirstCircuitOffset: prev.supplyFirstCircuitOffsetSaved !== null 
                                ? prev.supplyFirstCircuitOffsetSaved 
                                : prev.supplyFirstCircuitOffset,
                              supplyFirstCircuitOffsetSaved: null // Очищаем сохранённое значение
                            }));
                            setLengthsEqualized(false);
                          }
                        }}
                        disabled={returnCircuits.length === 0}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                          Привязать к контуру обратки
                        </Typography>
                        {returnCircuits.length === 0 && (
                          <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'warning.main' }}>
                            (нет контуров)
                          </Typography>
                        )}
                      </Box>
                    }
                    sx={{ ml: 0, mb: 0 }}
                  />
                  
                  {/* Поля управления - показываются только когда режим включен */}
                  {params.supplyFirstCircuitOffsetMode === 'fromReturnCircuit' && returnCircuits.length > 0 && (
                    <>
                      {/* Выбор контура обратки */}
                      <FormControl size="small" sx={{ minWidth: 100 }}>
                        <InputLabel sx={{ fontSize: '0.8rem' }}>Контур</InputLabel>
                        <Select
                          value={params.supplyFirstCircuitOffsetReferenceIndex || 0}
                          onChange={(e) => {
                            updateParam('supplyFirstCircuitOffsetReferenceIndex', e.target.value);
                          }}
                          label="Контур"
                          sx={{ fontSize: '0.8rem' }}
                        >
                          {returnCircuits.map((circuit, index) => (
                            <MenuItem key={index} value={index} sx={{ fontSize: '0.8rem' }}>
                              {index + 1}-й
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      
                      {/* Поле смещения */}
                      <TextField
                        label="Отступ от контура"
                        type="number"
                        size="small"
                        value={params.supplyFirstCircuitOffset || 0}
                        onChange={(e) => {
                          setLengthsEqualized(false);
                          updateParam('supplyFirstCircuitOffset', Number(e.target.value));
                        }}
                        sx={{ width: 140 }}
                        InputProps={{ 
                          endAdornment: <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>мм</Typography>,
                          sx: { fontSize: '0.8rem' }
                        }}
                        InputLabelProps={{ sx: { fontSize: '0.8rem' } }}
                      />
                    </>
                  )}
                </Box>
                
                {/* Расстояния */}
                <Box sx={{ display: 'flex', gap: 0.75, mb: 1.5 }}>
                  <TextField
                    label="Межосевое контура"
                    type={supplyCircuitSpacingUniform ? "number" : "text"}
                    size="small"
                    value={supplyCircuitSpacingUniform ? params.supplyCircuitSpacing : ""}
                    placeholder={supplyCircuitSpacingUniform ? "" : "Произвольное значение"}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '') return;
                const numValue = Number(value);
                if (isNaN(numValue)) return;
                      setLengthsEqualized(false);
                      updateParam('supplyCircuitSpacing', numValue);
                      // Применяем ко всем контурам подачи
                      setSupplyCircuits(prev => prev.map(circuit => ({
                  ...circuit,
                        circuitSpacing: numValue,
                })));
              }}
                    helperText="Ко всем"
                    fullWidth
                    InputProps={{ 
                      endAdornment: supplyCircuitSpacingUniform ? <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>мм</Typography> : null,
                      sx: { fontSize: '0.8rem' }
                    }}
                    InputLabelProps={{ sx: { fontSize: '0.8rem' } }}
                    FormHelperTextProps={{ sx: { fontSize: '0.65rem', mt: 0.25 } }}
            />
            <TextField
                    label="Между контурами"
                    type={supplySpacingBetweenUniform ? "number" : "text"}
                    size="small"
                    value={supplySpacingBetweenUniform ? params.supplySpacingBetweenCircuits : ""}
                    placeholder={supplySpacingBetweenUniform ? "" : "Произвольное значение"}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '') return;
                const numValue = Number(value);
                if (isNaN(numValue)) return;
                      setLengthsEqualized(false);
                      updateParam('supplySpacingBetweenCircuits', numValue);
                      setSupplyCircuits(prev => prev.map(circuit => ({
                  ...circuit,
                        spacingToNext: numValue,
                })));
              }}
                    helperText="Ко всем"
                    fullWidth
                    InputProps={{ 
                      endAdornment: supplySpacingBetweenUniform ? <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>мм</Typography> : null,
                      sx: { fontSize: '0.8rem' }
                    }}
                    InputLabelProps={{ sx: { fontSize: '0.8rem' } }}
                    FormHelperTextProps={{ sx: { fontSize: '0.65rem', mt: 0.25 } }}
            />
          </Box>
          
                {/* Кнопка добавить */}
                <Button
                  variant="outlined"
                  startIcon={<AddIcon sx={{ fontSize: '1rem' }} />}
                  onClick={addSupplyCircuit}
                  size="small"
                  fullWidth
                  sx={{ mb: 1.5, py: 0.5, fontSize: '0.75rem' }}
                  color="error"
                >
                  Добавить контур
                </Button>
                
                {/* Таблица контуров */}
          <TableContainer>
                  <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.5, px: 0.5, fontSize: '0.75rem' } }}>
              <TableHead>
                <TableRow>
                        <TableCell width="25" sx={{ fontWeight: 600 }}>#</TableCell>
                        <TableCell width="90" sx={{ fontWeight: 600 }}>Категория</TableCell>
                        <TableCell width="100" sx={{ fontWeight: 600 }}>Фитинг</TableCell>
                        <TableCell width="70" sx={{ fontWeight: 600 }}>Вылет</TableCell>
                        <TableCell width="70" sx={{ fontWeight: 600 }}>Межос.</TableCell>
                        <TableCell width="70" sx={{ fontWeight: 600 }}>След.</TableCell>
                        <TableCell width="40" align="center" sx={{ fontWeight: 600 }}>Swap</TableCell>
                        <TableCell width="35"></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                      {supplyCircuits.map((circuit, index) => (
                  <TableRow 
                    key={circuit.id}
                    draggable
                    onDragStart={() => setDraggedSupplyCircuitIndex(index)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => {
                      if (draggedSupplyCircuitIndex !== null && draggedSupplyCircuitIndex !== index) {
                        moveSupplyCircuit(draggedSupplyCircuitIndex, index);
                      }
                      setDraggedSupplyCircuitIndex(null);
                    }}
                    onDragEnd={() => setDraggedSupplyCircuitIndex(null)}
                    sx={{ 
                      cursor: 'move',
                      opacity: draggedSupplyCircuitIndex === index ? 0.5 : 1,
                      '&:hover': { backgroundColor: 'action.hover' }
                    }}
                  >
                    <TableCell>{index + 1}</TableCell>
                    
                    {/* Колонка: Категория */}
                    <TableCell>
                      <FormControl fullWidth size="small">
                        <Select
                          value={circuit.fittingCategory || 'Ниппели'}
                          onChange={(e) => {
                            const newCategory = e.target.value;
                            const fittingsInNewCategory = getSupplyFittings(newCategory);
                            const firstCompatibleFitting = fittingsInNewCategory.find(f => f.isCompatible);
                            
                            if (!firstCompatibleFitting) {
                              setIncompatibilityDialog({
                                open: true,
                                message: 'Невозможно сварить изделие, в связи с ограничением потока',
                              });
                              return;
                            }
                            
                            const updates = {
                              fittingCategory: newCategory,
                              fitting: firstCompatibleFitting.id,
                              fittingLength: null,
                              flangePipeLength: null,
                            };
                            
                            updateSupplyCircuit(index, updates);
                          }}
                          sx={{ fontSize: '0.8rem' }}
                        >
                          {circuitFittingCategories.map(cat => (
                            <MenuItem key={cat.id} value={cat.id} sx={{ fontSize: '0.8rem' }}>
                              {cat.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    
                    {/* Колонка: Фитинг */}
                    <TableCell>
                      <FormControl fullWidth size="small">
                        <Select
                          value={circuit.fitting}
                          onChange={(e) => {
                            const newFittingId = e.target.value;
                            const fittingInfo = getSupplyFittings(circuit.fittingCategory || 'Ниппели').find(f => f.id === newFittingId);
                            
                            if (fittingInfo && !fittingInfo.isCompatible) {
                              setIncompatibilityDialog({
                                open: true,
                                message: 'Невозможно сварить изделие, в связи с ограничением потока',
                              });
                              return;
                            }
                            
                            const updates = { 
                              fitting: newFittingId,
                              flangePipeLength: null,
                            };
                            
                            updateSupplyCircuit(index, updates);
                          }}
                          sx={{ fontSize: '0.8rem' }}
                        >
                          {getSupplyFittings(circuit.fittingCategory || 'Ниппели').map(f => (
                            <MenuItem 
                              key={f.id} 
                              value={f.id} 
                              onClick={(e) => {
                                if (!f.isCompatible) {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setIncompatibilityDialog({
                                    open: true,
                                    message: 'Невозможно сварить изделие, в связи с ограничением потока',
                                  });
                                }
                              }}
                              sx={{ 
                                fontSize: '0.8rem',
                                ...(!f.isCompatible && {
                                  color: 'text.disabled',
                                  textDecoration: 'line-through',
                                  backgroundColor: 'transparent',
                                }),
                              }}
                            >
                                {f.label}
                              </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    
                    <TableCell>
                            {circuit.fitting?.startsWith('Flanec_') || circuit.fitting?.startsWith('Nak_Gaika_') || circuit.fitting?.startsWith('Shaib_Nar_Rezba_') ? (
                        <TextField
                          size="small"
                          type="number"
                                value={circuit.flangePipeLength === 0 ? '' : (circuit.flangePipeLength ?? '')}
                                onChange={(e) => updateSupplyCircuit(index, 'flangePipeLength', e.target.value ? Number(e.target.value) : null)}
                          placeholder="Авто"
                                sx={{ width: 75 }}
                        />
                      ) : (
                        <TextField
                          size="small"
                          type="number"
                                value={circuit.fittingLength === 0 ? '' : (circuit.fittingLength ?? '')}
                                onChange={(e) => updateSupplyCircuit(index, 'fittingLength', e.target.value ? Number(e.target.value) : null)}
                          placeholder="Авто"
                                sx={{ width: 75 }}
                        />
                      )}
                    </TableCell>
                    
                          <TableCell>
                            <TextField
                              size="small"
                              type="number"
                              value={circuit.circuitSpacing ?? 50}
                              onChange={(e) => updateSupplyCircuit(index, 'circuitSpacing', e.target.value ? Number(e.target.value) : 50)}
                              sx={{ width: 75 }}
                              title="Межосевое расстояние внутри контура (между двумя патрубками)"
                            />
                          </TableCell>
                          
                          <TableCell>
                            {index < supplyCircuits.length - 1 && (
                              <TextField
                                size="small"
                                type="number"
                                value={circuit.spacingToNext ?? ''}
                                onChange={(e) => updateSupplyCircuit(index, 'spacingToNext', e.target.value ? Number(e.target.value) : null)}
                                placeholder={String(params.supplySpacingBetweenCircuits)}
                                sx={{ width: 75 }}
                              />
                            )}
                          </TableCell>
                          
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              onClick={() => {
                                const currentValue = circuit.swapOrder ?? false;
                                updateSupplyCircuit(index, 'swapOrder', !currentValue);
                              }}
                              color={(circuit.swapOrder ?? false) ? 'error' : 'default'}
                              title={(circuit.swapOrder ?? false) ? 'Д-К' : 'К-Д'}
                            >
                              <SwapHorizIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                          
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => deleteSupplyCircuit(index)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
            
            {/* ПРАВАЯ КОЛОНКА - КОЛЛЕКТОР ОБРАТКИ */}
            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: 1.5, height: '100%', bgcolor: '#f5f5ff' }}>
                <Typography variant="subtitle1" color="primary" sx={{ mb: 1.5, fontWeight: 600 }}>
                  Коллектор обратки ({returnCircuits.length})
                </Typography>
                
                {/* Отступы */}
                <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 'bold', fontSize: '0.8rem' }}>
                  Отступы
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.75, mb: 1.5 }}>
                  <TextField
                    label="Первого контура"
                    type="number"
                    size="small"
                    value={params.returnFirstCircuitOffsetMode === 'fromSupplyCircuit' ? effectiveReturnFirstCircuitOffset : params.returnFirstCircuitOffset}
                    onChange={(e) => {
                      setLengthsEqualized(false);
                      updateParam('returnFirstCircuitOffset', e.target.value);
                    }}
                    disabled={params.returnFirstCircuitOffsetMode === 'fromSupplyCircuit'}
                    fullWidth
                    InputProps={{ 
                      endAdornment: <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>мм</Typography>,
                      sx: { fontSize: '0.8rem' }
                    }}
                    InputLabelProps={{ sx: { fontSize: '0.8rem' } }}
                  />
                  <TextField
                    label="Последнего контура"
                    type="number"
                    size="small"
                    value={params.useManualLength ? Math.round(returnLastCircuitOffsetCalculated) : params.returnLastCircuitOffset}
                    onChange={(e) => {
                      setLengthsEqualized(false);
                      updateParam('returnLastCircuitOffset', e.target.value);
                    }}
                    fullWidth
                    InputProps={{ 
                      readOnly: params.useManualLength,
                      endAdornment: <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>мм</Typography>,
                      sx: { fontSize: '0.8rem' }
                    }}
                    InputLabelProps={{ sx: { fontSize: '0.8rem' } }}
                    helperText={params.useManualLength ? "Авто (ручная длина)" : undefined}
                    FormHelperTextProps={{ sx: { fontSize: '0.65rem', mt: 0.25 } }}
                  />
                </Box>
                
                {/* Переключатель привязки к контуру подачи */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={params.returnFirstCircuitOffsetMode === 'fromSupplyCircuit'}
                        onChange={(e) => {
                          const newMode = e.target.checked ? 'fromSupplyCircuit' : 'fromEdge';
                          
                          if (e.target.checked) {
                            // Включаем режим: выключаем режим подачи если включен (взаимоисключение)
                            setParams(prev => ({
                              ...prev,
                              // Выключаем режим подачи если включен
                              ...(prev.supplyFirstCircuitOffsetMode === 'fromReturnCircuit' ? {
                                supplyFirstCircuitOffsetMode: 'fromEdge',
                                supplyFirstCircuitOffsetLastUsed: prev.supplyFirstCircuitOffset,
                                supplyFirstCircuitOffset: prev.supplyFirstCircuitOffsetSaved !== null 
                                  ? prev.supplyFirstCircuitOffsetSaved 
                                  : prev.supplyFirstCircuitOffset,
                                supplyFirstCircuitOffsetSaved: null
                              } : {}),
                              // Включаем режим обратки
                              returnFirstCircuitOffsetMode: newMode,
                              returnFirstCircuitOffsetSaved: prev.returnFirstCircuitOffset,
                              returnFirstCircuitOffset: prev.returnFirstCircuitOffsetLastUsed || 0,
                            }));
                            setLengthsEqualized(false);
                          } else {
                            // Выключаем режим
                            setParams(prev => ({
                              ...prev,
                              returnFirstCircuitOffsetMode: newMode,
                              returnFirstCircuitOffsetLastUsed: prev.returnFirstCircuitOffset,
                              returnFirstCircuitOffset: prev.returnFirstCircuitOffsetSaved !== null 
                                ? prev.returnFirstCircuitOffsetSaved 
                                : prev.returnFirstCircuitOffset,
                              returnFirstCircuitOffsetSaved: null
                            }));
                            setLengthsEqualized(false);
                          }
                        }}
                        disabled={supplyCircuits.length === 0}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                          Привязать к контуру подачи
                        </Typography>
                        {supplyCircuits.length === 0 && (
                          <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'warning.main' }}>
                            (нет контуров)
                          </Typography>
                        )}
                      </Box>
                    }
                    sx={{ ml: 0, mb: 0 }}
                  />
                  
                  {/* Поля управления - показываются только когда режим включен */}
                  {params.returnFirstCircuitOffsetMode === 'fromSupplyCircuit' && supplyCircuits.length > 0 && (
                    <>
                      {/* Выбор контура подачи */}
                      <FormControl size="small" sx={{ minWidth: 100 }}>
                        <InputLabel sx={{ fontSize: '0.8rem' }}>Контур</InputLabel>
                        <Select
                          value={params.returnFirstCircuitOffsetReferenceIndex || 0}
                          onChange={(e) => {
                            updateParam('returnFirstCircuitOffsetReferenceIndex', e.target.value);
                          }}
                          label="Контур"
                          sx={{ fontSize: '0.8rem' }}
                        >
                          {supplyCircuits.map((circuit, index) => (
                            <MenuItem key={index} value={index} sx={{ fontSize: '0.8rem' }}>
                              {index + 1}-й
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      
                      {/* Поле смещения */}
                      <TextField
                        label="Отступ от контура"
                        type="number"
                        size="small"
                        value={params.returnFirstCircuitOffset || 0}
                        onChange={(e) => {
                          setLengthsEqualized(false);
                          updateParam('returnFirstCircuitOffset', Number(e.target.value));
                        }}
                        sx={{ width: 140 }}
                        InputProps={{ 
                          endAdornment: <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>мм</Typography>,
                          sx: { fontSize: '0.8rem' }
                        }}
                        InputLabelProps={{ sx: { fontSize: '0.8rem' } }}
                      />
                    </>
                  )}
                </Box>
                
                {/* Расстояния */}
                <Box sx={{ display: 'flex', gap: 0.75, mb: 1.5 }}>
                  <TextField
                    label="Межосевое контура"
                    type={returnCircuitSpacingUniform ? "number" : "text"}
                    size="small"
                    value={returnCircuitSpacingUniform ? params.returnCircuitSpacing : ""}
                    placeholder={returnCircuitSpacingUniform ? "" : "Произвольное значение"}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '') return;
                      const numValue = Number(value);
                      if (isNaN(numValue)) return;
                      setLengthsEqualized(false);
                      updateParam('returnCircuitSpacing', numValue);
                      // Применяем ко всем контурам обратки
                      setReturnCircuits(prev => prev.map(circuit => ({
                        ...circuit,
                        circuitSpacing: numValue,
                      })));
                    }}
                    helperText="Ко всем"
                    fullWidth
                    InputProps={{ 
                      endAdornment: returnCircuitSpacingUniform ? <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>мм</Typography> : null,
                      sx: { fontSize: '0.8rem' }
                    }}
                    InputLabelProps={{ sx: { fontSize: '0.8rem' } }}
                    FormHelperTextProps={{ sx: { fontSize: '0.65rem', mt: 0.25 } }}
                  />
                  <TextField
                    label="Между контурами"
                    type={returnSpacingBetweenUniform ? "number" : "text"}
                    size="small"
                    value={returnSpacingBetweenUniform ? params.returnSpacingBetweenCircuits : ""}
                    placeholder={returnSpacingBetweenUniform ? "" : "Произвольное значение"}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '') return;
                      const numValue = Number(value);
                      if (isNaN(numValue)) return;
                      setLengthsEqualized(false);
                      updateParam('returnSpacingBetweenCircuits', numValue);
                      setReturnCircuits(prev => prev.map(circuit => ({
                        ...circuit,
                        spacingToNext: numValue,
                      })));
                    }}
                    helperText="Ко всем"
                    fullWidth
                    InputProps={{ 
                      endAdornment: returnSpacingBetweenUniform ? <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>мм</Typography> : null,
                      sx: { fontSize: '0.8rem' }
                    }}
                    InputLabelProps={{ sx: { fontSize: '0.8rem' } }}
                    FormHelperTextProps={{ sx: { fontSize: '0.65rem', mt: 0.25 } }}
                  />
                </Box>
                
                {/* Кнопка добавить */}
                <Button
                  variant="outlined"
                  startIcon={<AddIcon sx={{ fontSize: '1rem' }} />}
                  onClick={addReturnCircuit}
                  size="small"
                  fullWidth
                  sx={{ mb: 1.5, py: 0.5, fontSize: '0.75rem' }}
                  color="primary"
                >
                  Добавить контур
                </Button>
                
                {/* Таблица контуров */}
                <TableContainer>
                  <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.5, px: 0.5, fontSize: '0.75rem' } }}>
                    <TableHead>
                      <TableRow>
                        <TableCell width="25" sx={{ fontWeight: 600 }}>#</TableCell>
                        <TableCell width="90" sx={{ fontWeight: 600 }}>Категория</TableCell>
                        <TableCell width="100" sx={{ fontWeight: 600 }}>Фитинг</TableCell>
                        <TableCell width="70" sx={{ fontWeight: 600 }}>Вылет</TableCell>
                        <TableCell width="70" sx={{ fontWeight: 600 }}>Межос.</TableCell>
                        <TableCell width="70" sx={{ fontWeight: 600 }}>След.</TableCell>
                        <TableCell width="40" align="center" sx={{ fontWeight: 600 }}>Swap</TableCell>
                        <TableCell width="35"></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {returnCircuits.map((circuit, index) => (
                        <TableRow 
                          key={circuit.id}
                          draggable
                          onDragStart={() => setDraggedReturnCircuitIndex(index)}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={() => {
                            if (draggedReturnCircuitIndex !== null && draggedReturnCircuitIndex !== index) {
                              moveReturnCircuit(draggedReturnCircuitIndex, index);
                            }
                            setDraggedReturnCircuitIndex(null);
                          }}
                          onDragEnd={() => setDraggedReturnCircuitIndex(null)}
                          sx={{ 
                            cursor: 'move',
                            opacity: draggedReturnCircuitIndex === index ? 0.5 : 1,
                            '&:hover': { backgroundColor: 'action.hover' }
                          }}
                        >
                          <TableCell>{index + 1}</TableCell>
                          
                          {/* Колонка: Категория */}
                    <TableCell>
                      <FormControl fullWidth size="small">
                        <Select
                                value={circuit.fittingCategory || 'Ниппели'}
                                onChange={(e) => {
                                  const newCategory = e.target.value;
                                  const fittingsInNewCategory = getReturnFittings(newCategory);
                                  const firstCompatibleFitting = fittingsInNewCategory.find(f => f.isCompatible);
                                  
                                  if (!firstCompatibleFitting) {
                                    setIncompatibilityDialog({
                                      open: true,
                                      message: 'Невозможно сварить изделие, в связи с ограничением потока',
                                    });
                                    return;
                                  }
                                  
                                  const updates = {
                                    fittingCategory: newCategory,
                                    fitting: firstCompatibleFitting.id,
                                    fittingLength: null,
                                    flangePipeLength: null,
                                  };
                                  
                                  updateReturnCircuit(index, updates);
                                }}
                                sx={{ fontSize: '0.8rem' }}
                        >
                                {circuitFittingCategories.map(cat => (
                                  <MenuItem key={cat.id} value={cat.id} sx={{ fontSize: '0.8rem' }}>
                              {cat.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </TableCell>
                          
                          {/* Колонка: Фитинг */}
                          <TableCell>
                            <FormControl fullWidth size="small">
                              <Select
                                value={circuit.fitting}
                                onChange={(e) => {
                                  const newFittingId = e.target.value;
                                  const fittingInfo = getReturnFittings(circuit.fittingCategory || 'Ниппели').find(f => f.id === newFittingId);
                                  
                                  if (fittingInfo && !fittingInfo.isCompatible) {
                                    setIncompatibilityDialog({
                                      open: true,
                                      message: 'Невозможно сварить изделие, в связи с ограничением потока',
                                    });
                                    return;
                                  }
                                  
                                  const updates = { 
                                    fitting: newFittingId,
                                    flangePipeLength: null,
                                  };
                                  
                                  updateReturnCircuit(index, updates);
                                }}
                                sx={{ fontSize: '0.8rem' }}
                              >
                                {getReturnFittings(circuit.fittingCategory || 'Ниппели').map(f => (
                                  <MenuItem 
                                    key={f.id} 
                                    value={f.id} 
                                    onClick={(e) => {
                                      if (!f.isCompatible) {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setIncompatibilityDialog({
                                          open: true,
                                          message: 'Невозможно сварить изделие, в связи с ограничением потока',
                                        });
                                      }
                                    }}
                                    sx={{ 
                                      fontSize: '0.8rem',
                                      ...(!f.isCompatible && {
                                        color: 'text.disabled',
                                        textDecoration: 'line-through',
                                        backgroundColor: 'transparent',
                                      }),
                                    }}
                                  >
                                {f.label}
                              </MenuItem>
                                ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    
                    <TableCell>
                            {circuit.fitting?.startsWith('Flanec_') || circuit.fitting?.startsWith('Nak_Gaika_') || circuit.fitting?.startsWith('Shaib_Nar_Rezba_') ? (
                        <TextField
                          size="small"
                          type="number"
                                value={circuit.flangePipeLength === 0 ? '' : (circuit.flangePipeLength ?? '')}
                                onChange={(e) => updateReturnCircuit(index, 'flangePipeLength', e.target.value ? Number(e.target.value) : null)}
                          placeholder="Авто"
                                sx={{ width: 75 }}
                        />
                      ) : (
                        <TextField
                          size="small"
                          type="number"
                                value={circuit.fittingLength === 0 ? '' : (circuit.fittingLength ?? '')}
                                onChange={(e) => updateReturnCircuit(index, 'fittingLength', e.target.value ? Number(e.target.value) : null)}
                          placeholder="Авто"
                                sx={{ width: 75 }}
                        />
                      )}
                    </TableCell>
                    
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                              value={circuit.circuitSpacing ?? 50}
                              onChange={(e) => updateReturnCircuit(index, 'circuitSpacing', e.target.value ? Number(e.target.value) : 50)}
                              sx={{ width: 75 }}
                              title="Межосевое расстояние внутри контура (между двумя патрубками)"
                      />
                    </TableCell>
                    
                    <TableCell>
                            {index < returnCircuits.length - 1 && (
                        <TextField
                          size="small"
                          type="number"
                          value={circuit.spacingToNext ?? ''}
                                onChange={(e) => updateReturnCircuit(index, 'spacingToNext', e.target.value ? Number(e.target.value) : null)}
                                placeholder={String(params.returnSpacingBetweenCircuits)}
                                sx={{ width: 75 }}
                        />
                      )}
                    </TableCell>
                    
                    <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => {
                            const currentValue = circuit.swapOrder ?? false;
                                updateReturnCircuit(index, 'swapOrder', !currentValue);
                          }}
                          color={(circuit.swapOrder ?? false) ? 'primary' : 'default'}
                              title={(circuit.swapOrder ?? false) ? 'Д-К' : 'К-Д'}
                        >
                              <SwapHorizIcon fontSize="small" />
                        </IconButton>
                    </TableCell>
                    
                    <TableCell>
                      <IconButton
                        size="small"
                              onClick={() => deleteReturnCircuit(index)}
                      >
                              <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
              </Paper>
            </Grid>
          </Grid>
          
          {/* Центральные фитинги (термометры и лицевые подключения) */}
          <Divider sx={{ my: 3 }} />
          
          <Grid container spacing={3}>
            {/* ЛЕВАЯ КОЛОНКА - ПОДАЮЩИЙ КОЛЛЕКТОР */}
            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: 2, bgcolor: '#fff5f5' }}>
                <Typography variant="subtitle2" color="error" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Фитинги термометров и лицевых подключений (подача)
                </Typography>
                
                <CenterFittingsUI
                  centerFittings={supplyCenterFittings}
                  centerFittingCount={params.supplyCenterFittingCount}
                  onUpdateCount={updateSupplyCenterFittingCount}
                  onUpdateFitting={updateSupplyCenterFitting}
                  onDeleteFitting={deleteSupplyCenterFitting}
                  centerFittingCategories={centerFittingCategories}
                  getFittingsByCategory={(cat) => {
                    if (params.shape === 'square') {
                      // В компактном режиме используем более строгие ограничения для центральных фитингов
                      const checkFunction = params.isCompact 
                        ? (fId) => isCenterFittingCompatibleWithCompactSize(fId, params.compactSize)
                        : (fId) => isEndFittingCompatibleWithSquareProfile(fId, params.supplyPipeProfile);
                      
                      return getFittingsByCategory(cat, null, null).map(f => ({
                        ...f,
                        isCompatible: checkFunction(f.id)
                      }));
                    }
                    return getFittingsByCategory(cat, params.supplyPipeDiameter, isCenterFittingCompatible);
                  }}
                  isFittingCompatibleWithMainPipe={(fittingId) => {
                    if (params.shape === 'square') {
                      // В компактном режиме используем более строгие ограничения для центральных фитингов
                      return params.isCompact 
                        ? isCenterFittingCompatibleWithCompactSize(fittingId, params.compactSize)
                        : isEndFittingCompatibleWithSquareProfile(fittingId, params.supplyPipeProfile);
                    }
                    return isCenterFittingCompatible(fittingId, params.supplyPipeDiameter);
                  }}
                  isFittingCompatibleWithSquareProfile={isEndFittingCompatibleWithSquareProfile}
                  mainPipeDiameter={params.supplyPipeDiameter}
                  shape={params.shape}
                  mainPipeProfile={params.supplyPipeProfile}
                  circuitCount={supplyCircuits.length}
                  bottomCircuitCount={returnCircuits.length}
                  onIncompatibilityWarning={(message) => setIncompatibilityDialog({ open: true, message })}
                  mode="distribution"
                  currentRow="supply"
                />
              </Paper>
            </Grid>
            
            {/* ПРАВАЯ КОЛОНКА - ОБРАТНЫЙ КОЛЛЕКТОР */}
            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: 2, bgcolor: '#f5f5ff' }}>
                <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Фитинги термометров и лицевых подключений (обратка)
                </Typography>
                
                <CenterFittingsUI
                  centerFittings={returnCenterFittings}
                  centerFittingCount={params.returnCenterFittingCount}
                  onUpdateCount={updateReturnCenterFittingCount}
                  onUpdateFitting={updateReturnCenterFitting}
                  onDeleteFitting={deleteReturnCenterFitting}
                  centerFittingCategories={centerFittingCategories}
                  getFittingsByCategory={(cat) => {
                    if (params.shape === 'square') {
                      // В компактном режиме используем более строгие ограничения для центральных фитингов
                      const checkFunction = params.isCompact 
                        ? (fId) => isCenterFittingCompatibleWithCompactSize(fId, params.compactSize)
                        : (fId) => isEndFittingCompatibleWithSquareProfile(fId, params.returnPipeProfile);
                      
                      return getFittingsByCategory(cat, null, null).map(f => ({
                        ...f,
                        isCompatible: checkFunction(f.id)
                      }));
                    }
                    return getFittingsByCategory(cat, params.returnPipeDiameter, isCenterFittingCompatible);
                  }}
                  isFittingCompatibleWithMainPipe={(fittingId) => {
                    if (params.shape === 'square') {
                      // В компактном режиме используем более строгие ограничения для центральных фитингов
                      return params.isCompact 
                        ? isCenterFittingCompatibleWithCompactSize(fittingId, params.compactSize)
                        : isEndFittingCompatibleWithSquareProfile(fittingId, params.returnPipeProfile);
                    }
                    return isCenterFittingCompatible(fittingId, params.returnPipeDiameter);
                  }}
                  isFittingCompatibleWithSquareProfile={isEndFittingCompatibleWithSquareProfile}
                  mainPipeDiameter={params.returnPipeDiameter}
                  shape={params.shape}
                  mainPipeProfile={params.returnPipeProfile}
                  circuitCount={returnCircuits.length}
                  bottomCircuitCount={supplyCircuits.length}
                  onIncompatibilityWarning={(message) => setIncompatibilityDialog({ open: true, message })}
                  mode="distribution"
                  currentRow="return"
                />
              </Paper>
            </Grid>
          </Grid>

          {/* Дополнительные выходы */}
          <Divider sx={{ my: 3 }} />
          <Typography variant="h6" gutterBottom>Дополнительные выходы</Typography>
          
          <Grid container spacing={3}>
            {/* ЛЕВАЯ КОЛОНКА - ПОДАЮЩИЙ КОЛЛЕКТОР */}
            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: 2, bgcolor: '#fff5f5' }}>
                <Typography variant="subtitle2" color="error" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Дополнительные выходы (подача)
                </Typography>
                
                <AdditionalOutletsUI
                  outlets={supplyAdditionalOutlets}
                  outletCount={params.supplyAdditionalOutletCount}
                  onUpdateCount={updateSupplyAdditionalOutletCount}
                  onUpdateOutlet={updateSupplyAdditionalOutlet}
                  onDeleteOutlet={deleteSupplyAdditionalOutlet}
                  fittingCategories={circuitFittingCategories}
                  getFittingsByCategory={(cat, outlet) => {
                    if (params.shape === 'square') {
                      const isCompatible = params.isCompact
                        ? (fId) => isAdditionalOutletFittingCompatibleWithCompactSize(fId, params.compactSize)
                        : (fId) => isCircuitFittingCompatibleWithSquareProfile(fId, params.supplyPipeProfile);
                      return getFittingsByCategory(cat, null, null).map(f => ({
                        ...f,
                        isCompatible: isCompatible(f.id)
                      }));
                    }
                    const outletType = outlet?.outletType || 'short';
                    return getFittingsByCategory(cat, params.supplyPipeDiameter, (fId) => 
                      isAdditionalOutletFittingCompatible(fId, params.supplyPipeDiameter, outletType)
                    );
                  }}
                  isFittingCompatibleWithMainPipe={(fittingId, mainPipeDiameter, outlet) => {
                    if (params.shape === 'square') {
                      return params.isCompact
                        ? isAdditionalOutletFittingCompatibleWithCompactSize(fittingId, params.compactSize)
                        : isCircuitFittingCompatibleWithSquareProfile(fittingId, params.supplyPipeProfile);
                    }
                    const outletType = outlet?.outletType || 'short';
                    return isAdditionalOutletFittingCompatible(fittingId, params.supplyPipeDiameter, outletType);
                  }}
                  mainPipeDiameter={params.supplyPipeDiameter}
                  circuitCount={supplyCircuits.length}
                  bottomCircuitCount={returnCircuits.length}
                  onIncompatibilityWarning={(message) => setIncompatibilityDialog({ open: true, message })}
                  availableDiameters={availableDiameters}
                  fittingsData={fittingsData}
                  collectorType="supply"
                  mode="distribution"
                  currentRow="supply"
                  isCompact={params.isCompact}
                />
              </Paper>
            </Grid>
            
            {/* ПРАВАЯ КОЛОНКА - ОБРАТНЫЙ КОЛЛЕКТОР */}
            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: 2, bgcolor: '#f5f5ff' }}>
                <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Дополнительные выходы (обратка)
                </Typography>
                
                <AdditionalOutletsUI
                  outlets={returnAdditionalOutlets}
                  outletCount={params.returnAdditionalOutletCount}
                  onUpdateCount={updateReturnAdditionalOutletCount}
                  onUpdateOutlet={updateReturnAdditionalOutlet}
                  onDeleteOutlet={deleteReturnAdditionalOutlet}
                  fittingCategories={circuitFittingCategories}
                  getFittingsByCategory={(cat, outlet) => {
                    if (params.shape === 'square') {
                      const isCompatible = params.isCompact
                        ? (fId) => isAdditionalOutletFittingCompatibleWithCompactSize(fId, params.compactSize)
                        : (fId) => isCircuitFittingCompatibleWithSquareProfile(fId, params.returnPipeProfile);
                      return getFittingsByCategory(cat, null, null).map(f => ({
                        ...f,
                        isCompatible: isCompatible(f.id)
                      }));
                    }
                    const outletType = outlet?.outletType || 'short';
                    return getFittingsByCategory(cat, params.returnPipeDiameter, (fId) => 
                      isAdditionalOutletFittingCompatible(fId, params.returnPipeDiameter, outletType)
                    );
                  }}
                  isFittingCompatibleWithMainPipe={(fittingId, mainPipeDiameter, outlet) => {
                    if (params.shape === 'square') {
                      return params.isCompact
                        ? isAdditionalOutletFittingCompatibleWithCompactSize(fittingId, params.compactSize)
                        : isCircuitFittingCompatibleWithSquareProfile(fittingId, params.returnPipeProfile);
                    }
                    const outletType = outlet?.outletType || 'short';
                    return isAdditionalOutletFittingCompatible(fittingId, params.returnPipeDiameter, outletType);
                  }}
                  mainPipeDiameter={params.returnPipeDiameter}
                  circuitCount={returnCircuits.length}
                  bottomCircuitCount={supplyCircuits.length}
                  onIncompatibilityWarning={(message) => setIncompatibilityDialog({ open: true, message })}
                  availableDiameters={availableDiameters}
                  fittingsData={fittingsData}
                  collectorType="return"
                  mode="distribution"
                  currentRow="return"
                  isCompact={params.isCompact}
                />
              </Paper>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Вкладка 3: Подключения */}
      {activeTab === 2 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Торцевые подключения</Typography>
          
          {/* Для компактного режима торцевые подключения не поддерживаются */}
          {params.isCompact ? (
            <Box sx={{ 
              p: 4, 
              textAlign: 'center', 
              bgcolor: 'grey.100', 
              borderRadius: 2,
            border: '0.0625rem dashed',
              borderColor: 'grey.400'
            }}>
              <Typography variant="body1" color="text.secondary">
                Торцевые подключения не поддерживаются в компактном режиме
              </Typography>
            </Box>
          ) : (
            <>
              {/* Подающий коллектор */}
              <Typography variant="subtitle2" color="primary" sx={{ mt: 2, mb: 1 }}>
                Подающий коллектор - Подключение слева
              </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={4} sm={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={params.hasSupplyLeftConnection}
                    onChange={(e) => updateParam('hasSupplyLeftConnection', e.target.checked)}
                    size="small"
                  />
                }
                label={<Typography variant="body2">Вкл.</Typography>}
              />
            </Grid>
            {params.hasSupplyLeftConnection && (
              <>
                <Grid item xs={6} sm={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel shrink>Категория</InputLabel>
                        <Select
                          value={params.supplyLeftConnectionFittingCategory || ''}
                          label="Категория"
                          displayEmpty
                          notched
                          onChange={(e) => {
                            const newCategory = e.target.value;
                            updateParam({
                              supplyLeftConnectionFittingCategory: newCategory,
                              supplyLeftConnectionFitting: '',
                              supplyLeftConnectionFittingLength: null
                            });
                          }}
                        >
                          <MenuItem value="">
                            <em>Выбрать</em>
                          </MenuItem>
                          {circuitFittingCategories.map((cat) => (
                            <MenuItem key={cat.id} value={cat.id}>
                              {cat.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel shrink>Фитинг</InputLabel>
                        <Select
                          value={params.supplyLeftConnectionFitting || ''}
                          label="Фитинг"
                          displayEmpty
                          notched
                          onChange={(e) => {
                            const newFitting = e.target.value;
                            
                            // Проверяем совместимость фитинга с диаметром гребенки (только если фитинг выбран)
                            if (newFitting) {
                              const isCompatibleFitting = params.shape === 'square'
                                ? isEndFittingCompatibleWithSquareProfile(newFitting, params.supplyPipeProfile)
                                : isEndFittingCompatibleWithMainPipe(newFitting, params.supplyPipeDiameter);
                              
                              if (!isCompatibleFitting) {
                                setIncompatibilityDialog({
                                  open: true,
                                  message: 'Невозможно сварить изделие, в связи с основным диаметром гребенки',
                                });
                                return;
                              }
                            }
                            
                            const updates = {
                              supplyLeftConnectionFitting: newFitting,
                              supplyLeftConnectionFittingLength: null,
                              supplyLeftConnectionLength: null
                            };
                            
                            // Получаем автоматические обновления для заглушек/переходов
                            const capUpdates = getCapUpdatesForConnectionFitting(
                              newFitting,
                              params.supplyPipeDiameter,
                              'left',
                              'supply',
                              params.shape
                            );
                            
                            updateParam({ ...updates, ...capUpdates });
                          }}
                        >
                          <MenuItem value="">
                            <em>Выбрать</em>
                          </MenuItem>
                          {getFittingsByCategory(params.supplyLeftConnectionFittingCategory, params.supplyPipeDiameter).map((f) => {
                            const isCompatible = params.shape === 'square' 
                              ? isEndFittingCompatibleWithSquareProfile(f.id, params.supplyPipeProfile)
                              : isEndFittingCompatibleWithMainPipe(f.id, params.supplyPipeDiameter);
                            return (
                              <MenuItem 
                                key={f.id} 
                                value={f.id}
                                onClick={(e) => {
                                  if (!isCompatible) {
                                    e.stopPropagation();
                                    setIncompatibilityDialog({
                                      open: true,
                                      message: 'Невозможно сварить изделие, в связи с основным диаметром гребенки',
                                    });
                                  }
                                }}
                                sx={{ 
                                  textDecoration: !isCompatible ? 'line-through' : 'none',
                                  color: !isCompatible ? 'text.disabled' : 'inherit',
                                  cursor: !isCompatible ? 'not-allowed' : 'pointer',
                                  '&:hover': {
                                    backgroundColor: !isCompatible ? 'transparent' : undefined,
                                  },
                                }}
                              >
                                {f.label}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    </Grid>
                    {shouldShowConnectionLengthField(params.supplyLeftConnectionFitting, params.supplyPipeDiameter) && (
                      <Grid item xs={6} sm={2}>
                        <TextField
                          label="Вылет"
                          type="number"
                          size="small"
                          value={(() => {
                            const useConnectionLength = shouldUseConnectionLength(
                              params.supplyLeftConnectionFitting,
                              params.hasSupplyLeftCap,
                              params.supplyPipeDiameter
                            );
                            
                            if (useConnectionLength) {
                              // Для фланцев/гаек/шайб с переходами/заглушками
                              return params.supplyLeftConnectionLength === 0 ? '' : (params.supplyLeftConnectionLength ?? '');
                            } else {
                              // Для обычных фитингов
                              return params.supplyLeftConnectionFittingLength === 0 ? '' : (params.supplyLeftConnectionFittingLength ?? '');
                            }
                          })()}
                          onChange={(e) => {
                            const useConnectionLength = shouldUseConnectionLength(
                              params.supplyLeftConnectionFitting,
                              params.hasSupplyLeftCap,
                              params.supplyPipeDiameter
                            );
                            
                            if (useConnectionLength) {
                              updateParam('supplyLeftConnectionLength', e.target.value === '' ? null : e.target.value);
                            } else {
                              updateParam('supplyLeftConnectionFittingLength', e.target.value === '' ? null : e.target.value);
                            }
                          }}
                          placeholder="Авто"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          InputProps={{ endAdornment: <Typography variant="caption">мм</Typography> }}
                        />
                      </Grid>
                    )}
                  </>
            )}
          </Grid>

          <Typography variant="subtitle2" color="primary" sx={{ mt: 3, mb: 1 }}>
            Подающий коллектор - Подключение справа
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={4} sm={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={params.hasSupplyRightConnection}
                    onChange={(e) => updateParam('hasSupplyRightConnection', e.target.checked)}
                    size="small"
                  />
                }
                label={<Typography variant="body2">Вкл.</Typography>}
              />
            </Grid>
            {params.hasSupplyRightConnection && (
              <>
                <Grid item xs={6} sm={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel shrink>Категория</InputLabel>
                        <Select
                          value={params.supplyRightConnectionFittingCategory || ''}
                          label="Категория"
                          displayEmpty
                          notched
                          onChange={(e) => {
                            const newCategory = e.target.value;
                            updateParam({
                              supplyRightConnectionFittingCategory: newCategory,
                              supplyRightConnectionFitting: '',
                              supplyRightConnectionFittingLength: null
                            });
                          }}
                        >
                          <MenuItem value="">
                            <em>Выбрать</em>
                          </MenuItem>
                          {circuitFittingCategories.map((cat) => (
                            <MenuItem key={cat.id} value={cat.id}>
                              {cat.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel shrink>Фитинг</InputLabel>
                        <Select
                          value={params.supplyRightConnectionFitting || ''}
                          label="Фитинг"
                          displayEmpty
                          notched
                          onChange={(e) => {
                            const newFitting = e.target.value;
                            
                            // Проверяем совместимость фитинга с диаметром гребенки (только если фитинг выбран)
                            if (newFitting) {
                              const isCompatibleFitting = params.shape === 'square'
                                ? isEndFittingCompatibleWithSquareProfile(newFitting, params.supplyPipeProfile)
                                : isEndFittingCompatibleWithMainPipe(newFitting, params.supplyPipeDiameter);
                              
                              if (!isCompatibleFitting) {
                                setIncompatibilityDialog({
                                  open: true,
                                  message: 'Невозможно сварить изделие, в связи с основным диаметром гребенки',
                                });
                                return;
                              }
                            }
                            
                            const updates = {
                              supplyRightConnectionFitting: newFitting,
                              supplyRightConnectionFittingLength: null,
                              supplyRightConnectionLength: null
                            };
                            
                            const capUpdates = getCapUpdatesForConnectionFitting(
                              newFitting,
                              params.supplyPipeDiameter,
                              'right',
                              'supply',
                              params.shape
                            );
                            
                            updateParam({ ...updates, ...capUpdates });
                          }}
                        >
                          <MenuItem value="">
                            <em>Выбрать</em>
                          </MenuItem>
                          {getFittingsByCategory(params.supplyRightConnectionFittingCategory, params.supplyPipeDiameter).map((f) => {
                            const isCompatible = params.shape === 'square' 
                              ? isEndFittingCompatibleWithSquareProfile(f.id, params.supplyPipeProfile)
                              : isEndFittingCompatibleWithMainPipe(f.id, params.supplyPipeDiameter);
                            return (
                              <MenuItem 
                                key={f.id} 
                                value={f.id}
                                onClick={(e) => {
                                  if (!isCompatible) {
                                    e.stopPropagation();
                                    setIncompatibilityDialog({
                                      open: true,
                                      message: 'Невозможно сварить изделие, в связи с основным диаметром гребенки',
                                    });
                                  }
                                }}
                                sx={{ 
                                  textDecoration: !isCompatible ? 'line-through' : 'none',
                                  color: !isCompatible ? 'text.disabled' : 'inherit',
                                  cursor: !isCompatible ? 'not-allowed' : 'pointer',
                                  '&:hover': {
                                    backgroundColor: !isCompatible ? 'transparent' : undefined,
                                  },
                                }}
                              >
                                {f.label}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    </Grid>
                    {shouldShowConnectionLengthField(params.supplyRightConnectionFitting, params.supplyPipeDiameter) && (
                      <Grid item xs={6} sm={2}>
                        <TextField
                          label="Вылет"
                          type="number"
                          size="small"
                          value={(() => {
                            const useConnectionLength = shouldUseConnectionLength(
                              params.supplyRightConnectionFitting,
                              params.hasSupplyRightCap,
                              params.supplyPipeDiameter
                            );
                            
                            if (useConnectionLength) {
                              return params.supplyRightConnectionLength === 0 ? '' : (params.supplyRightConnectionLength ?? '');
                            } else {
                              return params.supplyRightConnectionFittingLength === 0 ? '' : (params.supplyRightConnectionFittingLength ?? '');
                            }
                          })()}
                          onChange={(e) => {
                            const useConnectionLength = shouldUseConnectionLength(
                              params.supplyRightConnectionFitting,
                              params.hasSupplyRightCap,
                              params.supplyPipeDiameter
                            );
                            const paramName = useConnectionLength 
                              ? 'supplyRightConnectionLength' 
                              : 'supplyRightConnectionFittingLength';
                            updateParam(paramName, e.target.value === '' ? null : e.target.value);
                          }}
                          placeholder="Авто"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          InputProps={{ endAdornment: <Typography variant="caption">мм</Typography> }}
                        />
                      </Grid>
                    )}
                  </>
            )}
          </Grid>

          <Divider sx={{ my: 3 }} />
          
          {/* Кнопка синхронизации торцевых подключений */}
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2 }}>
            <Button
              variant={(leftConnectionsLinked && rightConnectionsLinked) ? 'contained' : 'outlined'}
              onClick={() => {
                const newLinkedState = !(leftConnectionsLinked && rightConnectionsLinked);
                setLeftConnectionsLinked(newLinkedState);
                setRightConnectionsLinked(newLinkedState);
                // Если включаем синхронизацию, копируем настройки подающего в обратный
                if (newLinkedState) {
                  updateParam({
                    // Левые подключения
                    hasReturnLeftConnection: params.hasSupplyLeftConnection,
                    returnLeftConnectionType: params.supplyLeftConnectionType,
                    returnLeftConnectionPipeDiameter: params.supplyLeftConnectionPipeDiameter,
                    returnLeftConnectionPipeLength: params.supplyLeftConnectionPipeLength,
                    returnLeftConnectionFittingCategory: params.supplyLeftConnectionFittingCategory,
                    returnLeftConnectionFitting: params.supplyLeftConnectionFitting,
                    returnLeftConnectionFittingLength: params.supplyLeftConnectionFittingLength,
                    returnLeftConnectionLength: params.supplyLeftConnectionLength,
                    // Левые заглушки и переходы
                    hasReturnLeftCap: params.hasSupplyLeftCap,
                    returnLeftCapType: params.supplyLeftCapType,
                    returnLeftCapElementType: params.supplyLeftCapElementType,
                    returnLeftReducer: params.supplyLeftReducer,
                    // Правые подключения
                    hasReturnRightConnection: params.hasSupplyRightConnection,
                    returnRightConnectionType: params.supplyRightConnectionType,
                    returnRightConnectionPipeDiameter: params.supplyRightConnectionPipeDiameter,
                    returnRightConnectionPipeLength: params.supplyRightConnectionPipeLength,
                    returnRightConnectionFittingCategory: params.supplyRightConnectionFittingCategory,
                    returnRightConnectionFitting: params.supplyRightConnectionFitting,
                    returnRightConnectionFittingLength: params.supplyRightConnectionFittingLength,
                    returnRightConnectionLength: params.supplyRightConnectionLength,
                    // Правые заглушки и переходы
                    hasReturnRightCap: params.hasSupplyRightCap,
                    returnRightCapType: params.supplyRightCapType,
                    returnRightCapElementType: params.supplyRightCapElementType,
                    returnRightReducer: params.supplyRightReducer,
                  });
                }
              }}
              sx={{
                minWidth: '48px',
                width: '48px',
                height: '48px',
                borderRadius: '4px',
                fontSize: '20px',
                fontWeight: 'bold',
                padding: 0,
              }}
              title={(leftConnectionsLinked && rightConnectionsLinked) ? 'Торцевые подключения синхронизированы' : 'Торцевые подключения независимы'}
            >
              =
            </Button>
          </Box>

          {/* Обратный коллектор */}
          <Typography variant="subtitle2" color="primary" sx={{ mt: 2, mb: 1 }}>
            Обратный коллектор - Подключение слева
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={4} sm={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={params.hasReturnLeftConnection}
                    onChange={(e) => updateParam('hasReturnLeftConnection', e.target.checked)}
                    size="small"
                  />
                }
                label={<Typography variant="body2">Вкл.</Typography>}
              />
            </Grid>
            {params.hasReturnLeftConnection && (
              <>
                <Grid item xs={6} sm={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel shrink>Категория</InputLabel>
                        <Select
                          value={params.returnLeftConnectionFittingCategory || ''}
                          label="Категория"
                          displayEmpty
                          notched
                          onChange={(e) => {
                            const newCategory = e.target.value;
                            updateParam({
                              returnLeftConnectionFittingCategory: newCategory,
                              returnLeftConnectionFitting: '',
                              returnLeftConnectionFittingLength: null
                            });
                          }}
                        >
                          <MenuItem value="">
                            <em>Выбрать</em>
                          </MenuItem>
                          {circuitFittingCategories.map((cat) => (
                            <MenuItem key={cat.id} value={cat.id}>
                              {cat.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel shrink>Фитинг</InputLabel>
                        <Select
                          value={params.returnLeftConnectionFitting || ''}
                          label="Фитинг"
                          displayEmpty
                          notched
                          onChange={(e) => {
                            const newFitting = e.target.value;
                            
                            // Проверяем совместимость фитинга с диаметром гребенки (только если фитинг выбран)
                            if (newFitting) {
                              const isCompatibleFitting = params.shape === 'square'
                                ? isEndFittingCompatibleWithSquareProfile(newFitting, params.returnPipeProfile)
                                : isEndFittingCompatibleWithMainPipe(newFitting, params.returnPipeDiameter);
                              
                              if (!isCompatibleFitting) {
                                setIncompatibilityDialog({
                                  open: true,
                                  message: 'Невозможно сварить изделие, в связи с основным диаметром гребенки',
                                });
                                return;
                              }
                            }
                            
                            const updates = {
                              returnLeftConnectionFitting: newFitting,
                              returnLeftConnectionFittingLength: null,
                              returnLeftConnectionLength: null
                            };
                            
                            const capUpdates = getCapUpdatesForConnectionFitting(
                              newFitting,
                              params.returnPipeDiameter,
                              'left',
                              'return',
                              params.shape
                            );
                            
                            updateParam({ ...updates, ...capUpdates });
                          }}
                        >
                          <MenuItem value="">
                            <em>Выбрать</em>
                          </MenuItem>
                          {getFittingsByCategory(params.returnLeftConnectionFittingCategory, params.returnPipeDiameter).map((f) => {
                            const isCompatible = params.shape === 'square' 
                              ? isEndFittingCompatibleWithSquareProfile(f.id, params.returnPipeProfile)
                              : isEndFittingCompatibleWithMainPipe(f.id, params.returnPipeDiameter);
                            return (
                              <MenuItem 
                                key={f.id} 
                                value={f.id}
                                onClick={(e) => {
                                  if (!isCompatible) {
                                    e.stopPropagation();
                                    setIncompatibilityDialog({
                                      open: true,
                                      message: 'Невозможно сварить изделие, в связи с основным диаметром гребенки',
                                    });
                                  }
                                }}
                                sx={{ 
                                  textDecoration: !isCompatible ? 'line-through' : 'none',
                                  color: !isCompatible ? 'text.disabled' : 'inherit',
                                  cursor: !isCompatible ? 'not-allowed' : 'pointer',
                                  '&:hover': {
                                    backgroundColor: !isCompatible ? 'transparent' : undefined,
                                  },
                                }}
                              >
                                {f.label}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    </Grid>
                    {shouldShowConnectionLengthField(params.returnLeftConnectionFitting, params.returnPipeDiameter) && (
                      <Grid item xs={6} sm={2}>
                        <TextField
                          label="Вылет"
                          type="number"
                          size="small"
                          value={(() => {
                            const useConnectionLength = shouldUseConnectionLength(
                              params.returnLeftConnectionFitting,
                              params.hasReturnLeftCap,
                              params.returnPipeDiameter
                            );
                            
                            if (useConnectionLength) {
                              return params.returnLeftConnectionLength === 0 ? '' : (params.returnLeftConnectionLength ?? '');
                            } else {
                              return params.returnLeftConnectionFittingLength === 0 ? '' : (params.returnLeftConnectionFittingLength ?? '');
                            }
                          })()}
                          onChange={(e) => {
                            const useConnectionLength = shouldUseConnectionLength(
                              params.returnLeftConnectionFitting,
                              params.hasReturnLeftCap,
                              params.returnPipeDiameter
                            );
                            const paramName = useConnectionLength 
                              ? 'returnLeftConnectionLength' 
                              : 'returnLeftConnectionFittingLength';
                            updateParam(paramName, e.target.value === '' ? null : e.target.value);
                          }}
                          placeholder="Авто"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          InputProps={{ endAdornment: <Typography variant="caption">мм</Typography> }}
                        />
                      </Grid>
                    )}
                  </>
            )}
          </Grid>

          <Typography variant="subtitle2" color="primary" sx={{ mt: 3, mb: 1 }}>
            Обратный коллектор - Подключение справа
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={4} sm={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={params.hasReturnRightConnection}
                    onChange={(e) => updateParam('hasReturnRightConnection', e.target.checked)}
                    size="small"
                  />
                }
                label={<Typography variant="body2">Вкл.</Typography>}
              />
            </Grid>
            {params.hasReturnRightConnection && (
              <>
                <Grid item xs={6} sm={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel shrink>Категория</InputLabel>
                        <Select
                          value={params.returnRightConnectionFittingCategory || ''}
                          label="Категория"
                          displayEmpty
                          notched
                          onChange={(e) => {
                            const newCategory = e.target.value;
                            updateParam({
                              returnRightConnectionFittingCategory: newCategory,
                              returnRightConnectionFitting: '',
                              returnRightConnectionFittingLength: null
                            });
                          }}
                        >
                          <MenuItem value="">
                            <em>Выбрать</em>
                          </MenuItem>
                          {circuitFittingCategories.map((cat) => (
                            <MenuItem key={cat.id} value={cat.id}>
                              {cat.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel shrink>Фитинг</InputLabel>
                        <Select
                          value={params.returnRightConnectionFitting || ''}
                          label="Фитинг"
                          displayEmpty
                          notched
                          onChange={(e) => {
                            const newFitting = e.target.value;
                            
                            // Проверяем совместимость фитинга с диаметром гребенки (только если фитинг выбран)
                            if (newFitting) {
                              const isCompatibleFitting = params.shape === 'square'
                                ? isEndFittingCompatibleWithSquareProfile(newFitting, params.returnPipeProfile)
                                : isEndFittingCompatibleWithMainPipe(newFitting, params.returnPipeDiameter);
                              
                              if (!isCompatibleFitting) {
                                setIncompatibilityDialog({
                                  open: true,
                                  message: 'Невозможно сварить изделие, в связи с основным диаметром гребенки',
                                });
                                return;
                              }
                            }
                            
                            const updates = {
                              returnRightConnectionFitting: newFitting,
                              returnRightConnectionFittingLength: null,
                              returnRightConnectionLength: null
                            };
                            
                            const capUpdates = getCapUpdatesForConnectionFitting(
                              newFitting,
                              params.returnPipeDiameter,
                              'right',
                              'return',
                              params.shape
                            );
                            
                            updateParam({ ...updates, ...capUpdates });
                          }}
                        >
                          <MenuItem value="">
                            <em>Выбрать</em>
                          </MenuItem>
                          {getFittingsByCategory(params.returnRightConnectionFittingCategory, params.returnPipeDiameter).map((f) => {
                            const isCompatible = params.shape === 'square' 
                              ? isEndFittingCompatibleWithSquareProfile(f.id, params.returnPipeProfile)
                              : isEndFittingCompatibleWithMainPipe(f.id, params.returnPipeDiameter);
                            return (
                              <MenuItem 
                                key={f.id} 
                                value={f.id}
                                onClick={(e) => {
                                  if (!isCompatible) {
                                    e.stopPropagation();
                                    setIncompatibilityDialog({
                                      open: true,
                                      message: 'Невозможно сварить изделие, в связи с основным диаметром гребенки',
                                    });
                                  }
                                }}
                                sx={{ 
                                  textDecoration: !isCompatible ? 'line-through' : 'none',
                                  color: !isCompatible ? 'text.disabled' : 'inherit',
                                  cursor: !isCompatible ? 'not-allowed' : 'pointer',
                                  '&:hover': {
                                    backgroundColor: !isCompatible ? 'transparent' : undefined,
                                  },
                                }}
                              >
                                {f.label}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    </Grid>
                    {shouldShowConnectionLengthField(params.returnRightConnectionFitting, params.returnPipeDiameter) && (
                      <Grid item xs={6} sm={2}>
                        <TextField
                          label="Вылет"
                          type="number"
                          size="small"
                          value={(() => {
                            const useConnectionLength = shouldUseConnectionLength(
                              params.returnRightConnectionFitting,
                              params.hasReturnRightCap,
                              params.returnPipeDiameter
                            );
                            
                            if (useConnectionLength) {
                              return params.returnRightConnectionLength === 0 ? '' : (params.returnRightConnectionLength ?? '');
                            } else {
                              return params.returnRightConnectionFittingLength === 0 ? '' : (params.returnRightConnectionFittingLength ?? '');
                            }
                          })()}
                          onChange={(e) => {
                            const useConnectionLength = shouldUseConnectionLength(
                              params.returnRightConnectionFitting,
                              params.hasReturnRightCap,
                              params.returnPipeDiameter
                            );
                            const paramName = useConnectionLength 
                              ? 'returnRightConnectionLength' 
                              : 'returnRightConnectionFittingLength';
                            updateParam(paramName, e.target.value === '' ? null : e.target.value);
                          }}
                          placeholder="Авто"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          InputProps={{ endAdornment: <Typography variant="caption">мм</Typography> }}
                        />
                      </Grid>
                    )}
                  </>
            )}
          </Grid>
            </>
          )}
        </Paper>
      )}

      {/* Вкладка 4: Заглушки */}
      {activeTab === 3 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Заглушки и переходы конц.</Typography>
          
          {/* В компактном режиме - просто "Заглушки", иначе - "Подающий коллектор" */}
          {!params.isCompact && (
            <Typography variant="subtitle2" color="primary" sx={{ mt: 2, mb: 1 }}>
              Подающий коллектор
            </Typography>
          )}
          <Grid container spacing={2}>
            {/* Левая сторона */}
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={params.hasSupplyLeftCap}
                      onChange={(e) => updateParam('hasSupplyLeftCap', e.target.checked)}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">Слева</Typography>}
                />
                {params.hasSupplyLeftCap && (
                  <>
                    {/* Для квадратных коллекторов только прямые заглушки */}
                    {params.shape === 'square' ? (
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                        Прямая заглушка
                      </Typography>
                    ) : (
                      <>
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <InputLabel>Элемент</InputLabel>
                          <Select
                            value={params.supplyLeftCapElementType || 'cap'}
                            label="Элемент"
                            onChange={(e) => updateParam('supplyLeftCapElementType', e.target.value)}
                          >
                            <MenuItem value="cap">Заглушка</MenuItem>
                            <MenuItem value="reducer">Переход конц.</MenuItem>
                          </Select>
                        </FormControl>
                        {params.supplyLeftCapElementType === 'cap' || !params.supplyLeftCapElementType ? (
                          <FormControl size="small" sx={{ minWidth: 130 }}>
                            <InputLabel>Тип</InputLabel>
                            <Select
                              value={params.supplyLeftCapType}
                              label="Тип"
                              onChange={(e) => updateParam('supplyLeftCapType', e.target.value)}
                            >
                              {params.supplyPipeDiameter <= 101.6 && (
                                <MenuItem value="straight">Прямая</MenuItem>
                              )}
                              <MenuItem value="elliptical">Эллиптическая</MenuItem>
                            </Select>
                          </FormControl>
                        ) : (
                          <FormControl size="small" sx={{ minWidth: 150 }}>
                            <InputLabel>Переход</InputLabel>
                            <Select
                              value={params.supplyLeftReducer || concentricReducers[0]?.id}
                              label="Переход"
                              onChange={(e) => updateParam('supplyLeftReducer', e.target.value)}
                            >
                              {concentricReducers.map((r) => (
                                <MenuItem key={r.id} value={r.id}>{r.label}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      </>
                    )}
                  </>
                )}
              </Box>
              {params.hasSupplyLeftCap && (
                <Typography variant="caption" color="text.secondary">
                  {params.supplyLeftCapElementType === 'reducer'
                    ? `${fittingsData[params.supplyLeftReducer]?.displayName || 'Переход'}`
                    : params.supplyLeftCapType === 'straight' 
                      ? params.shape === 'square'
                        ? `Заглушка прямая ${params.isCompact ? params.compactSize : params.supplyPipeProfile}`
                        : `Заглушка прямая ⌀${params.supplyPipeDiameter}`
                      : `Заглушка эллиптическая ⌀${params.supplyPipeDiameter}`
                  }
                </Typography>
              )}
            </Grid>
            
            {/* Правая сторона */}
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={params.hasSupplyRightCap}
                      onChange={(e) => updateParam('hasSupplyRightCap', e.target.checked)}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">Справа</Typography>}
                />
                {params.hasSupplyRightCap && (
                  <>
                    {/* Для квадратных коллекторов только прямые заглушки */}
                    {params.shape === 'square' ? (
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                        Прямая заглушка
                      </Typography>
                    ) : (
                      <>
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <InputLabel>Элемент</InputLabel>
                          <Select
                            value={params.supplyRightCapElementType || 'cap'}
                            label="Элемент"
                            onChange={(e) => updateParam('supplyRightCapElementType', e.target.value)}
                          >
                            <MenuItem value="cap">Заглушка</MenuItem>
                            <MenuItem value="reducer">Переход конц.</MenuItem>
                          </Select>
                        </FormControl>
                        {params.supplyRightCapElementType === 'cap' || !params.supplyRightCapElementType ? (
                          <FormControl size="small" sx={{ minWidth: 130 }}>
                            <InputLabel>Тип</InputLabel>
                            <Select
                              value={params.supplyRightCapType}
                              label="Тип"
                              onChange={(e) => updateParam('supplyRightCapType', e.target.value)}
                            >
                              {params.supplyPipeDiameter <= 101.6 && (
                                <MenuItem value="straight">Прямая</MenuItem>
                              )}
                              <MenuItem value="elliptical">Эллиптическая</MenuItem>
                            </Select>
                          </FormControl>
                        ) : (
                          <FormControl size="small" sx={{ minWidth: 150 }}>
                            <InputLabel>Переход</InputLabel>
                            <Select
                              value={params.supplyRightReducer || concentricReducers[0]?.id}
                              label="Переход"
                              onChange={(e) => updateParam('supplyRightReducer', e.target.value)}
                            >
                              {concentricReducers.map((r) => (
                                <MenuItem key={r.id} value={r.id}>{r.label}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      </>
                    )}
                  </>
                )}
              </Box>
              {params.hasSupplyRightCap && (
                <Typography variant="caption" color="text.secondary">
                  {params.supplyRightCapElementType === 'reducer'
                    ? `${fittingsData[params.supplyRightReducer]?.displayName || 'Переход'}`
                    : params.supplyRightCapType === 'straight' 
                      ? params.shape === 'square'
                        ? `Заглушка прямая ${params.isCompact ? params.compactSize : params.supplyPipeProfile}`
                        : `Заглушка прямая ⌀${params.supplyPipeDiameter}`
                      : `Заглушка эллиптическая ⌀${params.supplyPipeDiameter}`
                  }
                </Typography>
              )}
            </Grid>
          </Grid>

          {/* Обратный коллектор - скрыт в компактном режиме */}
          {!params.isCompact && (
            <>
              <Divider sx={{ my: 3 }} />

              <Typography variant="subtitle2" color="primary" sx={{ mt: 2, mb: 1 }}>
                Обратный коллектор
              </Typography>
          <Grid container spacing={2}>
            {/* Левая сторона */}
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={params.hasReturnLeftCap}
                      onChange={(e) => updateParam('hasReturnLeftCap', e.target.checked)}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">Слева</Typography>}
                />
                {params.hasReturnLeftCap && (
                  <>
                    {/* Для квадратных коллекторов только прямые заглушки */}
                    {params.shape === 'square' ? (
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                        Прямая заглушка
                      </Typography>
                    ) : (
                      <>
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <InputLabel>Элемент</InputLabel>
                          <Select
                            value={params.returnLeftCapElementType || 'cap'}
                            label="Элемент"
                            onChange={(e) => updateParam('returnLeftCapElementType', e.target.value)}
                          >
                            <MenuItem value="cap">Заглушка</MenuItem>
                            <MenuItem value="reducer">Переход конц.</MenuItem>
                          </Select>
                        </FormControl>
                        {params.returnLeftCapElementType === 'cap' || !params.returnLeftCapElementType ? (
                          <FormControl size="small" sx={{ minWidth: 130 }}>
                            <InputLabel>Тип</InputLabel>
                            <Select
                              value={params.returnLeftCapType}
                              label="Тип"
                              onChange={(e) => updateParam('returnLeftCapType', e.target.value)}
                            >
                              {params.returnPipeDiameter <= 101.6 && (
                                <MenuItem value="straight">Прямая</MenuItem>
                              )}
                              <MenuItem value="elliptical">Эллиптическая</MenuItem>
                            </Select>
                          </FormControl>
                        ) : (
                          <FormControl size="small" sx={{ minWidth: 150 }}>
                            <InputLabel>Переход</InputLabel>
                            <Select
                              value={params.returnLeftReducer || concentricReducers[0]?.id}
                              label="Переход"
                              onChange={(e) => updateParam('returnLeftReducer', e.target.value)}
                            >
                              {concentricReducers.map((r) => (
                                <MenuItem key={r.id} value={r.id}>{r.label}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      </>
                    )}
                  </>
                )}
              </Box>
              {params.hasReturnLeftCap && (
                <Typography variant="caption" color="text.secondary">
                  {params.returnLeftCapElementType === 'reducer'
                    ? `${fittingsData[params.returnLeftReducer]?.displayName || 'Переход'}`
                    : params.returnLeftCapType === 'straight' 
                      ? params.shape === 'square'
                        ? `Заглушка прямая ${params.isCompact ? params.compactSize : params.returnPipeProfile}`
                        : `Заглушка прямая ⌀${params.returnPipeDiameter}`
                      : `Заглушка эллиптическая ⌀${params.returnPipeDiameter}`
                  }
                </Typography>
              )}
            </Grid>
            
            {/* Правая сторона */}
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={params.hasReturnRightCap}
                      onChange={(e) => updateParam('hasReturnRightCap', e.target.checked)}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">Справа</Typography>}
                />
                {params.hasReturnRightCap && (
                  <>
                    {/* Для квадратных коллекторов только прямые заглушки */}
                    {params.shape === 'square' ? (
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                        Прямая заглушка
                      </Typography>
                    ) : (
                      <>
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <InputLabel>Элемент</InputLabel>
                          <Select
                            value={params.returnRightCapElementType || 'cap'}
                            label="Элемент"
                            onChange={(e) => updateParam('returnRightCapElementType', e.target.value)}
                          >
                            <MenuItem value="cap">Заглушка</MenuItem>
                            <MenuItem value="reducer">Переход конц.</MenuItem>
                          </Select>
                        </FormControl>
                        {params.returnRightCapElementType === 'cap' || !params.returnRightCapElementType ? (
                          <FormControl size="small" sx={{ minWidth: 130 }}>
                            <InputLabel>Тип</InputLabel>
                            <Select
                              value={params.returnRightCapType}
                              label="Тип"
                              onChange={(e) => updateParam('returnRightCapType', e.target.value)}
                            >
                              {params.returnPipeDiameter <= 101.6 && (
                                <MenuItem value="straight">Прямая</MenuItem>
                              )}
                              <MenuItem value="elliptical">Эллиптическая</MenuItem>
                            </Select>
                          </FormControl>
                        ) : (
                          <FormControl size="small" sx={{ minWidth: 150 }}>
                            <InputLabel>Переход</InputLabel>
                            <Select
                              value={params.returnRightReducer || concentricReducers[0]?.id}
                              label="Переход"
                              onChange={(e) => updateParam('returnRightReducer', e.target.value)}
                            >
                              {concentricReducers.map((r) => (
                                <MenuItem key={r.id} value={r.id}>{r.label}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      </>
                    )}
                  </>
                )}
              </Box>
              {params.hasReturnRightCap && (
                <Typography variant="caption" color="text.secondary">
                  {params.returnRightCapElementType === 'reducer'
                    ? `${fittingsData[params.returnRightReducer]?.displayName || 'Переход'}`
                    : params.returnRightCapType === 'straight' 
                      ? params.shape === 'square'
                        ? `Заглушка прямая ${params.isCompact ? params.compactSize : params.returnPipeProfile}`
                        : `Заглушка прямая ⌀${params.returnPipeDiameter}`
                      : `Заглушка эллиптическая ⌀${params.returnPipeDiameter}`
                  }
                </Typography>
              )}
            </Grid>
          </Grid>
            </>
          )}
        </Paper>
      )}

      {/* Диалог несовместимости */}
      <Dialog open={incompatibilityDialog.open} onClose={() => setIncompatibilityDialog({ open: false, message: '' })}>
        <DialogTitle>Предупреждение о несовместимости</DialogTitle>
        <DialogContent>
          <DialogContentText>{incompatibilityDialog.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIncompatibilityDialog({ open: false, message: '' })}>
            Понятно
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Диалог предложения приравнять длины */}
      <Dialog open={equalizeDialog} onClose={() => setEqualizeDialog(false)}>
        <DialogTitle>Приравнять длины коллекторов?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Длины коллекторов не равны:<br/>
              • Подача: {supplyCalculatedLength.toFixed(2)} мм<br/>
              • Обратка: {returnCalculatedLength.toFixed(2)} мм<br/>
              <br/>
              Приравнять длины коллекторов по максимальной ({Math.max(supplyCalculatedLength, returnCalculatedLength).toFixed(2)} мм)?
            </DialogContentText>
          </DialogContent>
        <DialogActions>
          <Button onClick={handleGenerateWithoutEqualize} color="inherit">
            Нет, оставить как есть
          </Button>
          <Button onClick={handleGenerateWithEqualize} variant="contained" color="primary">
            Да, приравнять
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
    </Box>
  );
};

/**
 * Создает объект pipeSpec с правильным покрытием
 * @param {number} diameter - Диаметр трубы
 * @param {string} thickness - Толщина трубы
 * @param {string} preferredCoating - Предпочитаемое покрытие
 * @returns {object} - Объект pipeSpec
 */
const createPipeSpec = (diameter, thickness, preferredCoating) => {
  const actualCoating = getActualCoating(diameter, thickness, preferredCoating);
  return {
    shape: 'round',
    diameter: diameter,
    thickness: thickness,
    coating: actualCoating,
  };
};

/**
 * Функция генерации распределительного коллектора
 */
function generateDistributionCollector(
  params,
  supplyCircuits,
  returnCircuits,
  supplyCenterFittings,
  returnCenterFittings,
  supplyLength,
  returnLength,
  supplyAdditionalOutlets = [],
  returnAdditionalOutlets = [],
  totalSystemLength = 0,
  effectiveSupplyFirstCircuitOffset = null,
  effectiveReturnFirstCircuitOffset = null
) {
  const {
    shape,
    isCompact,
    compactSize,
    supplyPipeDiameter,
    returnPipeDiameter,
    supplyPipeProfile,
    returnPipeProfile,
    pipeSpacing,
    supplyFirstCircuitOffset,
    supplyLastCircuitOffset,
    returnFirstCircuitOffset,
    returnLastCircuitOffset,
    supplySpacingBetweenCircuits,
    returnSpacingBetweenCircuits,
    // Подключения
    hasSupplyLeftConnection,
    hasSupplyRightConnection,
    supplyLeftConnectionType,
    supplyLeftConnectionFitting,
    supplyLeftConnectionFittingLength,
    supplyLeftConnectionLength,
    supplyLeftConnectionPipeDiameter,
    supplyLeftConnectionPipeLength,
    supplyRightConnectionType,
    supplyRightConnectionFitting,
    supplyRightConnectionFittingLength,
    supplyRightConnectionLength,
    supplyRightConnectionPipeDiameter,
    supplyRightConnectionPipeLength,
    hasReturnLeftConnection,
    hasReturnRightConnection,
    returnLeftConnectionType,
    returnLeftConnectionFitting,
    returnLeftConnectionFittingLength,
    returnLeftConnectionLength,
    returnLeftConnectionPipeDiameter,
    returnLeftConnectionPipeLength,
    returnRightConnectionType,
    returnRightConnectionFitting,
    returnRightConnectionFittingLength,
    returnRightConnectionLength,
    returnRightConnectionPipeDiameter,
    returnRightConnectionPipeLength,
    // Заглушки подающего коллектора
    hasSupplyLeftCap,
    hasSupplyRightCap,
    supplyLeftCapType,
    supplyLeftCapElementType,
    supplyLeftReducer,
    supplyRightCapType,
    supplyRightCapElementType,
    supplyRightReducer,
    // Заглушки обратного коллектора
    hasReturnLeftCap,
    hasReturnRightCap,
    returnLeftCapType,
    returnLeftCapElementType,
    returnLeftReducer,
    returnRightCapType,
    returnRightCapElementType,
    returnRightReducer,
  } = params;

  // Используем эффективное значение отступа первого контура подачи (если передано)
  const actualSupplyFirstCircuitOffset = effectiveSupplyFirstCircuitOffset !== null 
    ? effectiveSupplyFirstCircuitOffset 
    : supplyFirstCircuitOffset;

  // Используем эффективное значение отступа первого контура обратки (если передано)
  const actualReturnFirstCircuitOffset = effectiveReturnFirstCircuitOffset !== null 
    ? effectiveReturnFirstCircuitOffset 
    : returnFirstCircuitOffset;

  // Определяем покрытие для всей системы
  const allDiameters = [supplyPipeDiameter, returnPipeDiameter];
  
  // Импортируем функции для получения диаметров промежуточных труб
  const {
    extractDnFromFlangeKey,
    extractSizeFromNakGaikaKey,
    extractSizeFromShaibaNarRezba,
    getPipeDiameterForDn,
    getPipeDiameterForNakGaika,
    getPipeDiameterForShaibaNarRezba,
  } = require('./collectorUtils');
  
  // Функция для получения диаметра промежуточной трубы для фитинга
  const getPipeDiameterForFitting = (fittingId) => {
    if (!fittingId) return null;
    
    if (fittingId.startsWith('Flanec_')) {
      const dn = extractDnFromFlangeKey(fittingId);
      return getPipeDiameterForDn(dn);
    }
    
    if (fittingId.startsWith('Nak_Gaika_')) {
      const size = extractSizeFromNakGaikaKey(fittingId);
      return getPipeDiameterForNakGaika(size);
    }
    
    if (fittingId.startsWith('Shaib_Nar_Rezba_')) {
      const size = extractSizeFromShaibaNarRezba(fittingId);
      return getPipeDiameterForShaibaNarRezba(size);
    }
    
    return null;
  };
  
  // Добавляем диаметры ТОЛЬКО промежуточных труб фланцев из контуров
  // (сами контуры - ниппели, отводы и т.д. - НЕ влияют на покрытие основной системы)
  supplyCircuits.forEach(circuit => {
    const fittingId = circuit.fittingId || circuit.fitting; // Поддержка обоих полей
    if (fittingId && fittingId.startsWith('Flanec_')) {
      const pipeDiameter = getPipeDiameterForFitting(fittingId);
      if (pipeDiameter) allDiameters.push(pipeDiameter);
    }
  });
  returnCircuits.forEach(circuit => {
    const fittingId = circuit.fittingId || circuit.fitting; // Поддержка обоих полей
    if (fittingId && fittingId.startsWith('Flanec_')) {
      const pipeDiameter = getPipeDiameterForFitting(fittingId);
      if (pipeDiameter) allDiameters.push(pipeDiameter);
    }
  });
  
  // Добавляем диаметры из торцевых подключений
  if (hasSupplyLeftConnection && supplyLeftConnectionFitting) {
    const pipeDiameter = getPipeDiameterForFitting(supplyLeftConnectionFitting);
    if (pipeDiameter) allDiameters.push(pipeDiameter);
  }
  if (hasSupplyRightConnection && supplyRightConnectionFitting) {
    const pipeDiameter = getPipeDiameterForFitting(supplyRightConnectionFitting);
    if (pipeDiameter) allDiameters.push(pipeDiameter);
  }
  if (hasReturnLeftConnection && returnLeftConnectionFitting) {
    const pipeDiameter = getPipeDiameterForFitting(returnLeftConnectionFitting);
    if (pipeDiameter) allDiameters.push(pipeDiameter);
  }
  if (hasReturnRightConnection && returnRightConnectionFitting) {
    const pipeDiameter = getPipeDiameterForFitting(returnRightConnectionFitting);
    if (pipeDiameter) allDiameters.push(pipeDiameter);
  }
  
  // Убираем дубликаты
  const uniqueDiameters = [...new Set(allDiameters)];
  
  // Проверяем наличие оцинкованных фланцев в системе
  const hasGalvanizedFlanges = () => {
    const checkCircuits = (circuits) => circuits.some(circuit => {
      const fittingId = circuit.fittingId || circuit.fitting; // Поддержка обоих полей
      return fittingId && fittingId.includes('_galv');
    });
    
    if (checkCircuits(supplyCircuits) || checkCircuits(returnCircuits)) return true;
    
    // Проверяем торцевые подключения
    if ((hasSupplyLeftConnection && supplyLeftConnectionFitting && supplyLeftConnectionFitting.includes('_galv')) ||
        (hasSupplyRightConnection && supplyRightConnectionFitting && supplyRightConnectionFitting.includes('_galv')) ||
        (hasReturnLeftConnection && returnLeftConnectionFitting && returnLeftConnectionFitting.includes('_galv')) ||
        (hasReturnRightConnection && returnRightConnectionFitting && returnRightConnectionFitting.includes('_galv'))) return true;
    
    return false;
  };
  
  // Определяем покрытие:
  // - Если квадратный профиль → всегда Матовый (профили только матовые)
  // - Если есть оцинкованные фланцы → всегда Матовый (для аннотации)
  // - Иначе: Зеркало если у всех труб есть, иначе Матовый
  const { determineCoating } = require('./pipeThicknessUtils');
  const hasGalvFlanges = hasGalvanizedFlanges();
  const coating = (shape === 'square') ? 'Матовый' : (hasGalvFlanges ? 'Матовый' : determineCoating(uniqueDiameters));

  const cylinders = [];
  const fittings = [];
  const welds = [];
  const dimensionLines = [];
  const balloons = [];

  const startX = 400;
  const startY = 400;
  
  // Определяем размеры коллекторов (диаметр для круглых, высота профиля для квадратных)
  // Для квадратных профилей используем второй размер (высоту): например, для 60x30 высота = 30
  const supplyPipeSize = shape === 'square' 
    ? parseFloat(supplyPipeProfile.split('x')[1] || supplyPipeProfile.split('x')[0]) 
    : supplyPipeDiameter;
  const returnPipeSize = shape === 'square' 
    ? parseFloat(returnPipeProfile.split('x')[1] || returnPipeProfile.split('x')[0]) 
    : returnPipeDiameter;
  
  // supplyY и returnY - это ВЕРХ коллекторов (как в этажном коллекторе)
  // pipeSpacing - расстояние между ЦЕНТРАМИ коллекторов
  const supplyY = startY;
  const returnY = startY + pipeSpacing - supplyPipeSize / 2 + returnPipeSize / 2;

  // === ЗАГЛУШКИ И ПЕРЕХОДЫ ===
  // Вычисляем длины заглушек для всех 4 сторон
  // В компактном режиме заглушки рисуются с профилем compactSize (60x60 / 80x80),
  // поэтому для корректного расчёта длины используем тот же профиль.
  // Это касается и листа подачи, и профиля обратки: оба торца закрыты одними и теми же
  // заглушками compactSize, и длина гребёнки каждого коллектора должна быть уменьшена
  // на их длину (физически заглушки обратки не рисуются отдельно, но реально существуют).
  const capProfileForCompact = (isCompact && compactSize) ? compactSize : null;

  const supplyLeftCapInfo = getCapLength({
    hasCap: hasSupplyLeftCap,
    capElementType: supplyLeftCapElementType,
    capType: supplyLeftCapType,
    reducerKey: supplyLeftReducer,
    diameter: supplyPipeDiameter,
    shape: shape || 'round',
    profile: capProfileForCompact || supplyPipeProfile,
  });
  
  const supplyRightCapInfo = getCapLength({
    hasCap: hasSupplyRightCap,
    capElementType: supplyRightCapElementType,
    capType: supplyRightCapType,
    reducerKey: supplyRightReducer,
    diameter: supplyPipeDiameter,
    shape: shape || 'round',
    profile: capProfileForCompact || supplyPipeProfile,
  });
  
  const returnLeftCapInfo = getCapLength({
    hasCap: hasReturnLeftCap,
    capElementType: returnLeftCapElementType,
    capType: returnLeftCapType,
    reducerKey: returnLeftReducer,
    diameter: returnPipeDiameter,
    shape: shape || 'round',
    profile: capProfileForCompact || returnPipeProfile,
  });
  
  const returnRightCapInfo = getCapLength({
    hasCap: hasReturnRightCap,
    capElementType: returnRightCapElementType,
    capType: returnRightCapType,
    reducerKey: returnRightReducer,
    diameter: returnPipeDiameter,
    shape: shape || 'round',
    profile: capProfileForCompact || returnPipeProfile,
  });

  // === ОСНОВНЫЕ КОЛЛЕКТОРЫ ===
  // Вычисляем длину борт-шайб для торцевых подключений (когда диаметры совпадают)
  // Борт-шайбы входят в общую длину, поэтому гребёнка короче на их длину
  
  let supplyLeftBortshaibaLength = 0;
  let supplyRightBortshaibaLength = 0;
  let returnLeftBortshaibaLength = 0;
  let returnRightBortshaibaLength = 0;
  
  // Подающий коллектор - левая борт-шайба
  if (hasSupplyLeftConnection && supplyLeftConnectionType === 'fitting') {
    const isFlange = supplyLeftConnectionFitting?.startsWith('Flanec_');
    const isFreeFlange = isFlange && !supplyLeftConnectionFitting?.endsWith('_welded');
    const isNakGaika = supplyLeftConnectionFitting?.startsWith('Nak_Gaika_');
    const isShaibaNarRezba = supplyLeftConnectionFitting?.startsWith('Shaib_Nar_Rezba_');
    
    if ((isFreeFlange || isNakGaika) && !hasSupplyLeftCap) {
      // Только если НЕТ перехода/заглушки — шайба/бортшайба входит в размер гребёнки
      const shaibaKey = getShaibaKeyForFitting(supplyLeftConnectionFitting);
      if (shaibaKey) {
        const shaibaInfo = fittingsData[shaibaKey];
        if (shaibaInfo) {
          supplyLeftBortshaibaLength = shaibaInfo.length;
        }
      }
    } else if (isShaibaNarRezba && !hasSupplyLeftCap) {
      // Для шайбы с наружной резьбой: сама шайба входит в размер гребёнки (если НЕТ перехода/заглушки)
      const shaibaInfo = fittingsData[supplyLeftConnectionFitting];
      if (shaibaInfo) {
        supplyLeftBortshaibaLength = shaibaInfo.length;
      }
    }
  }
  
  // Подающий коллектор - правая борт-шайба
  if (hasSupplyRightConnection && supplyRightConnectionType === 'fitting') {
    const isFlange = supplyRightConnectionFitting?.startsWith('Flanec_');
    const isFreeFlange = isFlange && !supplyRightConnectionFitting?.endsWith('_welded');
    const isNakGaika = supplyRightConnectionFitting?.startsWith('Nak_Gaika_');
    const isShaibaNarRezba = supplyRightConnectionFitting?.startsWith('Shaib_Nar_Rezba_');
    
    if ((isFreeFlange || isNakGaika) && !hasSupplyRightCap) {
      // Только если НЕТ перехода/заглушки — шайба/бортшайба входит в размер гребёнки
      const shaibaKey = getShaibaKeyForFitting(supplyRightConnectionFitting);
      if (shaibaKey) {
        const shaibaInfo = fittingsData[shaibaKey];
        if (shaibaInfo) {
          supplyRightBortshaibaLength = shaibaInfo.length;
        }
      }
    } else if (isShaibaNarRezba && !hasSupplyRightCap) {
      // Для шайбы с наружной резьбой: сама шайба входит в размер гребёнки (если НЕТ перехода/заглушки)
      const shaibaInfo = fittingsData[supplyRightConnectionFitting];
      if (shaibaInfo) {
        supplyRightBortshaibaLength = shaibaInfo.length;
      }
    }
  }
  
  // Обратный коллектор - левая борт-шайба
  if (hasReturnLeftConnection && returnLeftConnectionType === 'fitting') {
    const isFlange = returnLeftConnectionFitting?.startsWith('Flanec_');
    const isFreeFlange = isFlange && !returnLeftConnectionFitting?.endsWith('_welded');
    const isNakGaika = returnLeftConnectionFitting?.startsWith('Nak_Gaika_');
    const isShaibaNarRezba = returnLeftConnectionFitting?.startsWith('Shaib_Nar_Rezba_');
    
    if ((isFreeFlange || isNakGaika) && !hasReturnLeftCap) {
      // Только если НЕТ перехода/заглушки — шайба/бортшайба входит в размер гребёнки
      const shaibaKey = getShaibaKeyForFitting(returnLeftConnectionFitting);
      if (shaibaKey) {
        const shaibaInfo = fittingsData[shaibaKey];
        if (shaibaInfo) {
          returnLeftBortshaibaLength = shaibaInfo.length;
        }
      }
    } else if (isShaibaNarRezba && !hasReturnLeftCap) {
      // Для шайбы с наружной резьбой: сама шайба входит в размер гребёнки (если НЕТ перехода/заглушки)
      const shaibaInfo = fittingsData[returnLeftConnectionFitting];
      if (shaibaInfo) {
        returnLeftBortshaibaLength = shaibaInfo.length;
      }
    }
  }
  
  // Обратный коллектор - правая борт-шайба
  if (hasReturnRightConnection && returnRightConnectionType === 'fitting') {
    const isFlange = returnRightConnectionFitting?.startsWith('Flanec_');
    const isFreeFlange = isFlange && !returnRightConnectionFitting?.endsWith('_welded');
    const isNakGaika = returnRightConnectionFitting?.startsWith('Nak_Gaika_');
    const isShaibaNarRezba = returnRightConnectionFitting?.startsWith('Shaib_Nar_Rezba_');
    
    if ((isFreeFlange || isNakGaika) && !hasReturnRightCap) {
      // Только если НЕТ перехода/заглушки — шайба/бортшайба входит в размер гребёнки
      const shaibaKey = getShaibaKeyForFitting(returnRightConnectionFitting);
      if (shaibaKey) {
        const shaibaInfo = fittingsData[shaibaKey];
        if (shaibaInfo) {
          returnRightBortshaibaLength = shaibaInfo.length;
        }
      }
    } else if (isShaibaNarRezba && !hasReturnRightCap) {
      // Для шайбы с наружной резьбой: сама шайба входит в размер гребёнки (если НЕТ перехода/заглушки)
      const shaibaInfo = fittingsData[returnRightConnectionFitting];
      if (shaibaInfo) {
        returnRightBortshaibaLength = shaibaInfo.length;
      }
    }
  }
  
  // Общая длина = расчётная длина (фиксированная)
  // Гребёнка короче на длину заглушек и бортшайб (заглушки и бортшайбы входят в общую длину)
  const actualSupplyPipeLength = supplyLength 
    - supplyLeftCapInfo.straightLength 
    - supplyRightCapInfo.straightLength
    - supplyLeftBortshaibaLength
    - supplyRightBortshaibaLength;
  
  const actualReturnPipeLength = returnLength 
    - returnLeftCapInfo.straightLength 
    - returnRightCapInfo.straightLength
    - returnLeftBortshaibaLength
    - returnRightBortshaibaLength;
  
  // Начало гребёнки смещено на длину левой заглушки и левой бортшайбы
  const supplyPipeStartX = startX + supplyLeftCapInfo.straightLength + supplyLeftBortshaibaLength;
  const returnPipeStartX = startX + returnLeftCapInfo.straightLength + returnLeftBortshaibaLength;
  
  const supplyPipeId = `cyl-supply-main-${Date.now()}`;
  
  // Переменные для хранения информации о торцевых подключениях (для размерных линий)
  let supplyLeftConnectionInfo = null;
  let supplyRightConnectionInfo = null;
  let returnLeftConnectionInfo = null;
  let returnRightConnectionInfo = null;
  
  // Создаем pipeSpec для подающего коллектора
  let supplyPipeSpec;
  if (shape === 'square') {
    const { DEFAULT_SQUARE_PIPE_COMBOS, DEFAULT_SQUARE_SHEET_COMBOS } = require('../pipeCatalog');
    
    // В компактном режиме подающий коллектор делается из листа
    if (isCompact) {
      const comboKey = `SQUARE_SHEET|${supplyPipeProfile}|`;
      const foundKey = Object.keys(DEFAULT_SQUARE_SHEET_COMBOS).find(key => key.startsWith(comboKey));
      if (foundKey) {
        const thickness = foundKey.split('|')[2];
        const coatingValue = foundKey.split('|')[3];
        supplyPipeSpec = {
          shape: 'square',
          profile: supplyPipeProfile,
          thickness: thickness,
          coating: coatingValue,
          squareType: 'sheet',
        };
      } else {
        supplyPipeSpec = {
          shape: 'square',
          profile: supplyPipeProfile,
          thickness: '2',
          coating: coating,
          squareType: 'sheet',
        };
      }
    } else {
      // В обычном режиме подающий коллектор делается из профиля
      const comboKey = `SQUARE_PROFILE|${supplyPipeProfile}|`;
      const foundKey = Object.keys(DEFAULT_SQUARE_PIPE_COMBOS).find(key => key.startsWith(comboKey));
      if (foundKey) {
        const thickness = foundKey.split('|')[2];
        const coatingValue = foundKey.split('|')[3];
        supplyPipeSpec = {
          shape: 'square',
          profile: supplyPipeProfile,
          thickness: thickness,
          coating: coatingValue,
          squareType: 'profile',
        };
      } else {
        supplyPipeSpec = {
          shape: 'square',
          profile: supplyPipeProfile,
          thickness: '3',
          coating: coating,
          squareType: 'profile',
        };
      }
    }
  } else {
    supplyPipeSpec = createPipeSpec(supplyPipeDiameter, getMinThickness(supplyPipeDiameter, coating), coating);
  }
  
  cylinders.push({
    id: supplyPipeId,
    x: supplyPipeStartX,
    y: supplyY,
    width: actualSupplyPipeLength,
    height: supplyPipeSize,
    pipeSpec: supplyPipeSpec,
  });

  // Создаем pipeSpec для обратного коллектора
  let returnPipeSpec;
  if (shape === 'square') {
    const { DEFAULT_SQUARE_PIPE_COMBOS } = require('../pipeCatalog');
    const comboKey = `SQUARE_PROFILE|${returnPipeProfile}|`;
    const foundKey = Object.keys(DEFAULT_SQUARE_PIPE_COMBOS).find(key => key.startsWith(comboKey));
    if (foundKey) {
      const thickness = foundKey.split('|')[2];
      const coatingValue = foundKey.split('|')[3];
      returnPipeSpec = {
        shape: 'square',
        profile: returnPipeProfile,
        thickness: thickness,
        coating: coatingValue,
        squareType: 'profile',
      };
    } else {
      returnPipeSpec = {
        shape: 'square',
        profile: returnPipeProfile,
        thickness: '3',
        coating: coating,
        squareType: 'profile',
      };
    }
  } else {
    returnPipeSpec = createPipeSpec(returnPipeDiameter, getMinThickness(returnPipeDiameter, coating), coating);
  }
  
  const returnPipeId = `cyl-return-main-${Date.now()}`;
  cylinders.push({
    id: returnPipeId,
    x: returnPipeStartX,
    y: returnY,
    width: actualReturnPipeLength,
    height: returnPipeSize,
    pipeSpec: returnPipeSpec,
  });

  // === ГЕНЕРАЦИЯ ЗАГЛУШЕК И ПЕРЕХОДОВ ===
  // Сохраняем ID заглушек для размерных линий
  let supplyLeftCapId = null;
  let supplyRightCapId = null;
  let returnLeftCapId = null;
  let returnRightCapId = null;
  
  // В компактном режиме заглушки размещаются по центру между листом и профилем
  // Вычисляем центральную Y-координату для заглушек в компактном режиме
  const compactCapY = isCompact 
    ? (supplyY + returnY + returnPipeSize) / 2 
    : null;
  
  // Подающий коллектор - левая заглушка/переход
  if (hasSupplyLeftCap) {
    const capX = supplyPipeStartX; // На левом краю гребёнки
    const capY = isCompact ? compactCapY : supplyY + supplyPipeSize / 2; // В компактном режиме - по центру, иначе - центр трубы
    
    if (supplyLeftCapElementType === 'reducer') {
      const reducer = generateReducer({
        reducerKey: supplyLeftReducer,
        x: capX,
        y: capY,
        rotation: -90,
        side: 'supply-left',
      });
      if (reducer) {
        fittings.push(reducer);
        supplyLeftCapId = reducer.id;
      }
    } else {
      // В компактном режиме используем общий размер для заглушки
      const capProfile = isCompact && compactSize 
        ? compactSize 
        : supplyPipeProfile;
      
      const cap = generateCap({
        capType: supplyLeftCapType,
        diameter: supplyPipeDiameter,
        shape: shape,
        profile: capProfile,
        x: capX,
        y: capY,
        rotation: -90,
        side: 'supply-left',
      });
      if (cap) {
        fittings.push(cap);
        supplyLeftCapId = cap.id;
      }
    }
  }
  
  // Подающий коллектор - правая заглушка/переход
  if (hasSupplyRightCap) {
    const capX = supplyPipeStartX + actualSupplyPipeLength; // На правом краю гребёнки
    const capY = isCompact ? compactCapY : supplyY + supplyPipeSize / 2; // В компактном режиме - по центру, иначе - центр трубы
    
    if (supplyRightCapElementType === 'reducer') {
      const reducer = generateReducer({
        reducerKey: supplyRightReducer,
        x: capX,
        y: capY,
        rotation: 90,
        side: 'supply-right',
      });
      if (reducer) {
        fittings.push(reducer);
        supplyRightCapId = reducer.id;
      }
    } else {
      // В компактном режиме используем общий размер для заглушки
      const capProfile = isCompact && compactSize 
        ? compactSize 
        : supplyPipeProfile;
      
      const cap = generateCap({
        capType: supplyRightCapType,
        diameter: supplyPipeDiameter,
        shape: shape,
        profile: capProfile,
        x: capX,
        y: capY,
        rotation: 90,
        side: 'supply-right',
      });
      if (cap) {
        fittings.push(cap);
        supplyRightCapId = cap.id;
      }
    }
  }
  
  // Обратный коллектор - левая заглушка/переход (не генерируем в компактном режиме)
  if (hasReturnLeftCap && !isCompact) {
    const capX = returnPipeStartX; // На левом краю гребёнки
    const capY = returnY + returnPipeSize / 2; // Центр трубы
    
    if (returnLeftCapElementType === 'reducer') {
      const reducer = generateReducer({
        reducerKey: returnLeftReducer,
        x: capX,
        y: capY,
        rotation: -90,
        side: 'return-left',
      });
      if (reducer) {
        fittings.push(reducer);
        returnLeftCapId = reducer.id;
      }
    } else {
      // В компактном режиме используем общий размер для заглушки
      const capProfile = isCompact && compactSize 
        ? compactSize 
        : returnPipeProfile;
      
      const cap = generateCap({
        capType: returnLeftCapType,
        diameter: returnPipeDiameter,
        shape: shape,
        profile: capProfile,
        x: capX,
        y: capY,
        rotation: -90,
        side: 'return-left',
      });
      if (cap) {
        fittings.push(cap);
        returnLeftCapId = cap.id;
      }
    }
  }
  
  // Обратный коллектор - правая заглушка/переход (не генерируем в компактном режиме)
  if (hasReturnRightCap && !isCompact) {
    const capX = returnPipeStartX + actualReturnPipeLength; // На правом краю гребёнки
    const capY = returnY + returnPipeSize / 2; // Центр трубы
    
    if (returnRightCapElementType === 'reducer') {
      const reducer = generateReducer({
        reducerKey: returnRightReducer,
        x: capX,
        y: capY,
        rotation: 90,
        side: 'return-right',
      });
      if (reducer) {
        fittings.push(reducer);
        returnRightCapId = reducer.id;
      }
    } else {
      // В компактном режиме используем общий размер для заглушки
      const capProfile = isCompact && compactSize 
        ? compactSize 
        : returnPipeProfile;
      
      const cap = generateCap({
        capType: returnRightCapType,
        diameter: returnPipeDiameter,
        shape: shape,
        profile: capProfile,
        x: capX,
        y: capY,
        rotation: 90,
        side: 'return-right',
      });
      if (cap) {
        fittings.push(cap);
        returnRightCapId = cap.id;
      }
    }
  }
  
  // В компактном режиме заглушки общие - используем ID подающего для обратного
  if (isCompact) {
    returnLeftCapId = supplyLeftCapId;
    returnRightCapId = supplyRightCapId;
  }

  // === КОНТУРЫ ===
  const circuitsResult = generatePairedCircuitsFull({
    supplyCircuits,
    returnCircuits,
    supplyStartX: startX, // Начало от внешнего края левой заглушки
    returnStartX: startX, // Начало от внешнего края левой заглушки
    supplyY,
    returnY,
    supplyPipeId,
    returnPipeId,
    supplyPipeDiameter: supplyPipeSize, // Используем размер (диаметр для круглых, размер профиля для квадратных)
    returnPipeDiameter: returnPipeSize, // Используем размер (диаметр для круглых, размер профиля для квадратных)
    supplyPipeThickness: supplyPipeSpec?.thickness, // Реальная толщина подающего коллектора
    returnPipeThickness: returnPipeSpec?.thickness, // Реальная толщина обратного коллектора
    supplyPipeProfile, // профиль для квадратных
    returnPipeProfile, // профиль для квадратных
    supplyPipeSpec, // спецификация для квадратных
    returnPipeSpec, // спецификация для квадратных
    supplyFirstCircuitOffset: actualSupplyFirstCircuitOffset,
    returnFirstCircuitOffset: actualReturnFirstCircuitOffset,
    supplySpacingBetweenCircuits,
    returnSpacingBetweenCircuits,
    pipeSpacing, // межосевое расстояние между коллекторами
    shape, // Передаем форму для правильных default/override значений
  });
  
  cylinders.push(...circuitsResult.cylinders);
  fittings.push(...circuitsResult.fittings);
  welds.push(...circuitsResult.welds);
  
  // Сохраняем информацию о контурах для размерных линий
  const supplyCircuitObjects = circuitsResult.supplyCircuitObjects || [];
  const returnCircuitObjects = circuitsResult.returnCircuitObjects || [];

  // Центры коллекторов для подключений (generateConnection ожидает центр)
  const supplyCenterY = supplyY + supplyPipeSize / 2;
  const returnCenterY = returnY + returnPipeSize / 2;
  
  // === ЦЕНТРАЛЬНЫЕ ФИТИНГИ ===
  
  // Подающий коллектор - центральные фитинги
  const supplyCenterFittingObjects = [];
  if (supplyCenterFittings && supplyCenterFittings.length > 0) {
    const supplyCenterResult = generateCenterFittings({
      centerFittings: supplyCenterFittings,
      mainPipeId: supplyPipeId,
      mainCenterY: supplyCenterY,
      startX,
      mainPipeStartX: supplyPipeStartX,
      mainPipeLength: supplyLength,
      circuits: supplyCircuits,  // Передаем исходные данные контуров, а не сгенерированные объекты
      bottomCircuits: returnCircuits,  // Передаем обратные контуры для возможности переключения
      circuitSpacing: supplySpacingBetweenCircuits,
      firstCircuitOffset: actualSupplyFirstCircuitOffset,
      bottomFirstCircuitOffset: actualReturnFirstCircuitOffset,
      bottomCircuitSpacing: returnSpacingBetweenCircuits,
      collectorType: 'supply',
      shape, // Передаем форму коллектора (round/square)
    });
    
    fittings.push(...supplyCenterResult.fittings);
    welds.push(...supplyCenterResult.welds);
    supplyCenterFittingObjects.push(...supplyCenterResult.centerFittingObjects);
  }
  
  // Обратный коллектор - центральные фитинги
  const returnCenterFittingObjects = [];
  if (returnCenterFittings && returnCenterFittings.length > 0) {
    const returnCenterResult = generateCenterFittings({
      centerFittings: returnCenterFittings,
      mainPipeId: returnPipeId,
      mainCenterY: returnCenterY,
      startX,
      mainPipeStartX: returnPipeStartX,
      mainPipeLength: returnLength,
      circuits: returnCircuits,  // Передаем исходные данные контуров, а не сгенерированные объекты
      bottomCircuits: supplyCircuits,  // Передаем подающие контуры для возможности переключения
      circuitSpacing: returnSpacingBetweenCircuits,
      firstCircuitOffset: actualReturnFirstCircuitOffset,
      bottomFirstCircuitOffset: actualSupplyFirstCircuitOffset,
      bottomCircuitSpacing: supplySpacingBetweenCircuits,
      collectorType: 'return',
      shape, // Передаем форму коллектора (round/square)
    });
    
    fittings.push(...returnCenterResult.fittings);
    welds.push(...returnCenterResult.welds);
    returnCenterFittingObjects.push(...returnCenterResult.centerFittingObjects);
  }

  // === ДОПОЛНИТЕЛЬНЫЕ ВЫХОДЫ (одиночные, как в этажном коллекторе) ===
  
  // Вспомогательная функция для вычисления X позиции выхода
  const calculateOutletX = (outlet, startX, mainPipeLength, circuits, circuitSpacing, firstCircuitOffset, alternateCircuits, alternateCircuitSpacing, alternateFirstCircuitOffset, collectorType = 'supply') => {
    const positionType = outlet.positionType || 'center';
    
    // Определяем, какие контуры использовать на основе referenceRow
    const referenceRow = outlet.referenceRow || 'supply';
    
    // Для подающего коллектора: circuits=подача, alternateCircuits=обратка
    // Для обратного коллектора: circuits=обратка, alternateCircuits=подача
    const useAlternate = (collectorType === 'supply' && referenceRow === 'return') || 
                         (collectorType === 'return' && referenceRow === 'supply');
    
    const selectedCircuits = (useAlternate && alternateCircuits) ? alternateCircuits : circuits;
    const selectedSpacing = (useAlternate && alternateCircuitSpacing !== undefined) ? alternateCircuitSpacing : circuitSpacing;
    const selectedFirstOffset = (useAlternate && alternateFirstCircuitOffset !== undefined) ? alternateFirstCircuitOffset : firstCircuitOffset;
    
    switch (positionType) {
      case 'center': {
        // По середине гребенки
        return startX + mainPipeLength / 2;
      }
      
      case 'betweenCircuits': {
        // Между контурами
        const betweenIndex = Number(outlet.betweenIndex) || 0;
        const circuit1Index = betweenIndex;
        const circuit2Index = betweenIndex + 1;
        
        if (circuit1Index < selectedCircuits.length && circuit2Index < selectedCircuits.length) {
          // Вычисляем позиции контуров (как в betweenFittings)
          const circuitPositions = [];
          let currentX = selectedFirstOffset;
          selectedCircuits.forEach((circuit, i) => {
            const internalSpacing = Number(circuit.circuitSpacing) || 50;
            circuitPositions.push({
              start: startX + currentX,
              spacing: internalSpacing
            });
            
            // Переход к следующему контуру
            if (i < selectedCircuits.length - 1) {
              const spacingToNext = Number(circuit.spacingToNext) || selectedSpacing;
              currentX += internalSpacing + spacingToNext;
            }
          });
          
          // Между контурами = между концом первого контура и началом второго контура
          const pos1End = circuitPositions[circuit1Index].start + circuitPositions[circuit1Index].spacing;  // конец первого контура
          const pos2Start = circuitPositions[circuit2Index].start;  // начало второго контура
          
          return (pos1End + pos2Start) / 2;
        }
        
        return startX + mainPipeLength / 2;
      }
      
      case 'betweenFittings': {
        // Между патрубками
        // betweenIndex - это индекс (0, 1, 2...), который обозначает:
        // 0 = между патрубком 1 и 2, 1 = между 2 и 3 и т.д.
        const betweenIndex = Number(outlet.betweenIndex) ?? 0;
        
        // Строим массив ВСЕХ позиций патрубков (как в схеме превью)
        // Сначала вычисляем позиции первых патрубков каждого контура
        const circuitPositions = [];
        let currentX = selectedFirstOffset;
        selectedCircuits.forEach((circuit, i) => {
          const internalSpacing = Number(circuit.circuitSpacing) || 50;
          circuitPositions.push({
            start: startX + currentX,
            spacing: internalSpacing
          });
          
          // Переход к следующему контуру
          if (i < selectedCircuits.length - 1) {
            const spacingToNext = Number(circuit.spacingToNext) || selectedSpacing;
            currentX += internalSpacing + spacingToNext;  // внутреннее межосевое + расстояние между контурами
          }
        });
        
        // Теперь строим массив всех патрубков
        const allFittingPositions = [];
        circuitPositions.forEach(pos => {
          allFittingPositions.push(pos.start);                  // первый патрубок контура
          allFittingPositions.push(pos.start + pos.spacing);    // второй патрубок контура
        });
        
        // Берем позиции двух патрубков по индексу
        if (betweenIndex < allFittingPositions.length - 1) {
          const x1 = allFittingPositions[betweenIndex];
          const x2 = allFittingPositions[betweenIndex + 1];
          return (x1 + x2) / 2;
        }
        
        return startX + mainPipeLength / 2;
      }
      
      case 'distance': {
        // На расстоянии от края
        const distance = Number(outlet.distanceFromEdge) || 60;
        const direction = outlet.distanceDirection || 'left';
        
        if (direction === 'right') {
          return startX + mainPipeLength - distance;
        } else {
          return startX + distance;
        }
      }
      
      default:
        return startX + mainPipeLength / 2;
    }
  };
  
  // Подающий коллектор - дополнительные выходы
  const supplyAdditionalOutletObjects = [];
  if (supplyAdditionalOutlets && supplyAdditionalOutlets.length > 0) {
    // Каждый выход генерируется отдельно с учетом своего направления
    supplyAdditionalOutlets.forEach((outlet) => {
      const outletDirection = outlet.direction || 'up';
      const outletType = outlet.outletType || 'short'; // 'short' или 'through'
      
      // Вычисляем X позицию выхода на основе типа размещения
      const outletX = calculateOutletX(
        outlet, 
        startX, 
        supplyLength, 
        supplyCircuits, 
        supplySpacingBetweenCircuits, 
        actualSupplyFirstCircuitOffset,
        returnCircuits,
        returnSpacingBetweenCircuits,
        returnFirstCircuitOffset,
        'supply'
      );
      
      const result = generateCircuits({
        circuits: [outlet],
        startX: outletX,
        startY: supplyY,
        mainPipeId: supplyPipeId,
        mainPipeDiameter: supplyPipeDiameter,
        mainPipeThickness: supplyPipeSpec?.thickness, // Реальная толщина коллектора
        mainPipeProfile: supplyPipeProfile, // передаем профиль для квадратных
        pipeSpec: supplyPipeSpec, // передаем спецификацию для квадратных
        firstCircuitOffset: 0, // Мы уже вычислили позицию
        circuitSpacing: 0,
        direction: outletDirection,
        shape, // Передаем форму для правильных default/override значений
        isAdditionalOutlet: true, // Флаг что это дополнительный выход
        outletType: outletType, // Тип выхода
        pipeSpacing: pipeSpacing, // Межосевое расстояние между коллекторами
      });
      
      cylinders.push(...result.cylinders);
      fittings.push(...result.fittings);
      welds.push(...result.welds);
      supplyAdditionalOutletObjects.push(...result.circuitObjects);
    });
  }
  
  // Обратный коллектор - дополнительные выходы
  const returnAdditionalOutletObjects = [];
  if (returnAdditionalOutlets && returnAdditionalOutlets.length > 0) {
    // Каждый выход генерируется отдельно с учетом своего направления
    returnAdditionalOutlets.forEach((outlet) => {
      const outletDirection = outlet.direction || 'down';
      const outletType = outlet.outletType || 'short'; // 'short' или 'through'
      
      // Вычисляем X позицию выхода на основе типа размещения
      const outletX = calculateOutletX(
        outlet, 
        startX, 
        returnLength, 
        returnCircuits, 
        returnSpacingBetweenCircuits, 
        actualReturnFirstCircuitOffset,
        supplyCircuits,
        supplySpacingBetweenCircuits,
        actualSupplyFirstCircuitOffset,
        'return'
      );
      
      const result = generateCircuits({
        circuits: [outlet],
        startX: outletX,
        startY: returnY,
        mainPipeId: returnPipeId,
        mainPipeDiameter: returnPipeDiameter,
        mainPipeThickness: returnPipeSpec?.thickness, // Реальная толщина коллектора
        mainPipeProfile: returnPipeProfile, // передаем профиль для квадратных
        pipeSpec: returnPipeSpec, // передаем спецификацию для квадратных
        firstCircuitOffset: 0, // Мы уже вычислили позицию
        circuitSpacing: 0,
        direction: outletDirection,
        shape, // Передаем форму для правильных default/override значений
        isAdditionalOutlet: true, // Флаг что это дополнительный выход
        outletType: outletType, // Тип выхода
        pipeSpacing: pipeSpacing, // Межосевое расстояние между коллекторами
      });
      
      cylinders.push(...result.cylinders);
      fittings.push(...result.fittings);
      welds.push(...result.welds);
      returnAdditionalOutletObjects.push(...result.circuitObjects);
    });
  }

  // === ПОДКЛЮЧЕНИЯ ===
  
  // Подающий коллектор - левое
  if (hasSupplyLeftConnection) {
    // Вычисляем точку подключения с учетом заглушки/перехода
    // Начинаем от края гребёнки (с учётом борт-шайбы, если она есть)
    let connectionStartX = supplyPipeStartX;
    if (hasSupplyLeftCap) {
      if (supplyLeftCapElementType === 'reducer' || supplyLeftCapType === 'elliptical') {
        // Переход или эллиптическая заглушка выступают наружу
        connectionStartX = startX - supplyLeftCapInfo.actualLength;
      }
      // Прямая заглушка входит в размер гребёнки, не смещаем
    }
    
    // Определяем какой параметр длины использовать
    const useConnectionLength = shouldUseConnectionLength(
      supplyLeftConnectionFitting,
      hasSupplyLeftCap,
      supplyPipeDiameter
    );
    
    // Применяем дефолты для квадратных профилей (как в этажном коллекторе)
    const isNakGaika = supplyLeftConnectionFitting?.startsWith('Nak_Gaika_');
    const isShaibaNarRezba = supplyLeftConnectionFitting?.startsWith('Shaib_Nar_Rezba_');
    const isNippel = supplyLeftConnectionFitting?.startsWith('Nippel_');
    
    // Для накидных гаек и шайб с резьбой: 58мм (квадратные) / 40мм (круглые)
    // Для муфт: используем значения из каталога (не применяем дефолт)
    let defaultConnectionLength;
    if (isNakGaika || isShaibaNarRezba) {
      defaultConnectionLength = shape === 'square' ? 58 : 40;
    } else if (isNippel && shape === 'square') {
      defaultConnectionLength = null;
    } else {
      defaultConnectionLength = null;
    }
    
    let effectiveFittingLength;
    if (useConnectionLength) {
      effectiveFittingLength = supplyLeftConnectionLength || 
        (defaultConnectionLength !== null ? defaultConnectionLength : 100);
    } else {
      effectiveFittingLength = supplyLeftConnectionFittingLength || defaultConnectionLength;
    }
    
    const result = generateConnectionWithCaps({
      connectionType: supplyLeftConnectionType,
      fittingId: supplyLeftConnectionFitting,
      pipeDiameter: supplyLeftConnectionPipeDiameter,
      pipeLength: supplyLeftConnectionPipeLength,
      fittingLength: effectiveFittingLength,
      startX: connectionStartX,
      y: supplyCenterY,
      direction: 'left',
      mainPipeId: supplyPipeId,
      prefix: 'supply-left',
      balloonYOffset: -supplyPipeSize / 2 - 30,
      // Параметры заглушек/переходов
      hasCap: hasSupplyLeftCap,
      capElementType: supplyLeftCapElementType,
      capType: supplyLeftCapType,
      capLength: supplyLeftCapInfo.actualLength,
      mainPipeDiameter: supplyPipeDiameter,
      shape,
    });
    
    cylinders.push(...result.cylinders);
    fittings.push(...result.fittings);
    welds.push(...result.welds);
    balloons.push(...result.balloons);
    
    // Сохраняем информацию о подключении для размерных линий
    // startX должен быть краем гребенки (не с учетом заглушки!) для правильной привязки размерных линий
    
    // Определяем, нужен ли переход/заглушка (учитываем несовпадение диаметров)
    let needsCapOrReducer = hasSupplyLeftCap;
    if (!needsCapOrReducer && supplyLeftConnectionFitting) {
      const pipeDiameterForFitting = getPipeDiameterForFitting(supplyLeftConnectionFitting);
      needsCapOrReducer = pipeDiameterForFitting && pipeDiameterForFitting !== supplyPipeDiameter;
    }
    
    supplyLeftConnectionInfo = {
      id: result.mainElementId,
      type: supplyLeftConnectionType === 'pipe' ? 'cylinder' : 'fitting',
      fittingKey: supplyLeftConnectionFitting,
      length: effectiveFittingLength || result.totalLength,
      diameter: supplyLeftConnectionType === 'pipe' ? supplyLeftConnectionPipeDiameter : fittingsData[supplyLeftConnectionFitting]?.diameter,
      startX: startX, // Край гребенки (левый край)
      intermediatePipeId: result.intermediatePipeId,
      bortshaibaId: result.bortshaibaId,
      // Для накидных гаек и шайб с резьбой: сохраняем ID заглушки (и прямой и эллиптической)
      // Для остальных фитингов: только для прямой заглушки
      capId: hasSupplyLeftCap && (
        (supplyLeftConnectionFitting?.startsWith('Nak_Gaika_') || supplyLeftConnectionFitting?.startsWith('Shaib_Nar_Rezba_')) ||
        supplyLeftCapType === 'straight'
      ) ? supplyLeftCapId : null,
      capType: supplyLeftCapType, // 'straight' или 'elliptical'
      capLength: supplyLeftCapInfo.straightLength, // Длина прямой заглушки
      // Флаг наличия заглушки/перехода (для фланцев) ИЛИ несовпадения диаметров
      needsCapOrReducer,
    };
  }

  // Подающий коллектор - правое
  if (hasSupplyRightConnection) {
    // Вычисляем точку подключения с учетом заглушки/перехода
    // Начинаем от правого края гребёнки (с учётом борт-шайбы, если она есть)
    let connectionStartX = supplyPipeStartX + actualSupplyPipeLength;
    if (hasSupplyRightCap) {
      if (supplyRightCapElementType === 'reducer' || supplyRightCapType === 'elliptical') {
        // Переход или эллиптическая заглушка выступают наружу
        connectionStartX = startX + supplyLength + supplyRightCapInfo.actualLength;
      }
      // Прямая заглушка входит в размер гребёнки, не смещаем
    }
    
    // Определяем правильный параметр длины для фитинга
    const useConnectionLengthRight = shouldUseConnectionLength(
      supplyRightConnectionFitting,
      hasSupplyRightCap,
      supplyPipeDiameter
    );
    
    // Применяем дефолты для квадратных профилей (как в этажном коллекторе)
    const isNakGaikaRight = supplyRightConnectionFitting?.startsWith('Nak_Gaika_');
    const isShaibaNarRezbaRight = supplyRightConnectionFitting?.startsWith('Shaib_Nar_Rezba_');
    const isNippelRight = supplyRightConnectionFitting?.startsWith('Nippel_');
    
    let defaultConnectionLengthRight;
    if (isNakGaikaRight || isShaibaNarRezbaRight) {
      defaultConnectionLengthRight = shape === 'square' ? 58 : 40;
    } else if (isNippelRight && shape === 'square') {
      defaultConnectionLengthRight = null;
    } else {
      defaultConnectionLengthRight = null;
    }
    
    let supplyRightFittingLengthToUse;
    if (useConnectionLengthRight) {
      supplyRightFittingLengthToUse = supplyRightConnectionLength || 
        (defaultConnectionLengthRight !== null ? defaultConnectionLengthRight : 100);
    } else {
      supplyRightFittingLengthToUse = supplyRightConnectionFittingLength || defaultConnectionLengthRight;
    }
    
    const result = generateConnectionWithCaps({
      connectionType: supplyRightConnectionType,
      fittingId: supplyRightConnectionFitting,
      pipeDiameter: supplyRightConnectionPipeDiameter,
      pipeLength: supplyRightConnectionPipeLength,
      fittingLength: supplyRightFittingLengthToUse,
      startX: connectionStartX,
      y: supplyCenterY,
      direction: 'right',
      mainPipeId: supplyPipeId,
      prefix: 'supply-right',
      balloonYOffset: -supplyPipeSize / 2 - 30,
      // Параметры заглушек/переходов
      hasCap: hasSupplyRightCap,
      capElementType: supplyRightCapElementType,
      capType: supplyRightCapType,
      capLength: supplyRightCapInfo.actualLength,
      mainPipeDiameter: supplyPipeDiameter,
      shape,
    });
    
    cylinders.push(...result.cylinders);
    fittings.push(...result.fittings);
    welds.push(...result.welds);
    balloons.push(...result.balloons);
    
    // Сохраняем информацию о подключении для размерных линий
    // startX должен быть краем гребенки (не с учетом заглушки!) для правильной привязки размерных линий
    
    // Определяем, нужен ли переход/заглушка (учитываем несовпадение диаметров)
    let needsCapOrReducer = hasSupplyRightCap;
    if (!needsCapOrReducer && supplyRightConnectionFitting) {
      const pipeDiameterForFitting = getPipeDiameterForFitting(supplyRightConnectionFitting);
      needsCapOrReducer = pipeDiameterForFitting && pipeDiameterForFitting !== supplyPipeDiameter;
    }
    
    supplyRightConnectionInfo = {
      id: result.mainElementId,
      type: supplyRightConnectionType === 'pipe' ? 'cylinder' : 'fitting',
      fittingKey: supplyRightConnectionFitting,
      length: supplyRightFittingLengthToUse || result.totalLength,
      diameter: supplyRightConnectionType === 'pipe' ? supplyRightConnectionPipeDiameter : fittingsData[supplyRightConnectionFitting]?.diameter,
      startX: startX + supplyLength, // Край гребенки (правый край)
      intermediatePipeId: result.intermediatePipeId,
      bortshaibaId: result.bortshaibaId,
      // Для накидных гаек и шайб с резьбой: сохраняем ID заглушки (и прямой и эллиптической)
      // Для остальных фитингов: только для прямой заглушки
      capId: hasSupplyRightCap && (
        (supplyRightConnectionFitting?.startsWith('Nak_Gaika_') || supplyRightConnectionFitting?.startsWith('Shaib_Nar_Rezba_')) ||
        supplyRightCapType === 'straight'
      ) ? supplyRightCapId : null,
      capType: supplyRightCapType, // 'straight' или 'elliptical'
      capLength: supplyRightCapInfo.straightLength, // Длина прямой заглушки
      // Флаг наличия заглушки/перехода (для фланцев) ИЛИ несовпадения диаметров
      needsCapOrReducer,
    };
  }

  // Обратный коллектор - левое
  if (hasReturnLeftConnection) {
    // Вычисляем точку подключения с учетом заглушки/перехода
    // Начинаем от края гребёнки (с учётом борт-шайбы, если она есть)
    let connectionStartX = returnPipeStartX;
    if (hasReturnLeftCap) {
      if (returnLeftCapElementType === 'reducer' || returnLeftCapType === 'elliptical') {
        // Переход или эллиптическая заглушка выступают наружу
        connectionStartX = startX - returnLeftCapInfo.actualLength;
      }
      // Прямая заглушка входит в размер гребёнки, не смещаем
    }
    
    // Определяем правильный параметр длины для фитинга
    const useConnectionLengthReturnLeft = shouldUseConnectionLength(
      returnLeftConnectionFitting,
      hasReturnLeftCap,
      returnPipeDiameter
    );
    
    // Применяем дефолты для квадратных профилей (как в этажном коллекторе)
    const isNakGaikaReturnLeft = returnLeftConnectionFitting?.startsWith('Nak_Gaika_');
    const isShaibaNarRezbaReturnLeft = returnLeftConnectionFitting?.startsWith('Shaib_Nar_Rezba_');
    const isNippelReturnLeft = returnLeftConnectionFitting?.startsWith('Nippel_');
    
    let defaultConnectionLengthReturnLeft;
    if (isNakGaikaReturnLeft || isShaibaNarRezbaReturnLeft) {
      defaultConnectionLengthReturnLeft = shape === 'square' ? 58 : 40;
    } else if (isNippelReturnLeft && shape === 'square') {
      defaultConnectionLengthReturnLeft = null;
    } else {
      defaultConnectionLengthReturnLeft = null;
    }
    
    let returnLeftFittingLengthToUse;
    if (useConnectionLengthReturnLeft) {
      returnLeftFittingLengthToUse = returnLeftConnectionLength || 
        (defaultConnectionLengthReturnLeft !== null ? defaultConnectionLengthReturnLeft : 100);
    } else {
      returnLeftFittingLengthToUse = returnLeftConnectionFittingLength || defaultConnectionLengthReturnLeft;
    }
    
    const result = generateConnectionWithCaps({
      connectionType: returnLeftConnectionType,
      fittingId: returnLeftConnectionFitting,
      pipeDiameter: returnLeftConnectionPipeDiameter,
      pipeLength: returnLeftConnectionPipeLength,
      fittingLength: returnLeftFittingLengthToUse,
      startX: connectionStartX,
      y: returnCenterY,
      direction: 'left',
      mainPipeId: returnPipeId,
      prefix: 'return-left',
      balloonYOffset: returnPipeSize / 2 + 30,
      // Параметры заглушек/переходов
      hasCap: hasReturnLeftCap,
      capElementType: returnLeftCapElementType,
      capType: returnLeftCapType,
      capLength: returnLeftCapInfo.actualLength,
      mainPipeDiameter: returnPipeDiameter,
      shape,
    });
    
    cylinders.push(...result.cylinders);
    fittings.push(...result.fittings);
    welds.push(...result.welds);
    balloons.push(...result.balloons);
    
    // Сохраняем информацию о подключении для размерных линий
    // startX должен быть краем гребенки (не с учетом заглушки!) для правильной привязки размерных линий
    
    // Определяем, нужен ли переход/заглушка (учитываем несовпадение диаметров)
    let needsCapOrReducer = hasReturnLeftCap;
    if (!needsCapOrReducer && returnLeftConnectionFitting) {
      const pipeDiameterForFitting = getPipeDiameterForFitting(returnLeftConnectionFitting);
      needsCapOrReducer = pipeDiameterForFitting && pipeDiameterForFitting !== returnPipeDiameter;
    }
    
    returnLeftConnectionInfo = {
      id: result.mainElementId,
      type: returnLeftConnectionType === 'pipe' ? 'cylinder' : 'fitting',
      fittingKey: returnLeftConnectionFitting,
      length: returnLeftFittingLengthToUse || result.totalLength,
      diameter: returnLeftConnectionType === 'pipe' ? returnLeftConnectionPipeDiameter : fittingsData[returnLeftConnectionFitting]?.diameter,
      startX: startX, // Край гребенки (левый край)
      intermediatePipeId: result.intermediatePipeId,
      bortshaibaId: result.bortshaibaId,
      // Для накидных гаек и шайб с резьбой: сохраняем ID заглушки (и прямой и эллиптической)
      // Для остальных фитингов: только для прямой заглушки
      capId: hasReturnLeftCap && (
        (returnLeftConnectionFitting?.startsWith('Nak_Gaika_') || returnLeftConnectionFitting?.startsWith('Shaib_Nar_Rezba_')) ||
        returnLeftCapType === 'straight'
      ) ? returnLeftCapId : null,
      capType: returnLeftCapType, // 'straight' или 'elliptical'
      capLength: returnLeftCapInfo.straightLength, // Длина прямой заглушки
      // Флаг наличия заглушки/перехода (для фланцев) ИЛИ несовпадения диаметров
      needsCapOrReducer,
    };
  }

  // Обратный коллектор - правое
  if (hasReturnRightConnection) {
    // Вычисляем точку подключения с учетом заглушки/перехода
    // Начинаем от правого края гребёнки (с учётом борт-шайбы, если она есть)
    let connectionStartX = returnPipeStartX + actualReturnPipeLength;
    if (hasReturnRightCap) {
      if (returnRightCapElementType === 'reducer' || returnRightCapType === 'elliptical') {
        // Переход или эллиптическая заглушка выступают наружу
        connectionStartX = startX + returnLength + returnRightCapInfo.actualLength;
      }
      // Прямая заглушка входит в размер гребёнки, не смещаем
    }
    
    // Определяем правильный параметр длины для фитинга
    const useConnectionLengthReturnRight = shouldUseConnectionLength(
      returnRightConnectionFitting,
      hasReturnRightCap,
      returnPipeDiameter
    );
    
    // Применяем дефолты для квадратных профилей (как в этажном коллекторе)
    const isNakGaikaReturnRight = returnRightConnectionFitting?.startsWith('Nak_Gaika_');
    const isShaibaNarRezbaReturnRight = returnRightConnectionFitting?.startsWith('Shaib_Nar_Rezba_');
    const isNippelReturnRight = returnRightConnectionFitting?.startsWith('Nippel_');
    
    let defaultConnectionLengthReturnRight;
    if (isNakGaikaReturnRight || isShaibaNarRezbaReturnRight) {
      defaultConnectionLengthReturnRight = shape === 'square' ? 58 : 40;
    } else if (isNippelReturnRight && shape === 'square') {
      defaultConnectionLengthReturnRight = null;
    } else {
      defaultConnectionLengthReturnRight = null;
    }
    
    let returnRightFittingLengthToUse;
    if (useConnectionLengthReturnRight) {
      returnRightFittingLengthToUse = returnRightConnectionLength || 
        (defaultConnectionLengthReturnRight !== null ? defaultConnectionLengthReturnRight : 100);
    } else {
      returnRightFittingLengthToUse = returnRightConnectionFittingLength || defaultConnectionLengthReturnRight;
    }
    
    const result = generateConnectionWithCaps({
      connectionType: returnRightConnectionType,
      fittingId: returnRightConnectionFitting,
      pipeDiameter: returnRightConnectionPipeDiameter,
      pipeLength: returnRightConnectionPipeLength,
      fittingLength: returnRightFittingLengthToUse,
      startX: connectionStartX,
      y: returnCenterY,
      direction: 'right',
      mainPipeId: returnPipeId,
      prefix: 'return-right',
      balloonYOffset: returnPipeSize / 2 + 30,
      // Параметры заглушек/переходов
      hasCap: hasReturnRightCap,
      capElementType: returnRightCapElementType,
      capType: returnRightCapType,
      capLength: returnRightCapInfo.actualLength,
      mainPipeDiameter: returnPipeDiameter,
      shape,
    });
    
    cylinders.push(...result.cylinders);
    fittings.push(...result.fittings);
    welds.push(...result.welds);
    balloons.push(...result.balloons);
    
    // Сохраняем информацию о подключении для размерных линий
    // startX должен быть краем гребенки (не с учетом заглушки!) для правильной привязки размерных линий
    
    // Определяем, нужен ли переход/заглушка (учитываем несовпадение диаметров)
    let needsCapOrReducer = hasReturnRightCap;
    if (!needsCapOrReducer && returnRightConnectionFitting) {
      const pipeDiameterForFitting = getPipeDiameterForFitting(returnRightConnectionFitting);
      needsCapOrReducer = pipeDiameterForFitting && pipeDiameterForFitting !== returnPipeDiameter;
    }
    
    returnRightConnectionInfo = {
      id: result.mainElementId,
      type: returnRightConnectionType === 'pipe' ? 'cylinder' : 'fitting',
      fittingKey: returnRightConnectionFitting,
      length: returnRightFittingLengthToUse || result.totalLength,
      diameter: returnRightConnectionType === 'pipe' ? returnRightConnectionPipeDiameter : fittingsData[returnRightConnectionFitting]?.diameter,
      startX: startX + returnLength, // Край гребенки (правый край)
      intermediatePipeId: result.intermediatePipeId,
      bortshaibaId: result.bortshaibaId,
      // Для накидных гаек и шайб с резьбой: сохраняем ID заглушки (и прямой и эллиптической)
      // Для остальных фитингов: только для прямой заглушки
      capId: hasReturnRightCap && (
        (returnRightConnectionFitting?.startsWith('Nak_Gaika_') || returnRightConnectionFitting?.startsWith('Shaib_Nar_Rezba_')) ||
        returnRightCapType === 'straight'
      ) ? returnRightCapId : null,
      capType: returnRightCapType, // 'straight' или 'elliptical'
      capLength: returnRightCapInfo.straightLength, // Длина прямой заглушки
      // Флаг наличия заглушки/перехода (для фланцев) ИЛИ несовпадения диаметров
      needsCapOrReducer,
    };
  }

  // === ЗАГЛУШКИ / ПЕРЕХОДЫ ===
  // TODO: Реализовать генерацию заглушек и переходов

  // === РАЗМЕРНЫЕ ЛИНИИ ===
  const generatedDimensions = generateDistributionCollectorDimensions({
    startX,
    supplyY,
    returnY,
    supplyPipeId,
    returnPipeId,
    supplyPipeDiameter,
    returnPipeDiameter,
    supplyPipeLength: supplyLength,
    returnPipeLength: returnLength,
    supplyCircuitObjects,
    returnCircuitObjects,
    supplyFirstCircuitOffset,
    supplyLastCircuitOffset,
    returnFirstCircuitOffset,
    returnLastCircuitOffset,
    supplyCircuits,
    returnCircuits,
    dimOffset: 40,
    // Информация о торцевых подключениях для размерных линий
    supplyLeftConnectionInfo,
    supplyRightConnectionInfo,
    returnLeftConnectionInfo,
    returnRightConnectionInfo,
    // Информация о заглушках
    supplyLeftCapId,
    supplyRightCapId,
    returnLeftCapId,
    returnRightCapId,
    // Длины прямых заглушек (для размерных линий)
    supplyLeftStraightCapLength: supplyLeftCapInfo.straightLength,
    supplyRightStraightCapLength: supplyRightCapInfo.straightLength,
    returnLeftStraightCapLength: returnLeftCapInfo.straightLength,
    returnRightStraightCapLength: returnRightCapInfo.straightLength,
    // Длины бортшайб (для размерных линий и отступов)
    supplyLeftBortshaibaLength,
    supplyRightBortshaibaLength,
    returnLeftBortshaibaLength,
    returnRightBortshaibaLength,
    // Фактические позиции гребёнок
    supplyPipeStartX,
    returnPipeStartX,
    actualSupplyPipeLength,
    actualReturnPipeLength,
    // Дополнительные выходы
    supplyAdditionalOutletObjects,
    returnAdditionalOutletObjects,
    supplyAdditionalOutlets,
    returnAdditionalOutlets,
    // Форма и профили (для квадратных коллекторов)
    shape,
    supplyPipeProfile,
    returnPipeProfile,
    supplyPipeSpec,
    returnPipeSpec,
    // Компактный режим
    isCompact,
    pipeSpacing,
  });
  
  dimensionLines.push(...generatedDimensions);

  // === ГЕНЕРАЦИЯ ПОЗИЦИЙ (BALLOONS) ===
  // Функция для получения текста позиции по ключу фитинга
  const getFittingLabel = (fittingKey) => {
    if (!fittingKey) return '';
    
    const sizeMap = {
      '1.4': '1/4"', '3.8': '3/8"', '1.2': '1/2"', '3.4': '3/4"', '1': '1"',
      '1_1.4': '1.1/4"', '1_1.2': '1.1/2"', '2': '2"', '2_1/2': '2.1/2"', '3': '3"', '4': '4"',
    };
    
    if (fittingKey.startsWith('Nippel_')) {
      const size = fittingKey.replace('Nippel_', '');
      return (sizeMap[size] || size) + 'НР';
    }
    if (fittingKey.startsWith('Mufta_')) {
      const size = fittingKey.replace('Mufta_', '');
      return (sizeMap[size] || size) + 'ВР';
    }
    if (fittingKey.startsWith('Perehod_Konc_')) {
      const dims = fittingKey.replace('Perehod_Konc_', '');
      return 'Переход\n' + dims;
    }
    if (fittingKey.startsWith('Flanec_')) {
      const fittingData = fittingsData[fittingKey];
      if (fittingData) {
        const displayName = fittingData.displayName;
        const match = displayName.match(/Фланец (Ду\d+)\s*\((.+)\)/);
        if (match) return 'Фланец ' + match[1] + '\n(' + match[2] + ')';
        return displayName;
      }
    }
    if (fittingKey.startsWith('Nak_Gaika_')) {
      const size = fittingKey.replace('Nak_Gaika_', '');
      return (sizeMap[size] || size) + ' Н.Г';
    }
    if (fittingKey.startsWith('Shaib_Nar_Rezba_')) {
      const size = fittingKey.replace('Shaib_Nar_Rezba_', '');
      return (sizeMap[size] || size) + ' Ш.Н.Р';
    }
    return fittingKey;
  };

  // Очищаем balloons от автоматически сгенерированных (из connectionLogic)
  balloons.length = 0;

  // Вычисляем уровни для размерных линий (для позиционирования balloons)
  // Учитываем как парные контуры, так и дополнительные выходы
  const allSupplyObjects = [...supplyCircuitObjects, ...supplyAdditionalOutletObjects];
  const allReturnObjects = [...returnCircuitObjects, ...returnAdditionalOutletObjects];
  
  const minSupplyTopY = allSupplyObjects.length > 0
    ? Math.min(...allSupplyObjects.map(c => c.y))
    : supplyY;
  const maxReturnBottomY = allReturnObjects.length > 0
    ? Math.max(...allReturnObjects.map(c => c.y + c.height))
    : returnY + returnPipeDiameter;

  const topDimY = minSupplyTopY - 40 * 2;
  const bottomDimY = maxReturnBottomY + 40 * 2;

  // Базовые Y позиции для позиций торцевых подключений
  const supplyBalloonY = supplyY - 30;
  const returnBalloonY = returnY + returnPipeDiameter + 30;
  const balloonYOffset = 50;

  // === ПОЗИЦИИ ДЛЯ КОНТУРОВ ===
  // Каждый контур состоит из двух патрубков (короткий и длинный)
  // supplyCircuitObjects содержит: [контур1-короткий, контур1-длинный, контур2-короткий, контур2-длинный, ...]
  // Позиции объединяют максимум 2 соседних ОДИНАКОВЫХ ФИТИНГА (не контура!)
  
  // Подающий коллектор - фитинги
  const processedSupplyIndices = new Set();
  
  supplyCircuitObjects.forEach((circuit, index) => {
    if (processedSupplyIndices.has(index)) return;
    
    const isFitting = circuit.type === 'fitting' || circuit.isFlange || circuit.isNakGaika || circuit.isShaibaNarRezba;
    if (!isFitting) return;
    
    // Определяем индекс контура и тип патрубка (короткий/длинный)
    const circuitIndex = Math.floor(index / 2);
    const isShortCircuit = circuit.isShort ?? (index % 2 === 0); // Используем метку isShort, которая учитывает swap
    const fittingKey = supplyCircuits[circuitIndex]?.fitting;
    const label = getFittingLabel(fittingKey);
    const targetId = circuit.flangeId || circuit.nakGaikaId || circuit.shaibaId || circuit.id;
    
    // Проверяем следующий фитинг (не контур!)
    const nextIndex = index + 1;
    const nextCircuit = supplyCircuitObjects[nextIndex];
    const nextCircuitIndex = Math.floor(nextIndex / 2);
    const nextFittingKey = supplyCircuits[nextCircuitIndex]?.fitting;
    const nextIsFitting = nextCircuit && (nextCircuit.type === 'fitting' || nextCircuit.isFlange || nextCircuit.isNakGaika || nextCircuit.isShaibaNarRezba);
    
    // Для длинных патрубков используем Y координату короткого патрубка того же контура
    // чтобы линия позиции шла от одного уровня
    let balloonYBase;
    if (isShortCircuit) {
      balloonYBase = circuit.y; // Короткий - используем свой Y
    } else {
      // Длинный - используем Y короткого патрубка того же контура
      const shortCircuit = supplyCircuitObjects.find((c, i) => Math.floor(i / 2) === circuitIndex && c.isShort);
      balloonYBase = shortCircuit ? shortCircuit.y : circuit.y;
    }
    const balloonY = (topDimY + balloonYBase) / 2;
    
    if (nextCircuit && nextIsFitting && nextFittingKey === fittingKey && !processedSupplyIndices.has(nextIndex)) {
      // Объединяем два соседних одинаковых фитинга
      const nextTargetId = nextCircuit.flangeId || nextCircuit.nakGaikaId || nextCircuit.shaibaId || nextCircuit.id;
      const midX = (circuit.centerX + nextCircuit.centerX) / 2;
      
      // Для длинных патрубков вычисляем смещение: (вылет / 2) / длина_длинного
      const nextIsShort = nextCircuit?.isShort ?? ((nextIndex % 2) === 0);
      
      // Получаем короткий патрубок для вычисления вылета
      const shortCircuit = supplyCircuitObjects.find((c, i) => Math.floor(i / 2) === circuitIndex && c.isShort);
      const shortLength = shortCircuit ? shortCircuit.height : 40;
      const anchorYOffset = shortLength / 2 / circuit.height; // смещение от верха
      
      const nextShortCircuit = supplyCircuitObjects.find((c, i) => Math.floor(i / 2) === nextCircuitIndex && c.isShort);
      const nextShortLength = nextShortCircuit ? nextShortCircuit.height : 40;
      const nextAnchorYOffset = nextCircuit ? (nextShortLength / 2 / nextCircuit.height) : 0;
      
      balloons.push({
        id: `balloon-supply-${index}-${Date.now()}`,
        x: midX,
        y: balloonY,
        text: label,
        textAlign: 'center',
        targets: [
          { 
            type: 'fitting', 
            id: targetId,
            // Для длинного: привязка со смещением (вылет/2) от верха
            ...(isShortCircuit ? {} : { anchorNormalized: { x: 0.5, y: anchorYOffset }, anchorMode: 'local' }),
          },
          { 
            type: 'fitting', 
            id: nextTargetId,
            ...(nextIsShort ? {} : { anchorNormalized: { x: 0.5, y: nextAnchorYOffset }, anchorMode: 'local' }),
          },
        ],
      });
      
      processedSupplyIndices.add(index);
      processedSupplyIndices.add(nextIndex);
    } else {
      // Одиночный фитинг
      let posX;
      const prevIndex = index - 1;
      const prevCircuit = supplyCircuitObjects[prevIndex];
      
      if (prevCircuit) {
        posX = (prevCircuit.centerX + circuit.centerX) / 2;
      } else {
        const internalSpacing = Number(supplyCircuits[circuitIndex]?.circuitSpacing) || 125;
        posX = circuit.centerX - internalSpacing / 2;
      }
      
      // Для длинных патрубков вычисляем смещение: (вылет / 2) / длина_длинного
      const shortCircuit = supplyCircuitObjects.find((c, i) => Math.floor(i / 2) === circuitIndex && c.isShort);
      const shortLength = shortCircuit ? shortCircuit.height : 40;
      const anchorYOffset = shortLength / 2 / circuit.height;
      
      balloons.push({
        id: `balloon-supply-${index}-${Date.now()}`,
        x: posX,
        y: balloonY,
        text: label,
        textAlign: 'center',
        targets: [{ 
          type: 'fitting', 
          id: targetId,
          // Для длинного: привязка со смещением (вылет/2) от верха
          ...(isShortCircuit ? {} : { anchorNormalized: { x: 0.5, y: anchorYOffset }, anchorMode: 'local' }),
        }],
      });
      
      processedSupplyIndices.add(index);
    }
  });

  // Обратный коллектор - фитинги
  const processedReturnIndices = new Set();
  
  returnCircuitObjects.forEach((circuit, index) => {
    if (processedReturnIndices.has(index)) return;
    
    const isFitting = circuit.type === 'fitting' || circuit.isFlange || circuit.isNakGaika || circuit.isShaibaNarRezba;
    if (!isFitting) return;
    
    // Определяем индекс контура и тип патрубка (короткий/длинный)
    // На обратном коллекторе порядок обратный: сначала длинный, потом короткий
    const circuitIndex = Math.floor(index / 2);
    const isShortCircuit = circuit.isShort ?? (index % 2 === 1); // Используем метку isShort, которая учитывает swap
    const fittingKey = returnCircuits[circuitIndex]?.fitting;
    const label = getFittingLabel(fittingKey);
    const targetId = circuit.flangeId || circuit.nakGaikaId || circuit.shaibaId || circuit.id;
    
    // Проверяем следующий фитинг (не контур!)
    const nextIndex = index + 1;
    const nextCircuit = returnCircuitObjects[nextIndex];
    const nextCircuitIndex = Math.floor(nextIndex / 2);
    const nextFittingKey = returnCircuits[nextCircuitIndex]?.fitting;
    const nextIsFitting = nextCircuit && (nextCircuit.type === 'fitting' || nextCircuit.isFlange || nextCircuit.isNakGaika || nextCircuit.isShaibaNarRezba);
    
    // Для длинных патрубков используем Y координату короткого патрубка того же контура
    // чтобы линия позиции шла от одного уровня
    // На обратном коллекторе: длинный = чётный индекс, короткий = нечётный индекс
    let balloonYBase;
    if (isShortCircuit) {
      balloonYBase = circuit.y + circuit.height; // Короткий - используем свой низ
    } else {
      // Длинный - используем низ короткого патрубка того же контура
      const shortCircuit = returnCircuitObjects.find((c, i) => Math.floor(i / 2) === circuitIndex && c.isShort);
      balloonYBase = shortCircuit ? (shortCircuit.y + shortCircuit.height) : (circuit.y + circuit.height);
    }
    const balloonY = (bottomDimY + balloonYBase) / 2;
    
    if (nextCircuit && nextIsFitting && nextFittingKey === fittingKey && !processedReturnIndices.has(nextIndex)) {
      // Объединяем два соседних одинаковых фитинга
      const nextTargetId = nextCircuit.flangeId || nextCircuit.nakGaikaId || nextCircuit.shaibaId || nextCircuit.id;
      const midX = (circuit.centerX + nextCircuit.centerX) / 2;
      
      // Для длинных патрубков вычисляем смещение: (вылет / 2) / длина_длинного
      const nextIsShort = nextCircuit?.isShort ?? ((nextIndex % 2) === 1);
      
      // Получаем короткий патрубок для вычисления вылета
      // Ищем короткий патрубок в том же контуре
      const shortCircuit = returnCircuitObjects.find((c, i) => Math.floor(i / 2) === circuitIndex && c.isShort);
      const shortLength = shortCircuit ? shortCircuit.height : 40;
      const anchorYOffset = shortLength / 2 / circuit.height; // смещение от верха (который визуально внизу)
      
      const nextShortCircuit = returnCircuitObjects.find((c, i) => Math.floor(i / 2) === nextCircuitIndex && c.isShort);
      const nextShortLength = nextShortCircuit ? nextShortCircuit.height : 40;
      const nextAnchorYOffset = nextCircuit ? (nextShortLength / 2 / nextCircuit.height) : 0;
      
      balloons.push({
        id: `balloon-return-${index}-${Date.now()}`,
        x: midX,
        y: balloonY,
        text: label,
        textAlign: 'center',
        targets: [
          { 
            type: 'fitting', 
            id: targetId,
            // Для длинного: привязка со смещением (вылет/2) от верха (визуально внизу)
            ...(isShortCircuit ? {} : { anchorNormalized: { x: 0.5, y: anchorYOffset }, anchorMode: 'local' }),
          },
          { 
            type: 'fitting', 
            id: nextTargetId,
            ...(nextIsShort ? {} : { anchorNormalized: { x: 0.5, y: nextAnchorYOffset }, anchorMode: 'local' }),
          },
        ],
      });
      
      processedReturnIndices.add(index);
      processedReturnIndices.add(nextIndex);
    } else {
      // Одиночный фитинг
      let posX;
      const prevIndex = index - 1;
      const prevCircuit = returnCircuitObjects[prevIndex];
      
      if (prevCircuit) {
        posX = (prevCircuit.centerX + circuit.centerX) / 2;
      } else {
        const internalSpacing = Number(returnCircuits[circuitIndex]?.circuitSpacing) || 125;
        posX = circuit.centerX - internalSpacing / 2;
      }
      
      // Для длинных патрубков вычисляем смещение: (вылет / 2) / длина_длинного
      const shortCircuit = returnCircuitObjects.find((c, i) => Math.floor(i / 2) === circuitIndex && c.isShort);
      const shortLength = shortCircuit ? shortCircuit.height : 40;
      const anchorYOffset = shortLength / 2 / circuit.height;
      
      balloons.push({
        id: `balloon-return-${index}-${Date.now()}`,
        x: posX,
        y: balloonY,
        text: label,
        textAlign: 'center',
        targets: [{ 
          type: 'fitting', 
          id: targetId,
          // Для длинного: привязка со смещением (вылет/2) от верха (визуально внизу)
          ...(isShortCircuit ? {} : { anchorNormalized: { x: 0.5, y: anchorYOffset }, anchorMode: 'local' }),
        }],
      });
      
      processedReturnIndices.add(index);
    }
  });

  // === ДОПОЛНИТЕЛЬНЫЕ ВЫХОДЫ ===
  // Balloons для дополнительных выходов подающего коллектора
  const processedSupplyOutletIndices = new Set();
  
  supplyAdditionalOutletObjects.forEach((outlet, index) => {
    if (processedSupplyOutletIndices.has(index)) return;
    
    const isFitting = outlet.type === 'fitting' || outlet.isFlange || outlet.isNakGaika || outlet.isShaibaNarRezba;
    if (!isFitting) return;
    
    const outletData = supplyAdditionalOutlets[index];
    const fittingKey = outletData?.fittingId;
    const label = getFittingLabel(fittingKey);
    const targetId = outlet.flangeId || outlet.nakGaikaId || outlet.shaibaId || outlet.id;
    
    // Определяем направление выхода
    const direction = outletData?.direction || 'up';
    
    // Определяем тип выхода (короткий/сквозной)
    const outletType = outletData?.outletType || 'short';
    const isThrough = outletType === 'through';
    
    // Позиция balloon выносится В СТОРОНУ направления выхода:
    // - Если выход вверх → balloon сверху от фитинга
    // - Если выход вниз → balloon снизу от фитинга (ближе к коллектору)
    let balloonY;
    if (direction === 'up') {
      // Выход вверх → balloon сверху от фитинга
      balloonY = outlet.y - 60; // На 60мм выше фитинга
    } else {
      // Выход вниз → balloon между коллектором и фитингом, ближе к фитингу
      balloonY = outlet.y + outlet.height * 0.7; // 70% от длины фитинга от верха
    }
    
    // Для сквозных выходов вычисляем смещение привязки (аналогично длинным фитингам контуров)
    let anchorYOffset = undefined;
    if (isThrough) {
      // Вычисляем базовую длину короткого выхода
      const isFlange = fittingKey?.startsWith('Flanec_');
      const isNakGaika = fittingKey?.startsWith('Nak_Gaika_');
      const isShaibaNarRezba = fittingKey?.startsWith('Shaib_Nar_Rezba_');
      const isSpecialFitting = isFlange || isNakGaika || isShaibaNarRezba;
      
      let baseLength = 80; // дефолт
      if (isSpecialFitting) {
        baseLength = visibleSpecialStubLength(fittingKey, outletData.flangePipeLength, shape);
      } else if (outletData.fittingLength !== null && outletData.fittingLength !== undefined) {
        baseLength = Number(outletData.fittingLength);
      } else if (fittingKey) {
        const fittingInfo = fittingsData[fittingKey];
        if (fittingInfo) {
          baseLength = fittingInfo.length;
        }
      } else if (outletData.length) {
        baseLength = Number(outletData.length);
      }
      
      // Смещение: (базовая_длина / 2) / длина_сквозного
      anchorYOffset = baseLength / 2 / outlet.height;
    }
    
    // Каждый дополнительный выход имеет свой баллон
    const posX = outlet.centerX + 40; // Смещение вправо на 40мм
    
    balloons.push({
      id: `balloon-supply-outlet-${index}-${Date.now()}`,
      x: posX,
      y: balloonY,
      text: label,
      textAlign: 'center',
      targets: [
        { 
          type: 'fitting', 
          id: targetId,
          ...(isThrough ? { anchorNormalized: { x: 0.5, y: anchorYOffset }, anchorMode: 'local' } : {}),
        },
      ],
    });
    
    processedSupplyOutletIndices.add(index);
  });

  // Balloons для дополнительных выходов обратного коллектора
  const processedReturnOutletIndices = new Set();
  
  returnAdditionalOutletObjects.forEach((outlet, index) => {
    if (processedReturnOutletIndices.has(index)) return;
    
    const isFitting = outlet.type === 'fitting' || outlet.isFlange || outlet.isNakGaika || outlet.isShaibaNarRezba;
    if (!isFitting) return;
    
    const outletData = returnAdditionalOutlets[index];
    const fittingKey = outletData?.fittingId;
    const label = getFittingLabel(fittingKey);
    const targetId = outlet.flangeId || outlet.nakGaikaId || outlet.shaibaId || outlet.id;
    
    // Определяем направление выхода
    const direction = outletData?.direction || 'down';
    
    // Определяем тип выхода (короткий/сквозной)
    const outletType = outletData?.outletType || 'short';
    const isThrough = outletType === 'through';
    
    // Позиция balloon выносится В СТОРОНУ направления выхода:
    // - Если выход вниз → balloon снизу от фитинга
    // - Если выход вверх → balloon сверху от фитинга (ближе к коллектору)
    let balloonY;
    if (direction === 'down') {
      // Выход вниз → balloon снизу от фитинга
      balloonY = outlet.y + outlet.height + 60; // На 60мм ниже фитинга
    } else {
      // Выход вверх → balloon между коллектором и фитингом, ближе к фитингу
      balloonY = outlet.y + outlet.height * 0.3; // 30% от длины фитинга от верха
    }
    
    // Для сквозных выходов вычисляем смещение привязки (аналогично длинным фитингам контуров)
    let anchorYOffset = undefined;
    if (isThrough) {
      // Вычисляем базовую длину короткого выхода
      const isFlange = fittingKey?.startsWith('Flanec_');
      const isNakGaika = fittingKey?.startsWith('Nak_Gaika_');
      const isShaibaNarRezba = fittingKey?.startsWith('Shaib_Nar_Rezba_');
      const isSpecialFitting = isFlange || isNakGaika || isShaibaNarRezba;
      
      let baseLength = 80; // дефолт
      if (isSpecialFitting) {
        baseLength = visibleSpecialStubLength(fittingKey, outletData.flangePipeLength, shape);
      } else if (outletData.fittingLength !== null && outletData.fittingLength !== undefined) {
        baseLength = Number(outletData.fittingLength);
      } else if (fittingKey) {
        const fittingInfo = fittingsData[fittingKey];
        if (fittingInfo) {
          baseLength = fittingInfo.length;
        }
      } else if (outletData.length) {
        baseLength = Number(outletData.length);
      }
      
      // Смещение: (базовая_длина / 2) / длина_сквозного
      anchorYOffset = baseLength / 2 / outlet.height;
    }
    
    // Каждый дополнительный выход имеет свой баллон
    const posX = outlet.centerX + 40; // Смещение вправо на 40мм
    
    balloons.push({
      id: `balloon-return-outlet-${index}-${Date.now()}`,
      x: posX,
      y: balloonY,
      text: label,
      textAlign: 'center',
      targets: [
        { 
          type: 'fitting', 
          id: targetId,
          ...(isThrough ? { anchorNormalized: { x: 0.5, y: anchorYOffset }, anchorMode: 'local' } : {}),
        },
      ],
    });
    
    processedReturnOutletIndices.add(index);
  });

  // === ПОДАЮЩИЙ КОЛЛЕКТОР ===
  // Левый переход/заглушка
  if (hasSupplyLeftCap && supplyLeftCapElementType === 'reducer' && supplyLeftCapId) {
    const label = getFittingLabel(supplyLeftReducer);
    const reducerEndX = startX - supplyLeftCapInfo.actualLength;
    const reducerY = supplyLeftConnectionInfo ? supplyBalloonY - balloonYOffset : supplyBalloonY;
    balloons.push({
      id: `balloon-supply-left-reducer-${Date.now()}`,
      x: reducerEndX - 25,
      y: reducerY,
      text: label,
      textAlign: 'center',
      targets: [{ type: 'fitting', id: supplyLeftCapId }],
    });
  }

  // Левый фитинг подающего
  if (supplyLeftConnectionInfo && supplyLeftConnectionInfo.type === 'fitting') {
    const label = getFittingLabel(supplyLeftConnectionInfo.fittingKey);
    const fittingEndX = supplyLeftConnectionInfo.startX - supplyLeftConnectionInfo.length;
    balloons.push({
      id: `balloon-supply-left-${Date.now()}`,
      x: fittingEndX - 60,
      y: supplyBalloonY,
      text: label,
      textAlign: 'center',
      targets: [{ type: 'fitting', id: supplyLeftConnectionInfo.id }],
    });
  }

  // Правый переход/заглушка
  if (hasSupplyRightCap && supplyRightCapElementType === 'reducer' && supplyRightCapId) {
    const label = getFittingLabel(supplyRightReducer);
    const reducerEndX = startX + supplyLength + supplyRightCapInfo.actualLength;
    const reducerY = supplyRightConnectionInfo ? supplyBalloonY - balloonYOffset : supplyBalloonY;
    balloons.push({
      id: `balloon-supply-right-reducer-${Date.now()}`,
      x: reducerEndX + 25,
      y: reducerY,
      text: label,
      textAlign: 'center',
      targets: [{ type: 'fitting', id: supplyRightCapId }],
    });
  }

  // Правый фитинг подающего
  if (supplyRightConnectionInfo && supplyRightConnectionInfo.type === 'fitting') {
    const label = getFittingLabel(supplyRightConnectionInfo.fittingKey);
    const fittingEndX = supplyRightConnectionInfo.startX + supplyRightConnectionInfo.length;
    balloons.push({
      id: `balloon-supply-right-${Date.now()}`,
      x: fittingEndX + 60,
      y: supplyBalloonY,
      text: label,
      textAlign: 'center',
      targets: [{ type: 'fitting', id: supplyRightConnectionInfo.id }],
    });
  }

  // === ОБРАТНЫЙ КОЛЛЕКТОР ===
  // Левый переход/заглушка
  if (hasReturnLeftCap && returnLeftCapElementType === 'reducer' && returnLeftCapId) {
    const label = getFittingLabel(returnLeftReducer);
    const reducerEndX = startX - returnLeftCapInfo.actualLength;
    const reducerY = returnLeftConnectionInfo ? returnBalloonY + balloonYOffset : returnBalloonY;
    balloons.push({
      id: `balloon-return-left-reducer-${Date.now()}`,
      x: reducerEndX - 25,
      y: reducerY,
      text: label,
      textAlign: 'center',
      targets: [{ type: 'fitting', id: returnLeftCapId }],
    });
  }

  // Левый фитинг обратного
  if (returnLeftConnectionInfo && returnLeftConnectionInfo.type === 'fitting') {
    const label = getFittingLabel(returnLeftConnectionInfo.fittingKey);
    const fittingEndX = returnLeftConnectionInfo.startX - returnLeftConnectionInfo.length;
    balloons.push({
      id: `balloon-return-left-${Date.now()}`,
      x: fittingEndX - 60,
      y: returnBalloonY,
      text: label,
      textAlign: 'center',
      targets: [{ type: 'fitting', id: returnLeftConnectionInfo.id }],
    });
  }

  // Правый переход/заглушка
  if (hasReturnRightCap && returnRightCapElementType === 'reducer' && returnRightCapId) {
    const label = getFittingLabel(returnRightReducer);
    const reducerEndX = startX + returnLength + returnRightCapInfo.actualLength;
    const reducerY = returnRightConnectionInfo ? returnBalloonY + balloonYOffset : returnBalloonY;
    balloons.push({
      id: `balloon-return-right-reducer-${Date.now()}`,
      x: reducerEndX + 25,
      y: reducerY,
      text: label,
      textAlign: 'center',
      targets: [{ type: 'fitting', id: returnRightCapId }],
    });
  }

  // Правый фитинг обратного
  if (returnRightConnectionInfo && returnRightConnectionInfo.type === 'fitting') {
    const label = getFittingLabel(returnRightConnectionInfo.fittingKey);
    const fittingEndX = returnRightConnectionInfo.startX + returnRightConnectionInfo.length;
    balloons.push({
      id: `balloon-return-right-${Date.now()}`,
      x: fittingEndX + 60,
      y: returnBalloonY,
      text: label,
      textAlign: 'center',
      targets: [{ type: 'fitting', id: returnRightConnectionInfo.id }],
    });
  }
  
  // === РАЗМЕРНЫЕ ЛИНИИ И ПОЗИЦИИ ДЛЯ ЦЕНТРАЛЬНЫХ ФИТИНГОВ ===
  
  // Подающий коллектор - размерные линии для центральных фитингов
  if (supplyCenterFittingObjects.length > 0) {
    const supplyCenterDims = generateCenterFittingDimensions(
      supplyCenterFittingObjects,
      supplyLeftCapId || supplyPipeId,
      supplyRightCapId || supplyPipeId,
      startX,
      supplyCenterY,
      supplyPipeDiameter,
      supplyLength,
      supplyLeftCapInfo.straightLength,
      supplyRightCapInfo.straightLength,
      supplyPipeId,
      actualSupplyPipeLength,
      supplyPipeStartX,
      supplyLeftConnectionInfo,
      supplyRightConnectionInfo,
      supplyLeftBortshaibaLength,
      supplyRightBortshaibaLength
    );
    
    // Размерные линии уже в правильном формате, просто добавляем их
    dimensionLines.push(...supplyCenterDims);
    
    // Balloons для центральных фитингов подающего коллектора
    const supplyCenterBalloons = generateCenterFittingBalloons(
      supplyCenterFittingObjects,
      getFittingLabel,
      supplyCenterY,
      0, // Без дополнительного смещения
      'supply-' // Уникальный префикс для подающего коллектора
    );
    balloons.push(...supplyCenterBalloons);
  }
  
  // Обратный коллектор - размерные линии для центральных фитингов
  if (returnCenterFittingObjects.length > 0) {
    const returnCenterDims = generateCenterFittingDimensions(
      returnCenterFittingObjects,
      returnLeftCapId || returnPipeId,
      returnRightCapId || returnPipeId,
      startX,
      returnCenterY,
      returnPipeDiameter,
      returnLength,
      returnLeftCapInfo.straightLength,
      returnRightCapInfo.straightLength,
      returnPipeId,
      actualReturnPipeLength,
      returnPipeStartX,
      returnLeftConnectionInfo,
      returnRightConnectionInfo,
      returnLeftBortshaibaLength,
      returnRightBortshaibaLength
    );
    
    // Размерные линии уже в правильном формате, просто добавляем их
    dimensionLines.push(...returnCenterDims);
    
    // Balloons для центральных фитингов обратного коллектора
    const returnCenterBalloons = generateCenterFittingBalloons(
      returnCenterFittingObjects,
      getFittingLabel,
      returnCenterY,
      0, // Без дополнительного смещения
      'return-' // Уникальный префикс для обратного коллектора
    );
    balloons.push(...returnCenterBalloons);
  }

  // Модифицируем существующие размерные линии для контуров с измененными значениями
  const fittingsWithOverride = fittings.filter(f => f.overrideEnabled && f.overrideText);
  const circuitObjectsWithOverride = [
    ...supplyCircuitObjects, 
    ...returnCircuitObjects,
    ...supplyAdditionalOutletObjects,
    ...returnAdditionalOutletObjects
  ].filter(obj => obj.overrideEnabled && obj.overrideText);
  
  fittingsWithOverride.forEach((fitting) => {
    dimensionLines.forEach((dimLine) => {
      if (!dimLine.elements || dimLine.elements.length === 0) return;
      
      // Проверяем что линия относится к этому фитингу
      const hasFitting = dimLine.elements.some(el => el.objectId === fitting.id);
      const hasBortshaiba = dimLine.elements.some(el => el.objectId === fitting.bortshaibaId);
      
      if (!hasFitting && !hasBortshaiba) return;
      
      // Определяем тип фитинга по rotation:
      // rotation = 0 или 180 -> контур (вертикальный фитинг)
      // rotation = -90 или 90 -> торцевое подключение (горизонтальный фитинг)
      const isCircuitFitting = fitting.rotation === 0 || fitting.rotation === 180;
      const isEndConnectionFitting = fitting.rotation === -90 || fitting.rotation === 90;
      
      // Для вертикальных линий (контуры): проверяем что значение совпадает с высотой фитинга
      if (!dimLine.isHorizontal) {
        if (isCircuitFitting && (dimLine.rawValue === fitting.height || dimLine.value === String(fitting.height))) {
          dimLine.overrideEnabled = true;
          dimLine.overrideText = fitting.overrideText;
        }
      }
      // Для горизонтальных линий (торцевые подключения и межосевые)
      else {
        if (isEndConnectionFitting) {
          // Для торцевых подключений: применяем override
          // Для накидных гаек/шайб: если линия привязана к бортшайбе ИЛИ к самому фитингу (для шайб)
          // Для муфт: если линия привязана к фитингу и значение в разумных пределах
          const isNakGaika = fitting.key?.startsWith('Nak_Gaika_');
          const isShaiba = fitting.key?.startsWith('Shaib_Nar_Rezba_');
          const isMufta = fitting.key?.startsWith('Mufta_');
          
          let shouldApplyOverride = false;
          if (isNakGaika && hasBortshaiba) {
            // Для накидных гаек: применяем если линия привязана к бортшайбе
            shouldApplyOverride = true;
          } else if (isShaiba && hasFitting) {
            // Для шайб с резьбой: применяем если линия привязана к самому фитингу (шайба = фитинг)
            shouldApplyOverride = true;
          } else if (isMufta && hasFitting) {
            // Для муфт: применяем только если размерная линия соответствует высоте фитинга
            shouldApplyOverride = dimLine.rawValue === fitting.height;
          }
          
          if (shouldApplyOverride) {
            dimLine.overrideEnabled = true;
            dimLine.overrideText = fitting.overrideText;
          }
        } else if (isCircuitFitting) {
          // Пропускаем горизонтальные линии для контуров (это межосевые расстояния)
        }
      }
    });
  });

  // Логика для circuitObjects (накидные гайки, шайбы с резьбой на контурах)
  circuitObjectsWithOverride.forEach((circuitObj) => {
    dimensionLines.forEach((dimLine) => {
      if (!dimLine.elements || dimLine.elements.length === 0) return;

      if (dimLine.isHorizontal) return; // Контуры - только вертикальные линии

      const hasCircuitObj = dimLine.elements.some(el =>
        el.objectId === circuitObj.id ||
        el.objectId === circuitObj.topObjectId ||
        el.objectId === circuitObj.nakGaikaId ||
        el.objectId === circuitObj.shaibaId
      );
      if (!hasCircuitObj) return;

      if (dimLine.rawValue === circuitObj.height || dimLine.value === String(circuitObj.height)) {
        dimLine.overrideEnabled = true;
        dimLine.overrideText = circuitObj.overrideText;
      }
    });
  });

  // IGES: Создаём "снимок" координат сразу после генерации
  // Этот снимок будет использоваться для экспорта вне зависимости от перемещений на чертеже
  const igesSnapshot = {
    cylinders: cylinders.map(cyl => ({
      id: cyl.id,
      x: cyl.x,
      y: cyl.y,
      width: cyl.width,
      height: cyl.height,
      rotation: cyl.rotation,
    })),
    fittings: fittings.map(fit => ({
      id: fit.id,
      groupX: fit.groupX,
      groupY: fit.groupY,
      diameter: fit.diameter,
      igesParentCylinderId: fit.igesParentCylinderId,
      igesCreateHole: fit.igesCreateHole,
      holeDiameter: fit.holeDiameter,
      igesHoleDirection: fit.igesHoleDirection,
      isLongCircuit: fit.isLongCircuit,
      pairedCylinderId: fit.pairedCylinderId,
    })),
  };

  return {
    cylinders,
    fittings,
    welds,
    annotations: [],
    dimensionLines,
    balloons,
    lines: [],
    objectConstraints: [],
    settings: { zoom: 1, textZoom: 1, positionTextZoom: 1, isLandscape: true },
    metadata: {
      totalLength: totalSystemLength,
      coating: coating, // Добавляем покрытие для аннотации
      // Параметры компактного режима для DXF экспорта
      isCompact: isCompact, // Флаг компактного режима
      compactSize: isCompact ? compactSize : null, // Размер (60x60 или 80x80)
      // IGES: Снимок координат для экспорта (не меняется при перемещении чертежа)
      igesSnapshot: igesSnapshot,
      // Параметры контуров для IGES экспорта
      supplyCircuitParams: {
        pipeId: supplyPipeId,
        pairedCylinderId: returnPipeId, // IGES: ID парной трубы для длинных патрубков
        startX,
        firstCircuitOffset: actualSupplyFirstCircuitOffset,
        spacingBetweenCircuits: supplySpacingBetweenCircuits,
        circuitPositions: [
          ...(circuitsResult.supplyCircuitPositions || []),
          // Добавляем позиции дополнительных выходов
          ...supplyAdditionalOutletObjects.map(obj => ({
            x: obj.centerX,
            direction: obj.direction || (obj.y < supplyY ? 'up' : 'down'),
          })),
        ],
      },
      returnCircuitParams: {
        pipeId: returnPipeId,
        pairedCylinderId: supplyPipeId, // IGES: ID парной трубы для длинных патрубков
        startX,
        firstCircuitOffset: actualReturnFirstCircuitOffset,
        spacingBetweenCircuits: returnSpacingBetweenCircuits,
        circuitPositions: [
          ...(circuitsResult.returnCircuitPositions || []),
          // Добавляем позиции дополнительных выходов
          ...returnAdditionalOutletObjects.map(obj => ({
            x: obj.centerX,
            direction: obj.direction || ((obj.y > returnY) ? 'down' : 'up'),
          })),
        ],
      },
    },
  };
}

export default DistributionCollectorEditor;
